import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import type { Permission } from "../../utils/permissions";
import {
  Box,
  Typography,
  Button,
  styled,
  IconButton,
  CircularProgress,
  Tab,
  Tabs,
  MenuItem,
  Select,
  SelectChangeEvent,
  Checkbox,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
} from "@mui/material";
import { TransformedSection } from "@services/facilitytype";
import CreateSpaceIcon from "@icons/CreateSpaceIcon";
import { useNavigate, useLocation } from "react-router-dom";
import OIGAdverseCheckForm from "./OIGAdverseCheckForm";
import FacilityAnalysis from "./FacilityAnalysis";
import Penalty from "./Penalty";
import RiskAnalysis from "./RiskAnalysis";
import PayorContractAnalysis from "./PayorContractAnalysis";
import ContinuousCompliance from "./ContinuousCompliance";
import AffableConnectedIcon from "./AffableConnectedIcon";
import {
  FacilityPickerOptionContent,
  FACILITY_HEADER_FIELD_MIN_HEIGHT_PX,
} from "./FacilityPickerOptionContent";
import { FacilityTypeValueStack } from "./FacilityTypeValueStack";
import { useAffableConnect } from "@hooks/useAffableConnect";
import {
  facilityComplianceService,
  FacilityComplianceData,
  FacilityCompliancePayload,
  FacilityCompliancePenaltiesResponse,
  createEmptyFacilityComplianceData,
  emptyPenaltiesAndTotalExposure,
  hasPenaltiesSectionLoaded,
  mergeComplianceStreamSection,
  mergePenaltiesIntoFacilityComplianceData,
  preservePenaltiesOnAnalysisUpdate,
  streamFacilityComplianceParallel,
  streamFacilityCompliancePenaltiesOnly,
} from "@services/facilityCompliance";
import {
  applyComplianceProgressEvent,
  buildInitialPenaltyExposureProgressSteps,
  buildPenaltyExposureProgressContext,
  markPenaltyExposureProgressBlockComplete,
  type PenaltyExposureProgressStep,
} from "./dashboard/penaltyExposureProgressSteps";
import {
  decideComplianceStreamFetch,
  shouldAbortComplianceStreamsOnEffectCleanup,
  shouldBeginPenaltiesFetch,
  shouldFetchPenaltiesAfterAnalysis,
  shouldFinalizePenaltiesFetch,
  shouldFetchFacilityPermissions,
  shouldResetComplianceAfterProfileSave,
  shouldResetPenaltyExposureProgress,
  shouldRetryEmptyPenaltiesResponse,
  shouldSeedPenaltyExposureProgressOnDashboard,
  shouldWaitForProfileBeforeCompliance,
  takePendingPenaltiesMerge,
  type PendingPenaltiesMergeEntry,
} from "./dashboard/dashboardComplianceStreamSession";
import { useFacilityLayoutData } from "@context";
import {
  getMyVerticalContext,
  getPreviewVertical,
  getRequestVerticalOverride,
  PREVIEW_VERTICAL_CHANGED_EVENT,
  syncPreviewVerticalAfterPurchase,
  type VerticalContextResponse,
} from "@services/verticals";
import {
  getProviderTypeOptions,
  type ProviderTypeOption,
} from "@services/facilitytype";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import {
  getSelectedCountryCode,
  userFacilityMatchesSelectedCountryCode,
} from "@utils/countryUtils";
import {
  normaliseAccreditingBodyForFacility,
  pickAccreditingBodyRawFromProfileSave,
} from "@utils/facilityProfileAccrediting";
import {
  buildGroupedFacilityOptions,
  formatFacilityDropdownLabel,
  getFacilityHierarchyId,
  getSelectedFacilityFromSession,
  notifySelectedFacilitySessionUpdated,
} from "@utils/facilityUtils";
import {
  clearPostPurchaseFacilitySelection,
  readPostPurchaseFacilitySelection,
  resolvePostPurchaseNavigationActiveTab,
} from "@utils/postPurchaseFacilitySelection";
import {
  buildPostPurchaseTargetFromNavigation,
  findFacilityForPostPurchaseTarget,
  hasPostPurchaseFacilityTarget,
  POST_PURCHASE_FACILITY_RETRY_MS,
  postPurchaseFacilityMatchesTarget,
  shouldClearSelectionForEmptyFacilities,
  shouldDeferSessionSelectedFacilityOnMount,
  shouldRetryPostPurchaseFacilityFetch,
  type PostPurchaseFacilityTarget,
} from "@utils/postPurchaseFacilityResolve";
import {
  preferredFacilityToSelectableRow,
  selectableRowFromPostPurchase,
} from "@utils/selfReportedPreferredFacility";
import {
  ensureSelfReportedFacilityEntitlement,
  shouldRepairFacilityDashboardAccess,
} from "@utils/selfReportedFacilityEntitlement";
import { readPreferredFacilityForPricing } from "@utils/pricingFacilityContext";
import {
  getAviationProfileSlug,
  hasDeterminedFacilityProfileForTabs,
  isAviationFacility,
} from "@utils/aviationFacilityProfile";
import { mockSurveyService } from "@services/mocksurvey";
import { fetchUserPermissions } from "@services/UserPermission";
import {
  canViewByName,
  canAddByName,
  canDeleteByName,
  canUpdateMockSurveyFromPermissions,
  canUpdateFeatureFromPermissions,
  FEATURE_NAMES,
  coercePermissionBool,
  normalizePermissionList,
} from "@utils/permissions";
import {
  canViewFacilityFeature,
  mergePermissionsByFeatureName,
} from "@utils/facilityPermissions";
import {
  getCurrentUserId,
  getCurrentUser,
  isGlobalAdministrator,
} from "@utils/userUtils";
import { auditLogService } from "@services/auditLog";
import { CloudUpload, Delete, Description, Save } from "@mui/icons-material";
import ReactPlayer from "react-player";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  affablebpmIntegrationService,
  ExternalGetRecordAndFilesDetailsResponse,
  ExternalGetFileByFileIdResponse,
} from "@services/affablebpmIntegration";
import { Alert } from "@mui/material";
import { DeleteFileIcon } from "@icons/DeleteFileIcon";
import FacilityProfileTab from "./FacilityProfileTab";
import FacilityDashboardTab, {
  type FacilityDashboardNavigateOptions,
  type FacilityDashboardNavigateTarget,
} from "./FacilityDashboardTab";
import {
  getFacilityDashboard,
  normalizeFacilityDashboardResponse,
  type FacilityIndividualDashboardResponse,
} from "@services/facilityDashboard";
import { facilityGroupBadgeLabel } from "@utils/networkFacilityRows";
import VideoRecordingIconRaw from "@assets/Video Recording.svg";
import { assetUrl } from "@utils/publicPath";
import {
  getTrainingVideoPlaybackUrl,
  getTrainingVideos,
  resolveTrainingVideoUrl,
  type TrainingVideo,
} from "@services/trainingVideos";
import { getApiOrigin } from "@utils/apiOrigin";
import {
  isRestrictedFacility,
  navigateToRestrictedFacility,
} from "@utils/facilityRestricted";
import {
  navigateToSelectPlanForUnpaidFacility,
  persistLeftoverUnpaidIdentity,
  resolveUnpaidAccessAction,
} from "@utils/leftoverUnpaidCheckout";
import { fetchAndMergeAdminPromoForLeftover } from "@utils/leftoverUnpaidPromo";

/** Root-absolute — PUBLIC_URL=. relative media URLs break on /dashboard/facilities. */
const VideoRecordingIcon = assetUrl(VideoRecordingIconRaw);

// ---------------------------------------------------------------------------
// Session-persisted set of facility IDs whose profile has been accepted.
// Storing in sessionStorage means the "already accepted" state survives
// component remounts (e.g. navigating away and back) within the same tab.
// ---------------------------------------------------------------------------
const PROFILE_ACCEPTED_STORAGE_KEY = "facilityProfileAcceptedIds";

function readProfileAcceptedIds(): Set<number> {
  try {
    const raw = sessionStorage.getItem(PROFILE_ACCEPTED_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as number[];
      if (Array.isArray(arr)) return new Set(arr);
    }
  } catch { /* ignore parse errors */ }
  return new Set<number>();
}

function persistProfileAcceptedId(id: number): Set<number> {
  const current = readProfileAcceptedIds();
  current.add(id);
  try {
    sessionStorage.setItem(PROFILE_ACCEPTED_STORAGE_KEY, JSON.stringify([...current]));
  } catch { /* ignore write errors */ }
  return current;
}

const normalizeFileName = (name: string) => (name || "").trim().toLowerCase();

// Static mapping of tab names to feature names (defined outside component for stable reference)
const tabToFeatureName: Record<string, string> = {
  dashboard: FEATURE_NAMES.DASHBOARD,
  facilities: FEATURE_NAMES.FACILITY_ANALYSIS,
  penalty: FEATURE_NAMES.POTENTIAL_PENALTIES,
  oig: FEATURE_NAMES.EXCLUSION_SANCTION_CHECK,
  compliance: FEATURE_NAMES.CONTINUOUS_COMPLIANCE,
  riskanalysis: FEATURE_NAMES.RISK_ANALYSIS,
  surveyorvisit: FEATURE_NAMES.MOCK_SURVEY,
};
const FACILITY_PROFILE_FEATURE_NAME = "Facility Profile";
const MOCK_SURVEY_RETURN_FLAG_KEY = "openMockSurveyOnReturn";
const TRAINING_VIDEO_API_BASE_URL = getApiOrigin();
const WATCH_GUIDE_CATEGORY_BY_TAB: Record<string, string> = {
  facilities: "Facility Analysis_Hide",
  surveyorvisit: "Mock Survey_Hide",
  penalty: "Potential Penalties_Hide",
  oig: "Exclusion Check_Hide",
  riskanalysis: "Risk Analysis_Hide",
  payorcontract: "Payor Contract Analysis_Hide",
  compliance: "Continuous Compliance_Hide",
};

// Helper function to format filename with USA date/time format: filename_MMDDYYYYHHMM.ext
function formatDownloadFileName(originalFileName: string): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");

  // Format: MMDDYYYYHHMM
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const year = now.getFullYear();
  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());
  const timestamp = `${month}${day}${year}${hours}${minutes}`;

  // Extract base name and extension
  const lastDotIndex = originalFileName.lastIndexOf(".");
  const baseName =
    lastDotIndex > 0
      ? originalFileName.substring(0, lastDotIndex)
      : originalFileName;
  const extension =
    lastDotIndex > 0 ? originalFileName.substring(lastDotIndex + 1) : "";

  return extension
    ? `${baseName}_${timestamp}.${extension}`
    : `${baseName}_${timestamp}`;
}

function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${month}-${day}-${year} ${hours}:${minutes}`;
  } catch (error) {
    return "";
  }
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"] as const;
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const precision = unitIndex === 0 ? 0 : 1;
  return `${size.toFixed(precision)} ${units[unitIndex]}`;
}

function isServerHostedTrainingVideo(value?: string | null): boolean {
  const resolved = resolveTrainingVideoUrl(value);
  if (!resolved || !TRAINING_VIDEO_API_BASE_URL) return false;

  try {
    return (
      new URL(resolved).origin === new URL(TRAINING_VIDEO_API_BASE_URL).origin
    );
  } catch {
    return false;
  }
}

const ContentContainer = styled(Box)({
  backgroundColor: "#fdffff",
  minHeight: "100%",
  height: "auto",
  flex: "1 0 auto",
  borderRadius: "12px",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
  position: "relative",
  overflow: "visible",
  // overflowY: "hidden",
  width: "100%",
  boxSizing: "border-box", // important to include borders in width
});
const TabContentContainer = styled(Box)({
  width: "100%",
  paddingTop: "8px",
  overflow: "visible",
});
const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: "8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "48px",
  [theme.breakpoints.down("sm")]: {
    padding: "4px",
    gap: "24px",
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "83%",
  height: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  alignItems: "stretch",
  [theme.breakpoints.down("sm")]: {
    padding: "0 4px",
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: "16px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  [theme.breakpoints.down("sm")]: {
    padding: "12px 8px",
    gap: "16px",
  },
}));

const HeaderContainer = styled(Box)({
  padding: "8px 0 0 0",
  borderBottom: "1px solid #35d7e4",
  width: "100%",
});

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "8px",
  marginTop: "0px",
});

const Title = styled(Typography)(({ theme }) => ({
  // fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "24px",
  lineHeight: "1.5",
  letterSpacing: "-0.4px",
  color: "#262626",
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}));

const DisclaimerText = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "1.5",
  color: "#737373",
  marginLeft: "8px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    marginLeft: "4px",
  },
}));

interface SpacesContentProps {
  selectedCategory?: string;
  onCategorySelect?: (selectedCategory: string) => void;
  onSectionClick?: (section: TransformedSection) => void;
}

// Loading component
const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "200px",
  width: "100%",
});

const AffableFileRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  borderBottom: "1px solid #eef6f8",
  padding: "8px 0",
  gap: "4px",
});

const AffableFileName = styled(Typography)({
  fontSize: "14px",
  fontWeight: 600,
  color: "#043c4a",
  fontFamily: "Rethink Sans, sans-serif",
});

const AffableFileMeta = styled(Typography)({
  fontSize: "13px",
  color: "#4f6f78",
  fontFamily: "Rethink Sans, sans-serif",
});

const FileDisplayContainer = styled(Box)({
  backgroundColor: "#ffffff",
  border: "1px solid #99d4e1",
  borderRadius: "8px",
  padding: "16px 16px",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  marginTop: "8px",
  flexWrap: "wrap",
});

const FileInfoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flex: 1,
  minWidth: 0,
});

const FileIcon = styled(Box)({
  backgroundColor: "#bdeff8",
  border: "none",
  borderRadius: "8px",
  padding: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
});

const FileDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
});

const FileName = styled(Typography)({
  fontSize: "14px",
  fontWeight: 600,
  color: "#262626",
  fontFamily: "Rethink Sans, sans-serif",
});

const FileSize = styled(Typography)({
  fontSize: "12px",
  color: "#737373",
  fontFamily: "Rethink Sans, sans-serif",
  whiteSpace: "nowrap",
});

const ReUploadButton = styled(Button)({
  backgroundColor: "#e6fafc",
  color: "#065b70",
  border: "1px solid #5bbfd6",
  borderRadius: "8px",
  padding: "8px 16px",
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 400,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: "Rethink Sans, sans-serif",
  "&:hover": {
    backgroundColor: "#d1f5f8",
  },
});

const ViewReportButton = styled(Button)({
  background: "linear-gradient(135deg, #0a829d 0%, #58c2d8 100%)",
  color: "#e6fafc",
  borderRadius: "8px",
  padding: "12px 24px",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.25)",
  "&:hover": {
    background: "linear-gradient(135deg, #096b82 0%, #4db0c5 100%)",
  },
});

const DeleteButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  minWidth: "40px",
  boxSizing: "border-box",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  color: "#262626",
  padding: 0,
  flexShrink: 0,
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
  },
  "&.Mui-disabled": {
    backgroundColor: "#ffffff",
    borderColor: "#e2e8f0",
    color: "#9ca3af",
  },
});

type Facility = MyFacilityListRow;

function readSelectedFacilityFromSession(): Facility | null {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    const stored = sessionStorage.getItem("filtered_selectedFacility");
    return stored ? (JSON.parse(stored) as Facility) : null;
  } catch {
    return null;
  }
}

export default function MyFacilitDetails({
  selectedCategory = "hospitals--inpatient",
  onCategorySelect,
  onSectionClick,
}: SpacesContentProps) {
  const { ensureMyFacilitiesLoaded, patchMyFacilityRow } = useFacilityLayoutData();

  // State management
  const [loading] = useState(false);
  const [activeTab, setActiveTab] = useState<
    | "dashboard"
    | "facilityProfile"
    | "facilities"
    | "oig"
    | "penalty"
    | "compliance"
    | "riskanalysis"
    | "surveyorvisit"
  >("dashboard");
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const [dashboardBffData, setDashboardBffData] =
    useState<FacilityIndividualDashboardResponse | null>(null);
  const [dashboardBffLoading, setDashboardBffLoading] = useState(false);
  const [dashboardBffError, setDashboardBffError] = useState<string | null>(null);
  const [complianceStreamInitSignal, setComplianceStreamInitSignal] = useState(0);
  const dashboardBffReadyRef = useRef(false);
  const dashboardBffInFlightRef = useRef<string | null>(null);
  const dashboardBffSoftRefreshInFlightRef = useRef<string | null>(null);
  const [complianceFocusSection, setComplianceFocusSection] = useState<
    "policy" | null
  >(null);
  const [riskFocusSection, setRiskFocusSection] = useState<
    "incident" | "survey" | null
  >(null);
  const [complianceData, setComplianceData] =
    useState<FacilityComplianceData | null>(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceError, setComplianceError] = useState<string | null>(null);
  /** Background `/parallel/penalties` after the faster 5-section Facility Analysis load. */
  const [penaltiesLoading, setPenaltiesLoading] = useState(false);
  const [penaltyExposureProgressSteps, setPenaltyExposureProgressSteps] = useState<
    PenaltyExposureProgressStep[]
  >([]);
  const complianceStreamAbortRef = useRef<AbortController | null>(null);
  const penaltiesStreamAbortRef = useRef<AbortController | null>(null);
  const lastFetchedFacilityIdRef = useRef<number | null>(null);
  const penaltiesFetchInFlightRef = useRef<number | null>(null);
  const penaltiesFetchGenerationRef = useRef(0);
  const emptyPenaltiesResponsesSeenRef = useRef(0);
  /** Bumped on profile save so in-flight parallel responses are ignored. */
  const complianceFetchGenerationRef = useRef(0);
  /** Prevents overlapping parallel fetches for the same facility (effect re-runs). */
  const complianceFetchInFlightRef = useRef<number | null>(null);
  const penaltyProgressSessionFacilityIdRef = useRef<number | null>(null);
  const pendingPenaltiesMergeRef = useRef<
    PendingPenaltiesMergeEntry<FacilityCompliancePenaltiesResponse> | null
  >(null);
  const selectedFacilityIdRef = useRef<number | null>(null);
  const permissionGateRef = useRef({
    loading: true,
    checkedFacilityId: null as number | null,
    hasAnyView: false,
  });
  const [complianceRefetchNonce, setComplianceRefetchNonce] = useState(0);
  const [facilityPermissions, setFacilityPermissions] = useState<any[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionCheckedFacilityId, setPermissionCheckedFacilityId] =
    useState<number | null>(null);
  const [permissionsResponseEmpty, setPermissionsResponseEmpty] =
    useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get facility from navigation state
  const navigationState = location.state as {
    facilityId?: number;
    facilityName?: string;
    facilityCity?: string;
    selectedFacility?: any;
    activeTab?: string;
    mockSurveyAccordionOpen?: boolean;
    refreshFacilities?: boolean;
    returnTo?: {
      currentView?: string;
      networkTab?: string;
    };
  } | null;

  const postPurchaseSelectionOnMount = readPostPurchaseFacilitySelection();

  const postPurchaseTargetOnMount = buildPostPurchaseTargetFromNavigation(
    navigationState,
    postPurchaseSelectionOnMount,
  );

  /** Post-checkout target: stable for retries until the new row appears in my-facilities. */
  const postPurchaseTargetRef = useRef<PostPurchaseFacilityTarget | null>(
    postPurchaseTargetOnMount,
  );
  const postPurchaseRetryAttemptRef = useRef(0);
  const postPurchaseRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [postPurchaseResolving, setPostPurchaseResolving] = useState(
    () => postPurchaseTargetRef.current != null,
  );

  // Capture the navigation facilityId once on mount (before any effects run).
  // Using a ref avoids re-render cycles and lets us consume it exactly once.
  const initialNavFacilityIdRef = useRef<number | undefined>(
    navigationState?.facilityId ?? postPurchaseSelectionOnMount?.facilityId,
  );

  // Fallback identifiers for facility matching when facilityId alone doesn't resolve.
  const initialNavFacilityNameRef = useRef<string | undefined>(
    navigationState?.facilityName ?? postPurchaseSelectionOnMount?.facilityName,
  );
  const initialNavFacilityCityRef = useRef<string | undefined>(
    navigationState?.facilityCity ?? postPurchaseSelectionOnMount?.facilityCity,
  );
  const initialNavSelectedFacilityRef = useRef<any>(
    navigationState?.selectedFacility ?? undefined,
  );

  // Capture the navigation-requested tab at mount. When the user clicks a
  // facility from the left panel (with activeTab: "facilities"), this ref
  // ensures the auto-tab logic uses the navigation intent. Cleared after first
  // consumption so subsequent in-page facility switches resume the normal logic.
  const navRequestedTabRef = useRef<string | undefined>(
    resolvePostPurchaseNavigationActiveTab(
      navigationState?.activeTab,
      postPurchaseSelectionOnMount,
    ),
  );
  const returnToMockSurveyRef = useRef<boolean>(
    navigationState?.activeTab === "surveyorvisit" ||
      navigationState?.mockSurveyAccordionOpen === true ||
      sessionStorage.getItem(MOCK_SURVEY_RETURN_FLAG_KEY) === "1",
  );

  const normalizeNavigationFacility = (facility: any): Facility | undefined => {
    if (!facility || typeof facility !== "object") return undefined;
    const facilityId = Number(facility.facility_id ?? facility.id);
    if (!Number.isFinite(facilityId)) return undefined;
    return { ...facility, facility_id: facilityId } as Facility;
  };

  // Handle activeTab from location state (e.g., from ContactUs redirect)
  useEffect(() => {
    if (navigationState?.activeTab) {
      const raw = navigationState.activeTab;
      const normalized =
        raw === "payorcontract" ? "riskanalysis" : raw;
      const tab = resolvePostPurchaseNavigationActiveTab(
        normalized,
        postPurchaseSelectionOnMount,
      );
      setActiveTab(
        tab as
          | "dashboard"
          | "facilityProfile"
          | "facilities"
          | "oig"
          | "penalty"
          | "compliance"
          | "riskanalysis"
          | "surveyorvisit",
      );
    }
  }, [navigationState?.activeTab, postPurchaseSelectionOnMount]);

  /** Payor Contracts merged into Risk Analysis; remap any stale tab id. */
  useEffect(() => {
    if ((activeTab as string) === "payorcontract") {
      setActiveTab("riskanalysis");
    }
  }, [activeTab]);

  // Clear transient navigation fields from browser history state on mount so
  // that pressing F5 (which preserves history.state) does not reuse stale
  // facility selection from a previous cross-page navigation.
  // All values were captured synchronously into refs above.
  useEffect(() => {
    if (
      initialNavFacilityIdRef.current != null ||
      initialNavFacilityNameRef.current != null
    ) {
      navigate(location.pathname, {
        replace: true,
        state: navigationState
          ? {
              ...navigationState,
              facilityId: undefined,
              facilityName: undefined,
              facilityCity: undefined,
            }
          : null,
      });
    }
    // Intentionally run only on mount – deps are stable refs / initial values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mock Survey main accordion (open when returning from survey viewer)
  const [mockSurveyAccordionExpanded, setMockSurveyAccordionExpanded] =
    useState<boolean>(true);
  const [watchGuideLoading, setWatchGuideLoading] = useState(false);
  const [watchGuideOpen, setWatchGuideOpen] = useState(false);
  const [watchGuideTitle, setWatchGuideTitle] = useState("Watch Guide");
  const [watchGuideVideo, setWatchGuideVideo] = useState<TrainingVideo | null>(
    null,
  );
  const [watchGuideVideoUrl, setWatchGuideVideoUrl] = useState("");
  const [watchGuideError, setWatchGuideError] = useState<string | null>(null);
  const [watchGuideUserStarted, setWatchGuideUserStarted] = useState(false);
  const [watchGuideAvailability, setWatchGuideAvailability] = useState<
    Record<string, boolean>
  >({});

  // Open mock survey accordion when returning from survey viewer page
  useEffect(() => {
    const shouldOpenFromStorage =
      sessionStorage.getItem(MOCK_SURVEY_RETURN_FLAG_KEY) === "1";
    const shouldOpenFromNavigation =
      navigationState?.mockSurveyAccordionOpen === true ||
      navigationState?.activeTab === "surveyorvisit";

    if (shouldOpenFromNavigation || shouldOpenFromStorage) {
      setActiveTab("surveyorvisit");
      setMockSurveyAccordionExpanded(true);
      if (shouldOpenFromStorage) {
        sessionStorage.removeItem(MOCK_SURVEY_RETURN_FLAG_KEY);
      }
    }
  }, [navigationState?.activeTab, navigationState?.mockSurveyAccordionOpen]);

  const resetWatchGuideDialogState = useCallback(() => {
    setWatchGuideError(null);
    setWatchGuideVideo(null);
    setWatchGuideUserStarted(false);
    if (watchGuideVideoUrl.startsWith("blob:")) {
      URL.revokeObjectURL(watchGuideVideoUrl);
    }
    setWatchGuideVideoUrl("");
  }, [watchGuideVideoUrl]);

  const closeWatchGuideDialog = useCallback(() => {
    setWatchGuideOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      if (watchGuideVideoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(watchGuideVideoUrl);
      }
    };
  }, [watchGuideVideoUrl]);

  // Depend only on the active tab's slot — not the whole map — so updates like
  // `payorcontract` do not re-run this effect while `activeTab` still has no value yet
  // (avoids a fetch storm when Risk + Payor guides resolve at different times).
  const watchGuideKnownForActiveTab = watchGuideAvailability[activeTab];
  useEffect(() => {
    const preferredCategory = WATCH_GUIDE_CATEGORY_BY_TAB[activeTab];
    if (!preferredCategory) return;
    if (watchGuideKnownForActiveTab !== undefined) return;

    let cancelled = false;
    const checkWatchGuideAvailability = async () => {
      try {
        const response = await getTrainingVideos({
          category: preferredCategory,
          is_active: true,
          page: 1,
          page_size: 100,
        });
        const hasPlayable = response.items.some(
          (item) => item.is_active && !item.is_delete && !!item.storage_link,
        );
        if (!cancelled) {
          setWatchGuideAvailability((prev) => ({
            ...prev,
            [activeTab]: hasPlayable,
          }));
        }
      } catch {
        if (!cancelled) {
          setWatchGuideAvailability((prev) => ({ ...prev, [activeTab]: false }));
        }
      }
    };

    void checkWatchGuideAvailability();
    return () => {
      cancelled = true;
    };
  }, [activeTab, watchGuideKnownForActiveTab]);

  const handleWatchGuideClick = useCallback(async () => {
    setWatchGuideLoading(true);
    setWatchGuideError(null);
    setWatchGuideUserStarted(false);

    try {
      if (watchGuideVideoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(watchGuideVideoUrl);
      }
      setWatchGuideVideoUrl("");

      const preferredCategory = WATCH_GUIDE_CATEGORY_BY_TAB[activeTab];
      if (!preferredCategory) {
        throw new Error("Guide category is not configured for this tab");
      }

      const preferredCategoryResponse = await getTrainingVideos({
        category: preferredCategory,
        is_active: true,
        page: 1,
        page_size: 100,
      });

      const firstPlayableVideo = preferredCategoryResponse.items.find(
        (item) => item.is_active && !item.is_delete && item.storage_link,
      );

      if (!firstPlayableVideo?.storage_link) {
        throw new Error("No guide video available");
      }

      setWatchGuideVideo(firstPlayableVideo);
      setWatchGuideTitle(firstPlayableVideo.name || "Watch Guide");

      const playbackUrl = isServerHostedTrainingVideo(
        firstPlayableVideo.storage_link,
      )
        ? await getTrainingVideoPlaybackUrl(firstPlayableVideo.storage_link)
        : resolveTrainingVideoUrl(firstPlayableVideo.storage_link);

      if (!playbackUrl) {
        throw new Error("Unable to load guide video");
      }

      setWatchGuideVideoUrl(playbackUrl);
      setWatchGuideOpen(true);
    } catch (error: any) {
      setWatchGuideError(error?.message || "Unable to load guide video");
      setWatchGuideOpen(true);
    } finally {
      setWatchGuideLoading(false);
    }
  }, [activeTab, watchGuideVideoUrl]);

  // Load data on component mount
  //   useEffect(() => {
  //     loadFacilityTypes();
  //   }, []);

  const handleTabClick = async (
    tab:
      | "dashboard"
      | "facilityProfile"
      | "facilities"
      | "oig"
      | "penalty"
      | "compliance"
      | "riskanalysis"
      | "surveyorvisit",
  ) => {
    // Block leaving Facility Profile only on first-time setup (no saved/approved profile yet).
    // Once the user has saved (is_profile_approved or session stamp), other tabs stay usable.
    if (activeTab === "facilityProfile" && tab !== "facilityProfile") {
      const facility =
        selectedFacility || getSelectedFacilityFromSession<Facility>();
      const fid = facility?.facility_id;
      const profileAccepted =
        facility != null &&
        (facility.is_profile_approved === true ||
          (typeof fid === "number" && readProfileAcceptedIds().has(fid)));
      if (facility && !profileAccepted) {
        toast.warning("Please save your facility profile before switching tabs.", {
          toastId: "profile-save-required",
        });
        return;
      }
    }

    // When facility type not confirmed and no type already determined, require Provider Type selection before Penalty/Risk
    if (tab === "penalty" || tab === "riskanalysis") {
      const facility =
        selectedFacility || getSelectedFacilityFromSession<Facility>();
      const confirmed = (facility as any)?.facility_type_confirmed === true;
      const hasAviationProfile = !!getAviationProfileSlug(facility);
      const hasDeterminedType = hasDeterminedFacilityProfileForTabs(facility);
      if (facility && isAviationFacility(facility) && !hasAviationProfile) {
        setActiveTab("facilityProfile");
        toast.warning("Please complete the aviation profile before opening this tab.", {
          toastId: "aviation-profile-required",
        });
        return;
      }
      if (facility && !confirmed && !hasDeterminedType) {
        setProviderTypeModalPendingTab(tab);
        setProviderTypeModalOpen(true);
        setProviderTypeSelectedSlug(
          (facility as any)?.facility_type_slug ||
            (facility as any)?.facility_type ||
            "",
        );
        setProviderTypeOptionsLoading(true);
        const countryCode =
          (facility as any)?.country_code ||
          (facility as any)?.country ||
          getSelectedCountryCode();
        getProviderTypeOptions(countryCode)
          .then((res) =>
            setProviderTypeOptions(res.provider_type_options ?? []),
          )
          .catch(() => setProviderTypeOptions([]))
          .finally(() => setProviderTypeOptionsLoading(false));
        return;
      }
    }

    setActiveTab(tab);

    // Log Mock Survey tab access to audit log
    if (tab === "surveyorvisit") {
      void logMockSurveyAuditEvent("VIEW", "/api/v1/mock-survey/", "GET", {
        page: "Mock Survey",
        action: "VIEW",
      });
    }
  };

  const handleDashboardNavigate = useCallback(
    (
      target: FacilityDashboardNavigateTarget,
      options?: FacilityDashboardNavigateOptions,
    ) => {
      if (target === "discover") {
        const discoverState =
          options && "category" in options
            ? options
            : { category: "latest-updates" as const };
        navigate("/dashboard/discover", {
          state: discoverState,
        });
        return;
      }
      if (target === "compliance") {
        setComplianceFocusSection(
          options && "section" in options && options.section === "policy"
            ? "policy"
            : "policy",
        );
        void handleTabClick("compliance");
        return;
      }
      if (target === "riskanalysis") {
        const section =
          options && "section" in options ? options.section : "incident";
        if (section === "incident" || section === "survey") {
          setRiskFocusSection(section);
        }
        void handleTabClick("riskanalysis");
        return;
      }
      void handleTabClick(target);
    },
    [navigate, handleTabClick],
  );

  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(
    () => {
      if (shouldDeferSessionSelectedFacilityOnMount(postPurchaseTargetOnMount)) {
        return null;
      }
      const fromSession = readSelectedFacilityFromSession();
      if (!fromSession) return null;
      const want =
        getRequestVerticalOverride() ||
        getPreviewVertical() ||
        null;
      if (
        want &&
        (fromSession.vertical || "healthcare").toString().trim().toLowerCase() !==
          want
      ) {
        try {
          sessionStorage.removeItem("filtered_selectedFacility");
        } catch {
          /* ignore */
        }
        return null;
      }
      return fromSession;
    },
  );
  selectedFacilityIdRef.current = selectedFacility?.facility_id ?? null;

  const loadDashboardBff = useCallback(async (facility: Facility | null) => {
    const facilityId = facility?.facility_id;
    if (!facilityId) {
      return;
    }
    const countryCode = facility.country_code || undefined;
    const inFlightKey = `${facilityId}:${countryCode || "USA"}`;
    if (dashboardBffInFlightRef.current === inFlightKey) {
      return;
    }
    dashboardBffInFlightRef.current = inFlightKey;

    const userId = parseInt(getCurrentUserId(), 10);
    if (!userId) {
      setDashboardBffError("Not authenticated");
      setDashboardBffLoading(false);
      dashboardBffReadyRef.current = true;
      dashboardBffInFlightRef.current = null;
      return;
    }
    setDashboardBffLoading(true);
    setDashboardBffError(null);
    setDashboardBffData(null);
    dashboardBffReadyRef.current = false;
    try {
      const res = await getFacilityDashboard(facilityId, userId, countryCode);
      setDashboardBffData(
        normalizeFacilityDashboardResponse(res as unknown as Record<string, unknown>),
      );
    } catch (e) {
      if (shouldRepairFacilityDashboardAccess(e, facility)) {
        try {
          const linked = await ensureSelfReportedFacilityEntitlement(facility);
          if (linked.facilityId) {
            const repairedId = linked.facilityId;
            if (repairedId !== facilityId) {
              setSelectedFacility((prev) =>
                prev && prev.facility_id === facilityId
                  ? { ...prev, facility_id: repairedId, id: repairedId }
                  : prev,
              );
            }
            const repaired = await getFacilityDashboard(
              repairedId,
              userId,
              countryCode,
            );
            setDashboardBffData(
              normalizeFacilityDashboardResponse(
                repaired as unknown as Record<string, unknown>,
              ),
            );
            setDashboardBffError(null);
            return;
          }
        } catch {
          /* fall through to original error */
        }
      }
      setDashboardBffError(
        e instanceof Error ? e.message : "Failed to load dashboard",
      );
      setDashboardBffData(null);
    } finally {
      setDashboardBffLoading(false);
      dashboardBffReadyRef.current = true;
      dashboardBffInFlightRef.current = null;
    }
  }, []);

  const refreshDashboardBffTiles = useCallback(async (facility: Facility | null) => {
    const facilityId = facility?.facility_id;
    if (!facilityId) {
      return;
    }
    const countryCode = facility.country_code || undefined;
    const inFlightKey = `${facilityId}:${countryCode || "USA"}:soft`;
    if (dashboardBffSoftRefreshInFlightRef.current === inFlightKey) {
      return;
    }
    dashboardBffSoftRefreshInFlightRef.current = inFlightKey;

    const userId = parseInt(getCurrentUserId(), 10);
    if (!userId) {
      dashboardBffSoftRefreshInFlightRef.current = null;
      return;
    }
    try {
      const res = await getFacilityDashboard(facilityId, userId, countryCode);
      setDashboardBffData(
        normalizeFacilityDashboardResponse(res as unknown as Record<string, unknown>),
      );
    } catch {
      // Keep existing dashboard tiles on soft refresh failure.
    } finally {
      dashboardBffSoftRefreshInFlightRef.current = null;
    }
  }, []);

  const canLoadDashboardBff = useMemo(() => {
    if (!selectedFacility?.facility_id || selectedFacility.is_network) {
      return false;
    }
    if (permissionsLoading) {
      return false;
    }
    if (facilityPermissions.length === 0) {
      return canViewByName(FEATURE_NAMES.DASHBOARD);
    }
    const permission = facilityPermissions.find(
      (p) =>
        p.feature_name?.toLowerCase() === FEATURE_NAMES.DASHBOARD.toLowerCase(),
    );
    return permission ? coercePermissionBool(permission.view) : false;
  }, [
    selectedFacility?.facility_id,
    selectedFacility?.is_network,
    permissionsLoading,
    facilityPermissions,
  ]);

  // Prefetch dashboard BFF before other effects flood the browser connection pool.
  useLayoutEffect(() => {
    if (!selectedFacility?.facility_id) {
      return;
    }
    if (postPurchaseResolving) {
      return;
    }
    if (!canLoadDashboardBff) {
      return;
    }
    void loadDashboardBff(selectedFacility);
  }, [
    selectedFacility?.facility_id,
    selectedFacility?.country_code,
    dashboardRefreshKey,
    loadDashboardBff,
    postPurchaseResolving,
    canLoadDashboardBff,
  ]);

  // Session vertical override (exec preview OR aviation landing lock) + user
  // vertical context. Both feed `activeVertical`, which scopes the Selected-
  // Facility dropdown. After sales-bypass checkout for a non-healthcare
  // facility, Pricing syncs preview; if that was missed, recover from
  // post-purchase / preferred session.
  const [previewVerticalSlug, setPreviewVerticalSlug] = useState<string | null>(
    () => {
      const fromSession = getRequestVerticalOverride();
      if (fromSession) return fromSession;
      const existing = getPreviewVertical();
      if (existing) return existing;
      const fromPostPurchase = postPurchaseSelectionOnMount?.vertical;
      if (fromPostPurchase) {
        syncPreviewVerticalAfterPurchase(fromPostPurchase);
        return getRequestVerticalOverride() || getPreviewVertical();
      }
      try {
        const raw = sessionStorage.getItem("preferred_facility_for_pricing");
        if (raw) {
          const preferred = JSON.parse(raw) as {
            vertical?: string;
            facility_details?: { vertical?: string };
          };
          const v =
            preferred?.vertical ?? preferred?.facility_details?.vertical;
          if (typeof v === "string" && v.trim()) {
            syncPreviewVerticalAfterPurchase(v);
            return getRequestVerticalOverride() || getPreviewVertical();
          }
        }
      } catch {
        /* ignore */
      }
      return null;
    },
  );
  const [verticalContext, setVerticalContext] =
    useState<VerticalContextResponse | null>(null);
  /**
   * Vertical the dropdown should filter against:
   *   1. Session override (exec preview switcher OR /aviation landing lock)
   *   2. The user's effective vertical from /me/vertical-context
   *   3. The user's signup vertical
   *   4. "healthcare" (legacy default — matches Facility.vertical server default)
   */
  const activeVertical = useMemo<string>(() => {
    const override = getRequestVerticalOverride() || previewVerticalSlug;
    if (override) return override;
    return (
      verticalContext?.effective_vertical ||
      verticalContext?.user_vertical ||
      "healthcare"
    );
  }, [previewVerticalSlug, verticalContext]);
  const logMockSurveyAuditEvent = useCallback(
    async (
      actionType:
        | "VIEW"
        | "UPLOAD"
        | "DOWNLOAD"
        | "DELETE"
        | "CREATE"
        | "UPDATE"
        | "CONNECT_WITH_AFFABLEFLOW"
        | "UPLOAD_FROM_AFFABLE",
      endpoint: string,
      method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
      actionDetails: Record<string, any> = {},
      statusCode = 200,
      errorMessage: string | null = null,
    ) => {
      const userId = parseInt(getCurrentUserId(), 10);
      if (!Number.isFinite(userId)) return;
      const facility = selectedFacility || getSelectedFacilityFromSession();
      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint,
        method,
        request_payload: {},
        response_payload: {},
        status_code: statusCode,
        error_message: errorMessage,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: "Mock Survey",
          ...actionDetails,
        },
        action_type: actionType,
        action_status: errorMessage ? "FAILURE" : "SUCCESS",
      });
    },
    [selectedFacility],
  );

  // Profile-accepted tracking is now handled via is_profile_approved on each
  // Facility object (stamped at accept-time and restored from sessionStorage on
  // mount).  No separate state or ref is needed here.

  // Provider Type modal when NPI is ambiguous (USA): must select type before Penalty/Risk tabs
  const [providerTypeModalOpen, setProviderTypeModalOpen] = useState(false);
  const [providerTypeModalPendingTab, setProviderTypeModalPendingTab] =
    useState<
      | "facilities"
      | "oig"
      | "penalty"
      | "compliance"
      | "riskanalysis"
      | "surveyorvisit"
    >("penalty");
  const [providerTypeOptions, setProviderTypeOptions] = useState<
    ProviderTypeOption[]
  >([]);
  const [providerTypeSelectedSlug, setProviderTypeSelectedSlug] =
    useState<string>("");
  const [providerTypeOptionsLoading, setProviderTypeOptionsLoading] =
    useState(false);

  // AffableFLOW connection hook
  const getFacilityData = useCallback(
    () => selectedFacility || getSelectedFacilityFromSession<Facility>(),
    [selectedFacility],
  );

  useAffableConnect({ getFacilityData, workflowType: "MockSurvey" });

  // Resolve facility once for render-time checks (e.g., Affable connection status)
  const resolvedFacility = useMemo(() => getFacilityData(), [getFacilityData]);
  const isAffableConnected = Boolean(resolvedFacility?.is_affable_connected);
  // Mock Survey: client-side uploads (keyed per facility)
  const MOCK_SURVEY_STORAGE_KEY = "mockSurveyFiles";
  const [mockSurveyFiles, setMockSurveyFiles] = useState<
    {
      id: string;
      name: string;
      url: string; // data URL for persistence
      type: string;
      comment: string;
      size: number;
      notes?: {
        id: string;
        text: string;
        page?: number;
        xPct?: number;
        yPct?: number;
        highlightedText?: string;
      }[];
      inlineOnly?: boolean;
      fileid?: string | number;
      justAdded?: boolean;
    }[]
  >([]);
  const [savingFileId, setSavingFileId] = useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<
    string | number | null
  >(null);
  const [deletingFileId, setDeletingFileId] = useState<string | number | null>(
    null,
  );
  const [mockSurveyFileToDelete, setMockSurveyFileToDelete] = useState<{
    id: string;
    name: string;
    fileid?: string | number;
  } | null>(null);
  const [uploadingToAffableFileId, setUploadingToAffableFileId] = useState<
    string | number | null
  >(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Server uploaded mock survey files
  const [serverMockFiles, setServerMockFiles] = useState<
    Array<{
      id: string | number;
      filename: string;
      filecontent?: string;
      created_at?: string;
      isAffable?: boolean; // Indicates if file was uploaded from AffableFLOW
      DocumentId?: number;
    }>
  >([]);
  const [serverFilesLoading, setServerFilesLoading] = useState<boolean>(false);
  const [mockFilesPage, setMockFilesPage] = useState(1);
  const [mockFilesRowsPerPage, setMockFilesRowsPerPage] = useState(10);
  // Derive a storage key that is scoped to the currently selected facility
  const getMockSurveyStorageKey = useCallback(() => {
    const facilityKey =
      selectedFacility?.facility_id ??
      selectedFacility?.facility_name ??
      "default";
    return `${MOCK_SURVEY_STORAGE_KEY}_${facilityKey}`;
  }, [selectedFacility?.facility_id, selectedFacility?.facility_name]);
  // Mock survey upload loading state
  const [mockUploadLoading, setMockUploadLoading] = useState<boolean>(false);

  const totalMockFilesPages = Math.max(
    1,
    Math.ceil(serverMockFiles.length / mockFilesRowsPerPage),
  );
  const mockFilesStartIndex = (mockFilesPage - 1) * mockFilesRowsPerPage;
  const paginatedServerMockFiles = serverMockFiles.slice(
    mockFilesStartIndex,
    mockFilesStartIndex + mockFilesRowsPerPage,
  );

  useEffect(() => {
    if (mockFilesPage > totalMockFilesPages) {
      setMockFilesPage(totalMockFilesPages);
    }
  }, [mockFilesPage, totalMockFilesPages]);

  // AffableFLOW integration states
  const [isAffablePanelClosed, setIsAffablePanelClosed] = useState(false);
  const [isSyncingAffableRecords, setIsSyncingAffableRecords] = useState(false);
  const [affableSyncError, setAffableSyncError] = useState<string | null>(null);
  const [hasAttemptedAffableSync, setHasAttemptedAffableSync] = useState(false);
  const [affableDocuments, setAffableDocuments] = useState<any[]>([]);
  const [affableRecords, setAffableRecords] = useState<any[]>([]);
  const [selectedAffableDocumentId, setSelectedAffableDocumentId] = useState<
    number | null
  >(null);
  const [selectedAffableFolderId, setSelectedAffableFolderId] = useState<
    number | null
  >(null);
  const [selectedAffableFileKey, setSelectedAffableFileKey] = useState<
    string | null
  >(null);
  const [loadingAffableFile, setLoadingAffableFile] = useState(false);
  const [errorAffableFile, setErrorAffableFile] = useState<string | null>(null);
  const [affableRecordCount, setAffableRecordCount] = useState(0);
  const [affableDocsPage, setAffableDocsPage] = useState(1);
  const [affableRowsPerPage, setAffableRowsPerPage] = useState(10);
  const [affableTableColumns, setAffableTableColumns] = useState<string[]>([]);
  const [affableUploadedFile, setAffableUploadedFile] = useState<{
    name: string;
    size: number;
  } | null>(null);

  // Helper function to convert base64 to File
  const base64ToFile = (
    base64: string,
    filename: string,
    mimeType: string,
  ): File => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
  };

  // Helper function to normalize AffableFLOW response
  const normalizeAffableResponse = (
    response: ExternalGetRecordAndFilesDetailsResponse | null | undefined,
  ): {
    documents: any[];
    records: any[];
    totalCount: number;
  } => {
    const raw = (response || {}) as any;
    const dataItem =
      Array.isArray(raw?.Data) && raw.Data.length > 0 ? raw.Data[0] : null;
    const rawDocuments =
      dataItem?.RecordFileData && Array.isArray(dataItem.RecordFileData)
        ? dataItem.RecordFileData
        : [];
    const rawRecords =
      dataItem?.RecordList && Array.isArray(dataItem.RecordList)
        ? dataItem.RecordList
        : [];

    const documents = rawDocuments
      .map((doc: any) => {
        const documentId = Number(doc?.DocumentId);
        if (isNaN(documentId)) return null;

        const folderList = Array.isArray(doc?.Folders) ? doc.Folders : [];
        const folders = folderList
          .map((folder: any) => {
            const folderId = Number(folder?.FolderId);
            if (isNaN(folderId)) return null;

            const imageList = Array.isArray(folder?.Images)
              ? folder.Images
              : [];
            const images = imageList
              .map((image: any) => {
                const imageId = Number(image?.ImageId);
                if (isNaN(imageId)) return null;
                return {
                  id: imageId,
                  name: (image?.ImageName || "").trim() || `File ${imageId}`,
                  type: (image?.ImageType || "").trim(),
                  createdAt: (image?.ImageCreatedDate || "").trim(),
                };
              })
              .filter(Boolean);

            const rawFolderName = (folder?.FolderName || "").trim();
            const displayName =
              rawFolderName.toLowerCase() === "uncategorized"
                ? "Default"
                : rawFolderName || `Folder ${folderId}`;

            return {
              id: folderId,
              name: displayName,
              images,
            };
          })
          .filter(Boolean);

        return {
          id: documentId,
          folders,
        };
      })
      .filter(Boolean);

    return {
      documents,
      records: rawRecords || [],
      totalCount: Number(raw?.Record_TotalCount) || 0,
    };
  };

  // Get selected document and folder
  const selectedAffableDocument = useMemo(() => {
    if (!affableDocuments.length) return null;
    if (selectedAffableDocumentId === null) return affableDocuments[0];
    return (
      affableDocuments.find(
        (doc: any) => doc.id === selectedAffableDocumentId,
      ) || affableDocuments[0]
    );
  }, [affableDocuments, selectedAffableDocumentId]);

  const selectedAffableFolder = useMemo(() => {
    if (!selectedAffableDocument) return null;
    if (selectedAffableFolderId === null)
      return selectedAffableDocument.folders[0] || null;
    return (
      selectedAffableDocument.folders.find(
        (folder: any) => folder.id === selectedAffableFolderId,
      ) ||
      selectedAffableDocument.folders[0] ||
      null
    );
  }, [selectedAffableDocument, selectedAffableFolderId]);

  // Handle AffableFLOW records sync
  const handleAffableRecordsSync = async (
    page: number = 0,
    pageSize?: number,
  ) => {
    if (!resolvedFacility) {
      toast.error("Please select a facility first.");
      return;
    }

    const orgName = (resolvedFacility as any)?.org_name;
    if (!orgName) {
      toast.error("Facility organization name unavailable.");
      return;
    }

    setIsSyncingAffableRecords(true);
    setAffableSyncError(null);
    setIsAffablePanelClosed(false);
    setHasAttemptedAffableSync(true);

    try {
      // Check sessionStorage first for cached workflowId
      const storageKey = `mockSurvey_workflowId_${orgName}`;
      let resolvedDeptId: number | undefined;
      const cachedWorkflowId = sessionStorage.getItem(storageKey);

      if (cachedWorkflowId) {
        const parsedId = parseInt(cachedWorkflowId, 10);
        if (Number.isFinite(parsedId) && parsedId > 0) {
          resolvedDeptId = parsedId;
        }
      }

      // If not in cache, fetch from API
      if (!resolvedDeptId) {
        const mapping =
          await affablebpmIntegrationService.getWorkflowMappingByTypeAndOrg({
            orgName,
            type: "MockSurvey",
          });

        if (
          mapping?.success &&
          Array.isArray(mapping.data) &&
          mapping.data.length > 0
        ) {
          const firstMapping = mapping.data[0];
          if (
            typeof firstMapping?.workflowId === "number" &&
            Number.isFinite(firstMapping.workflowId) &&
            firstMapping.workflowId > 0
          ) {
            resolvedDeptId = firstMapping.workflowId;
            // Save to sessionStorage for future use
            sessionStorage.setItem(storageKey, String(resolvedDeptId));
          }
        }
      }

      // Use resolved workflowId, pass undefined if not found (API will handle fallback)
      const rowsPerPage = affableRowsPerPage || 10;

      const response =
        await affablebpmIntegrationService.getRecordAndFilesDetails({
          orgName,
          deptId: resolvedDeptId,
          perPage: rowsPerPage,
          pageNumber: page, // API uses 0-based page numbers (0 for first page)
        });

      if (response?.Status !== "Success" && response?.Status !== "SUCCESS") {
        const errorMsg =
          typeof response?.ErrorMsg === "string"
            ? response.ErrorMsg
            : typeof response?.Message === "string"
              ? response.Message
              : "Failed to fetch records from AffableBPM.";
        setAffableDocuments([]);
        setAffableRecords([]);
        setAffableRecordCount(0);
        setAffableTableColumns([]);
        setIsAffablePanelClosed(false);
        setAffableSyncError(errorMsg);
        return;
      }

      const { documents, records, totalCount } =
        normalizeAffableResponse(response);

      // Extract table columns from the first record
      const allColumns = records.length > 0 ? Object.keys(records[0]) : [];
      const filteredColumns = allColumns.filter(
        (col) => col !== "DocumentID" && col !== "DeptID" && col !== "RowNum",
      );
      const columns = filteredColumns.length > 0 ? [filteredColumns[0]] : [];

      setAffableDocuments(documents);
      setAffableRecords(records);
      setAffableRecordCount(totalCount);
      setAffableTableColumns(columns);
      setIsAffablePanelClosed(false);

      // Refresh uploaded files list when panel is opened (from AffableFLOW)
      const facilityId = resolvedFacility?.facility_id
        ? String(resolvedFacility.facility_id)
        : resolvedFacility?.facility_name || "";
      if (facilityId) {
        try {
          const list = await mockSurveyService.listFiles(facilityId);
          setServerMockFiles(
            (list?.items || []).map((i: any) => ({
              id: i.id,
              filename: i.filename,
              filecontent: i.fileblob,
              created_at: i.created_at,
              DocumentId: i.DocumentId ?? 0,
            })),
          );
        } catch (err) {
          console.error("Failed to refresh file list when opening panel", err);
        }
      }

      if (!documents.length) {
        setAffableSyncError(
          "No folders returned from AffableBPM for this facility.",
        );
      }
    } catch (error) {
      console.error("Failed to sync AffableFLOW documents:", error);
      const errorMsg =
        error instanceof Error
          ? error.message || "Failed to sync with AffableFLOW."
          : "Failed to sync with AffableFLOW.";
      setAffableSyncError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSyncingAffableRecords(false);
    }
  };

  // Handle uploading selected AffableFLOW file
  const handleUploadSelectedAffableFile = async () => {
    if (!selectedAffableFileKey || !selectedAffableFolder) {
      setErrorAffableFile("No file selected");
      return;
    }

    if (!selectedAffableDocument) {
      setErrorAffableFile("No document selected");
      return;
    }

    const [folderIdStr, imageIdStr] = selectedAffableFileKey.split("-");
    const imageId = Number(imageIdStr);
    const folderId = Number(folderIdStr);

    if (!imageId || isNaN(imageId)) {
      setErrorAffableFile("Invalid file ID");
      return;
    }

    const selectedImage = selectedAffableFolder.images.find(
      (img: any) => img.id === imageId,
    );

    if (!selectedImage) {
      setErrorAffableFile("Selected file not found");
      return;
    }

    setLoadingAffableFile(true);
    setErrorAffableFile(null);

    try {
      const orgName = (resolvedFacility as any)?.org_name;
      if (!orgName) {
        throw new Error("Facility organization name unavailable.");
      }

      // Get workflowId from sessionStorage
      const storageKey = `mockSurvey_workflowId_${orgName}`;
      let deptId: number | undefined;
      const cachedWorkflowId = sessionStorage.getItem(storageKey);

      if (cachedWorkflowId) {
        const parsedId = parseInt(cachedWorkflowId, 10);
        if (Number.isFinite(parsedId) && parsedId > 0) {
          deptId = parsedId;
        }
      }

      // If not in cache, fetch from API
      if (!deptId) {
        const mapping =
          await affablebpmIntegrationService.getWorkflowMappingByTypeAndOrg({
            orgName,
            type: "MockSurvey",
          });

        if (
          mapping?.success &&
          Array.isArray(mapping.data) &&
          mapping.data.length > 0
        ) {
          const firstMapping = mapping.data[0];
          if (
            typeof firstMapping?.workflowId === "number" &&
            Number.isFinite(firstMapping.workflowId) &&
            firstMapping.workflowId > 0
          ) {
            deptId = firstMapping.workflowId;
            // Save to sessionStorage for future use
            sessionStorage.setItem(storageKey, String(deptId));
          }
        }
      }

      const fileResponse: ExternalGetFileByFileIdResponse =
        await affablebpmIntegrationService.getFileByFileId({
          orgName,
          fileId: imageId,
          deptId: deptId,
          orgId: 0,
        });

      if (fileResponse.Status !== "Success" || !fileResponse.File) {
        throw new Error(
          fileResponse.Status || "Failed to fetch file from AffableBPM",
        );
      }

      const fileData = fileResponse.File;

      if (!fileData.Base64 || fileData.Base64.trim() === "") {
        throw new Error("File data is empty");
      }

      const imageType = fileData.ImageType || selectedImage.type || ".pdf";
      const mimeTypeMap: Record<string, string> = {
        ".pdf": "application/pdf",
        ".doc": "application/msword",
        ".docx":
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".html": "text/html",
        ".htm": "text/html",
        ".txt": "text/plain",
        ".xls": "application/vnd.ms-excel",
        ".xlsx":
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };
      const mimeType =
        mimeTypeMap[imageType.toLowerCase()] || "application/octet-stream";

      let baseName = fileData.ImageName || selectedImage.name;
      if (baseName.toLowerCase().endsWith(imageType.toLowerCase())) {
        baseName = baseName.slice(0, -imageType.length);
      }
      const filename = `${baseName}${imageType}`;

      const file = base64ToFile(fileData.Base64, filename, mimeType);

      // Get facility ID for upload
      const facilityId = selectedFacility?.facility_id
        ? String(selectedFacility.facility_id)
        : selectedFacility?.facility_name || "test";

      // Upload to mock survey with AffableBPM IDs
      const result = await mockSurveyService.uploadFile(file, facilityId, {
        documentId: selectedAffableDocument.id,
        folderId: folderId,
        fileId: imageId, // AffableBPM ImageId
      });

      if (result.success || result.file_id) {
        toast.success("File uploaded from AffableFLOW successfully!");
        void logMockSurveyAuditEvent(
          "UPLOAD_FROM_AFFABLE",
          "/api/v1/mock-survey/mock-survey-file-upload",
          "POST",
          {
            action: "UPLOAD_FROM_AFFABLE",
            fileName: file.name,
            fileId: result.file_id ?? imageId,
            source: "AffableFLOW",
          },
        );

        // Store uploaded file info for display
        setAffableUploadedFile({ name: file.name, size: file.size });

        // Uncheck the selected file
        setSelectedAffableFileKey(null);

        // Refresh server files list (from AffableFLOW)
        try {
          const list = await mockSurveyService.listFiles(facilityId);
          setServerMockFiles(
            (list?.items || []).map((i: any) => ({
              id: i.id,
              filename: i.filename,
              filecontent: i.fileblob,
              created_at: i.created_at,
              isAffable:
                i.isAffable ||
                i.is_affable ||
                (i.DocumentId && i.DocumentId > 0) ||
                false,
              DocumentId: i.DocumentId ?? 0,
            })),
          );
        } catch (err) {
          console.error("Failed to refresh file list", err);
        }
      } else {
        toast.error(result.message || "Failed to upload file");
      }
    } catch (error) {
      console.error("Failed to upload AffableBPM file:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Failed to upload file";
      setErrorAffableFile(errorMsg);
      toast.error(errorMsg);
      void logMockSurveyAuditEvent(
        "UPLOAD_FROM_AFFABLE",
        "/api/v1/mock-survey/mock-survey-file-upload",
        "POST",
        {
          action: "UPLOAD_FROM_AFFABLE",
          source: "AffableFLOW",
          fileId: selectedImage?.id,
          fileName: selectedImage?.name,
        },
        500,
        errorMsg,
      );
    } finally {
      setLoadingAffableFile(false);
    }
  };

  // Function to load/refresh server files list
  const loadServerFilesList = useCallback(async () => {
    if (activeTab !== "surveyorvisit") return;
    const facilityId = selectedFacility?.facility_id
      ? String(selectedFacility.facility_id)
      : selectedFacility?.facility_name || "";
    if (!facilityId) return;
    try {
      setServerFilesLoading(true);
      const list = await mockSurveyService.listFiles(facilityId);
      setServerMockFiles(
        (list?.items || []).map((i: any) => ({
          id: i.id,
          filename: i.filename,
          filecontent: i.fileblob,
          created_at: i.created_at,
          isAffable:
            i.isAffable ||
            i.is_affable ||
            (i.DocumentId && i.DocumentId > 0) ||
            false,
          DocumentId: i.DocumentId ?? 0,
        })),
      );
      setServerFilesLoading(false);
    } catch (e) {
      console.error("Failed to load server mock files", e);
      setServerFilesLoading(false);
    }
  }, [
    activeTab,
    selectedFacility?.facility_id,
    selectedFacility?.facility_name,
  ]);

  // Handle uploading file back to AffableFLOW
  const handleUploadToAffableFLOW = async (serverFile: any) => {
    if (!resolvedFacility) {
      toast.error("Please select a facility first.");
      return;
    }

    if (!isAffableConnected) {
      toast.error("Please connect with AffableFLOW first.");
      return;
    }

    // Ensure AffableFLOW documents are loaded
    if (affableDocuments.length === 0) {
      toast.error("Please open AffableFLOW Document Manager first.");
      return;
    }

    setUploadingToAffableFileId(serverFile.id);

    // Use currently selected document and folder IDs (must be explicitly selected)
    // Allow folderId to be 0 (default folder)
    if (
      !selectedAffableDocumentId ||
      selectedAffableFolderId === null ||
      selectedAffableFolderId === undefined
    ) {
      toast.error(
        "Please select a document and folder in AffableFLOW Document Manager.",
      );
      return;
    }

    const documentId = selectedAffableDocumentId;
    const folderId = selectedAffableFolderId;

    const orgName = (resolvedFacility as any)?.org_name;
    if (!orgName) {
      toast.error("Organization name not found.");
      return;
    }

    // Check sessionStorage for cached workflowId
    const storageKey = `mockSurvey_workflowId_${orgName}`;
    let resolvedDeptId: number | undefined;
    const cachedWorkflowId = sessionStorage.getItem(storageKey);

    if (cachedWorkflowId) {
      const parsedId = parseInt(cachedWorkflowId, 10);
      if (Number.isFinite(parsedId) && parsedId > 0) {
        resolvedDeptId = parsedId;
      }
    }

    // If not in cache, fetch from API
    if (!resolvedDeptId) {
      try {
        const mapping =
          await affablebpmIntegrationService.getWorkflowMappingByTypeAndOrg({
            orgName,
            type: "MockSurvey",
          });

        if (
          mapping?.success &&
          Array.isArray(mapping.data) &&
          mapping.data.length > 0
        ) {
          const firstMapping = mapping.data[0];
          if (
            typeof firstMapping?.workflowId === "number" &&
            Number.isFinite(firstMapping.workflowId) &&
            firstMapping.workflowId > 0
          ) {
            resolvedDeptId = firstMapping.workflowId;
            sessionStorage.setItem(storageKey, String(resolvedDeptId));
          }
        }
      } catch (error) {
        console.error("Failed to fetch workflow mapping:", error);
      }
    }

    if (!resolvedDeptId) {
      toast.error("Failed to get workflow ID. Please try again.");
      return;
    }

    try {
      // Get file content
      if (!serverFile.filecontent) {
        toast.error("File content not available.");
        return;
      }

      // Decode base64 content
      const binaryString = atob(serverFile.filecontent);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Determine MIME type based on file extension
      const fileExtension = (
        serverFile.filename.split(".").pop() || ""
      ).toLowerCase();
      let mimeType = "application/octet-stream";
      if (fileExtension === "docx" || fileExtension === "doc") {
        mimeType =
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      } else if (fileExtension === "pdf") {
        mimeType = "application/pdf";
      } else if (fileExtension === "xlsx" || fileExtension === "xls") {
        mimeType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      }

      const blob = new Blob([bytes], { type: mimeType });

      // Append current datetime to filename (same format as download: MMDDYYYYHHMM)
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const month = pad(now.getMonth() + 1);
      const day = pad(now.getDate());
      const year = now.getFullYear();
      const hours = pad(now.getHours());
      const minutes = pad(now.getMinutes());
      const timestamp = `${month}${day}${year}${hours}${minutes}`;

      const baseName = serverFile.filename.replace(/\.[^/.]+$/, ""); // Remove extension
      const extension = serverFile.filename.split(".").pop() || "";
      const uploadFileName = `${baseName}_${timestamp}.${extension}`;

      const file = new File([blob], uploadFileName, { type: mimeType });

      // Fetch comments and generate XLSX file
      let commentsXlsxFile: File | undefined;
      try {
        const commentsResponse = await mockSurveyService.getComments(
          serverFile.id,
        );
        if (commentsResponse?.items && commentsResponse.items.length > 0) {
          // Prepare data for Excel
          const commentsData: any[] = [];

          commentsResponse.items.forEach((item, index) => {
            const commentJson = item.commentsjson;
            commentsData.push({
              "Comment #": index + 1,
              "Selected Text": commentJson.selectedText || "",
              "Comment Text": commentJson.commentText || "",
              Author: commentJson.author || "",
              Timestamp: item.created_at
                ? new Date(item.created_at).toLocaleString()
                : "",
            });
          });

          // Create workbook and worksheet
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(commentsData);

          // Set column widths
          const colWidths = [
            { wch: 15 }, // Comment #
            { wch: 30 }, // Selected Text
            { wch: 50 }, // Comment Text
            { wch: 20 }, // Author
            { wch: 25 }, // Timestamp
          ];
          ws["!cols"] = colWidths;

          // Add worksheet to workbook
          XLSX.utils.book_append_sheet(wb, ws, "Comments");

          // Generate XLSX file as ArrayBuffer
          const xlsxArrayBuffer = XLSX.write(wb, {
            type: "array",
            bookType: "xlsx",
          });

          // Create File from ArrayBuffer
          const xlsxBaseName = baseName;
          const xlsxFileName = `${xlsxBaseName}_${timestamp}_comments.xlsx`;
          commentsXlsxFile = new File([xlsxArrayBuffer], xlsxFileName, {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
        }
      } catch (commentsError) {
        console.error(
          "Failed to fetch comments or generate XLSX:",
          commentsError,
        );
        // Continue with upload even if comments fail
      }

      // Upload main DOCX file to AffableFLOW (first call)
      const uploadResult = await affablebpmIntegrationService.uploadFile({
        orgName,
        documentId,
        folderId,
        deptId: resolvedDeptId,
        orgId: 0,
        file: file,
        fileName: uploadFileName,
      });

      // Upload XLSX comments file to AffableFLOW (second call) if available
      if (commentsXlsxFile) {
        try {
          await affablebpmIntegrationService.uploadFile({
            orgName,
            documentId,
            folderId,
            deptId: resolvedDeptId,
            orgId: 0,
            file: commentsXlsxFile,
            fileName: commentsXlsxFile.name,
          });
          console.log(
            "✅ Comments XLSX file uploaded to AffableFLOW successfully",
          );
        } catch (xlsxError) {
          console.error("Failed to upload comments XLSX file:", xlsxError);
          // Don't fail the whole operation if XLSX upload fails
        }
      }

      // After successful upload to AffableFLOW, remove the file from mock survey
      // Commented out for now - do not delete file after external upload
      /*
      try {
        const removeResult = await mockSurveyService.removeFile(serverFile.id);
        if (removeResult.success) {
          toast.success(
            uploadResult?.Message ||
              "File uploaded to AffableFLOW and removed from mock survey successfully!",
          );

          // Remove file from local state
          setServerMockFiles((prev) =>
            prev.filter((f) => f.id !== serverFile.id),
          );
        } else {
          toast.success(
            uploadResult?.Message ||
              "File uploaded to AffableFLOW successfully!",
          );
          toast.warning(
            "File uploaded to AffableFLOW, but failed to remove from mock survey.",
          );
        }
      } catch (removeError) {
        console.error("Failed to remove file from mock survey:", removeError);
        toast.success(
          uploadResult?.Message || "File uploaded to AffableFLOW successfully!",
        );
        toast.warning(
          "File uploaded to AffableFLOW, but failed to remove from mock survey.",
        );
      }
      */

      // Show success message without deleting file
      toast.success(
        uploadResult?.Message || "File uploaded to AffableFLOW successfully!",
      );

      // Refresh AffableFLOW document library
      await handleAffableRecordsSync(affableDocsPage - 1);
    } catch (error) {
      console.error("Failed to upload file to AffableFLOW:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to upload file to AffableFLOW.";
      toast.error(message);
    } finally {
      setUploadingToAffableFileId(null);
    }
  };

  // No auto-selection - user must manually select a record
  // Removed auto-selection logic to allow user to explicitly choose a record

  // Save AffableFLOW panel state and selected facility to sessionStorage before navigating to Survey Viewer
  const saveAffablePanelState = useCallback(() => {
    const state = {
      isAffablePanelClosed,
      selectedAffableDocumentId,
      selectedAffableFolderId,
      selectedAffableFileKey,
      affableDocsPage,
      affableRowsPerPage,
      hasAttemptedAffableSync,
    };
    sessionStorage.setItem(
      "mockSurvey_affablePanelState",
      JSON.stringify(state),
    );

    // Also ensure selected facility is saved to sessionStorage
    if (selectedFacility) {
      sessionStorage.setItem(
        "filtered_selectedFacility",
        JSON.stringify(selectedFacility),
      );
    }
  }, [
    isAffablePanelClosed,
    selectedAffableDocumentId,
    selectedAffableFolderId,
    selectedAffableFileKey,
    affableDocsPage,
    affableRowsPerPage,
    hasAttemptedAffableSync,
    selectedFacility,
  ]);

  // Restore AffableFLOW panel state from sessionStorage
  const [hasRestoredState, setHasRestoredState] = useState(false);
  useEffect(() => {
    if (
      activeTab === "surveyorvisit" &&
      isAffableConnected &&
      !hasRestoredState
    ) {
      try {
        const savedStateStr = sessionStorage.getItem(
          "mockSurvey_affablePanelState",
        );
        if (savedStateStr) {
          const savedState = JSON.parse(savedStateStr);
          if (savedState.isAffablePanelClosed !== undefined) {
            setIsAffablePanelClosed(savedState.isAffablePanelClosed);
          }
          if (savedState.selectedAffableDocumentId !== undefined) {
            setSelectedAffableDocumentId(savedState.selectedAffableDocumentId);
          }
          if (savedState.selectedAffableFolderId !== undefined) {
            setSelectedAffableFolderId(savedState.selectedAffableFolderId);
          }
          if (savedState.selectedAffableFileKey !== undefined) {
            setSelectedAffableFileKey(savedState.selectedAffableFileKey);
          }
          if (savedState.affableDocsPage !== undefined) {
            setAffableDocsPage(savedState.affableDocsPage);
          }
          if (savedState.affableRowsPerPage !== undefined) {
            setAffableRowsPerPage(savedState.affableRowsPerPage);
          }
          if (savedState.hasAttemptedAffableSync !== undefined) {
            setHasAttemptedAffableSync(savedState.hasAttemptedAffableSync);
          }

          // If panel was open, reload documents if they're not already loaded
          if (
            savedState.isAffablePanelClosed === false &&
            affableDocuments.length === 0 &&
            !isSyncingAffableRecords
          ) {
            // Reload documents to restore the panel state
            handleAffableRecordsSync(
              savedState.affableDocsPage ? savedState.affableDocsPage - 1 : 0,
            );
          }

          setHasRestoredState(true);
        }
      } catch (error) {
        console.error("Failed to restore AffableFLOW panel state:", error);
        setHasRestoredState(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAffableConnected, hasRestoredState]);

  // Close AffableFLOW panel when facility changes
  useEffect(() => {
    if (selectedFacility?.facility_id) {
      setIsAffablePanelClosed(true);
      setHasAttemptedAffableSync(false);
      setAffableDocuments([]);
      setAffableRecords([]);
      setAffableRecordCount(0);
      setSelectedAffableDocumentId(null);
      setSelectedAffableFolderId(null);
      setSelectedAffableFileKey(null);
      setAffableSyncError(null);
      setErrorAffableFile(null);
      // Clear saved state when facility changes
      sessionStorage.removeItem("mockSurvey_affablePanelState");
      setHasRestoredState(false); // Reset restore flag to allow restoration for new facility
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility?.facility_id]);

  const shouldShowAffablePanel =
    isAffableConnected &&
    !isAffablePanelClosed &&
    (hasAttemptedAffableSync ||
      isSyncingAffableRecords ||
      affableDocuments.length > 0 ||
      Boolean(affableSyncError));

  const totalAffableDocPages = Math.ceil(
    affableRecordCount / affableRowsPerPage,
  );

  const handleMockFilesAdd = async (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0) return;

    // Check if user has add permission for Mock Survey
    if (!canAddMockSurvey()) {
      toast.error("You do not have permission to upload documents");
      return;
    }

    setMockUploadLoading(true);

    try {
      const rawFiles = Array.from(filesList);
      const usedNames = new Set<string>();

      (serverMockFiles || []).forEach((f) => {
        if (f?.filename) usedNames.add(normalizeFileName(f.filename));
      });

      const ensureUniqueName = (initialName: string): string => {
        let name = initialName && initialName.trim() ? initialName : "untitled";
        const extIndex = name.lastIndexOf(".");
        const base = extIndex > 0 ? name.slice(0, extIndex) : name;
        const ext = extIndex > 0 ? name.slice(extIndex) : "";
        const normalized = normalizeFileName(name);
        if (!usedNames.has(normalized)) {
          usedNames.add(normalized);
          return name;
        }
        let counter = 2;
        let candidate = "";
        do {
          candidate = `${base} (${counter})${ext}`;
          counter++;
        } while (usedNames.has(normalizeFileName(candidate)));
        usedNames.add(normalizeFileName(candidate));
        return candidate;
      };

      const files = rawFiles.map((file) => {
        const uniqueName = ensureUniqueName(file.name);
        if (uniqueName === file.name) return file;
        try {
          return new File([file], uniqueName, {
            type: file.type,
            lastModified: file.lastModified,
          });
        } catch (_) {
          console.warn(
            "Failed to create renamed File object; falling back to original name",
            file.name,
          );
          return file;
        }
      });

      // Determine facility ID for upload
      const facilityId = selectedFacility?.facility_id
        ? String(selectedFacility.facility_id)
        : selectedFacility?.facility_name || "test";

      for (const file of files) {
        const result = await mockSurveyService.uploadFile(file, facilityId);

        if (!(result?.success || result?.file_id)) {
          throw new Error(result?.message || "Failed to upload file");
        }
        void logMockSurveyAuditEvent(
          "UPLOAD",
          "/api/v1/mock-survey/mock-survey-file-upload",
          "POST",
          {
            action: "UPLOAD",
            fileName: file.name,
            fileId: result?.file_id,
            source: "Mock Survey",
          },
        );
      }

      toast.success("File(s) uploaded successfully");

      // Refresh server-side files so the new uploads appear in the list
      await loadServerFilesList();
    } catch (error: any) {
      console.error("Failed to upload mock survey file(s):", error);
      toast.error(
        error?.message || "Failed to upload file(s). Please try again.",
      );
      void logMockSurveyAuditEvent(
        "UPLOAD",
        "/api/v1/mock-survey/mock-survey-file-upload",
        "POST",
        {
          action: "UPLOAD",
          source: "Mock Survey",
        },
        500,
        error?.message || "Failed to upload file(s). Please try again.",
      );
    } finally {
      setMockUploadLoading(false);
      // reset the hidden input so selecting the same file again fires change
      try {
        if (fileInputRef.current) fileInputRef.current.value = "";
      } catch {}
    }
  };

  // Load server files whenever the Mock Survey tab becomes active or facility changes
  useEffect(() => {
    loadServerFilesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedFacility?.facility_id, mockSurveyFiles?.length]);

  const requestMockSurveyFileDelete = (file: {
    id: string;
    name: string;
    fileid?: string | number;
  }) => {
    setMockSurveyFileToDelete(file);
  };

  const removeMockFileBackend = async (file: {
    id: string;
    name: string;
    fileid?: string | number;
  }) => {
    // Use fileid if available, otherwise find by filename
    const fileid =
      (file as any).fileid ||
      serverMockFiles.find((it) => it.filename === file.name)?.id;

    if (fileid === undefined) {
      return;
    }

    setDeletingFileId(fileid);
    try {
      const result = await mockSurveyService.removeFile(fileid);

      if (result.success) {
        toast.success("File removed successfully");
        void logMockSurveyAuditEvent(
          "DELETE",
          "/api/v1/mock-survey/mock-survey-file-remove",
          "DELETE",
          {
            action: "DELETE",
            fileId: fileid,
            fileName: file.name,
          },
        );
      } else {
        toast.error(result.message || "Failed to remove file");
        void logMockSurveyAuditEvent(
          "DELETE",
          "/api/v1/mock-survey/mock-survey-file-remove",
          "DELETE",
          {
            action: "DELETE",
            fileId: fileid,
            fileName: file.name,
          },
          500,
          result.message || "Failed to remove file",
        );
      }
      // Refresh server list after deletion
      try {
        const facilityId = selectedFacility?.facility_id
          ? String(selectedFacility.facility_id)
          : selectedFacility?.facility_name || "";
        if (facilityId) {
          setServerFilesLoading(true);
          const list = await mockSurveyService.listFiles(facilityId);
          setServerMockFiles(
            (list?.items || []).map((i: any) => ({
              id: i.id,
              filename: i.filename,
              filecontent: i.fileblob,
              created_at: i.created_at,
              DocumentId: i.DocumentId ?? 0,
            })),
          );
          setServerFilesLoading(false);
        }
      } catch {
        setServerFilesLoading(false);
      }
    } catch (error) {
      console.error("Failed to remove file:", error);
      toast.error("Failed to remove file. Please try again.");
      void logMockSurveyAuditEvent(
        "DELETE",
        "/api/v1/mock-survey/mock-survey-file-remove",
        "DELETE",
        {
          action: "DELETE",
          fileId: fileid,
          fileName: file.name,
        },
        500,
        "Failed to remove file. Please try again.",
      );
    } finally {
      setDeletingFileId(null);
    }
  };

  // Load from session when facility changes (scoped per facility)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(getMockSurveyStorageKey());
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setMockSurveyFiles(
            parsed.map((item: any) => ({ ...item, justAdded: false })),
          );
        } else {
          setMockSurveyFiles([]);
        }
      } else {
        setMockSurveyFiles([]);
      }
    } catch (e) {
      // ignore
      setMockSurveyFiles([]);
    }
  }, [getMockSurveyStorageKey]);

  // Persist to session on change (scoped per facility)
  useEffect(() => {
    try {
      sessionStorage.setItem(
        getMockSurveyStorageKey(),
        JSON.stringify(mockSurveyFiles),
      );
    } catch (e) {
      // ignore
    }
  }, [mockSurveyFiles, getMockSurveyStorageKey]);

  const [selectedCountry, setSelectedCountry] = useState<string>(
    getSelectedCountryCode(),
  );

  useEffect(() => {
    let cancelled = false;

    const schedulePostPurchaseRetry = () => {
      if (postPurchaseRetryTimerRef.current != null) {
        clearTimeout(postPurchaseRetryTimerRef.current);
      }
      postPurchaseRetryTimerRef.current = setTimeout(() => {
        postPurchaseRetryTimerRef.current = null;
        if (!cancelled) {
          void loadFacilities();
        }
      }, POST_PURCHASE_FACILITY_RETRY_MS);
    };

    const loadFacilities = async () => {
      const postPurchaseTarget = postPurchaseTargetRef.current;
      const postPurchasePending = hasPostPurchaseFacilityTarget(
        postPurchaseTarget,
      );
      try {
        setFacilitiesLoading(true);
        if (postPurchasePending) {
          setPostPurchaseResolving(true);
        }
        const rawData: Facility[] = (await ensureMyFacilitiesLoaded({
          ...(postPurchasePending ? { bypassCache: true } : {}),
          vertical: activeVertical,
        })) as Facility[];
        if (cancelled) return;

        const data: Facility[] = rawData;
        setFacilities(data);

        const navSelectedFacility = normalizeNavigationFacility(
          initialNavSelectedFacilityRef.current,
        );
        let facilityToSelect: Facility | null = null;
        let postPurchaseResolved = false;

        if (postPurchasePending && postPurchaseTarget) {
          const matched = findFacilityForPostPurchaseTarget(
            data,
            postPurchaseTarget,
          );
          if (matched) {
            facilityToSelect = matched;
            postPurchaseResolved = true;
            navRequestedTabRef.current = resolvePostPurchaseNavigationActiveTab(
              navRequestedTabRef.current,
              postPurchaseTarget,
            );
            setComplianceData(null);
          } else if (
            shouldRetryPostPurchaseFacilityFetch(
              false,
              postPurchaseRetryAttemptRef.current,
            )
          ) {
            postPurchaseRetryAttemptRef.current += 1;
            schedulePostPurchaseRetry();
            return;
          } else {
            const synthetic =
              selectableRowFromPostPurchase(
                readPostPurchaseFacilitySelection(),
                readPreferredFacilityForPricing(),
              ) ||
              preferredFacilityToSelectableRow(
                readPreferredFacilityForPricing() || {},
              );
            if (synthetic) {
              facilityToSelect = synthetic as Facility;
              postPurchaseResolved = true;
              setFacilities((prev) =>
                prev.some((f) => f.facility_id === synthetic.facility_id)
                  ? prev
                  : [...prev, synthetic as Facility],
              );
              navRequestedTabRef.current = resolvePostPurchaseNavigationActiveTab(
                navRequestedTabRef.current,
                postPurchaseTarget,
              );
              setComplianceData(null);
            } else {
              postPurchaseTargetRef.current = null;
              postPurchaseRetryAttemptRef.current = 0;
              setPostPurchaseResolving(false);
              clearPostPurchaseFacilitySelection();
              initialNavFacilityIdRef.current = undefined;
              initialNavFacilityNameRef.current = undefined;
              initialNavFacilityCityRef.current = undefined;
              initialNavSelectedFacilityRef.current = undefined;
            }
          }
        }

        if (data.length > 0 && !postPurchaseResolved && !postPurchasePending) {
          let resolved = false;
          facilityToSelect = data[0];

          const navFacilityId = initialNavFacilityIdRef.current;
          const navFacilityName = initialNavFacilityNameRef.current;
          const navFacilityCity = initialNavFacilityCityRef.current;
          if (navFacilityId != null || navFacilityName || navSelectedFacility) {
            let navigationFacility =
              navFacilityId != null
                ? data.find((f: Facility) => f.facility_id === navFacilityId)
                : undefined;

            if (!navigationFacility && navFacilityName) {
              navigationFacility = data.find(
                (f: Facility) =>
                  f.facility_name?.toLowerCase() ===
                    navFacilityName.toLowerCase() &&
                  (!navFacilityCity ||
                    f.city?.toLowerCase() === navFacilityCity.toLowerCase()),
              );
            }

            if (navigationFacility) {
              facilityToSelect = navigationFacility;
              resolved = true;
              setComplianceData(null);
            } else if (navSelectedFacility) {
              facilityToSelect = navSelectedFacility;
              resolved = true;
              setFacilities((prev) =>
                prev.some(
                  (f) => f.facility_id === navSelectedFacility.facility_id,
                )
                  ? prev
                  : [...prev, navSelectedFacility],
              );
              setComplianceData(null);
            }

            initialNavFacilityIdRef.current = undefined;
            initialNavFacilityNameRef.current = undefined;
            initialNavFacilityCityRef.current = undefined;
            initialNavSelectedFacilityRef.current = undefined;
          }

          if (!resolved && !postPurchasePending) {
            const storedFacility = sessionStorage.getItem(
              "filtered_selectedFacility",
            );

            if (storedFacility) {
              try {
                const parsedStoredFacility = JSON.parse(storedFacility);

                let matchingFacility = data.find(
                  (facility: Facility) =>
                    facility.facility_id ===
                      parsedStoredFacility?.facility_id ||
                    facility.facility_id === parsedStoredFacility?.facilityId,
                );

                if (!matchingFacility) {
                  matchingFacility = data.find(
                    (facility: Facility) =>
                      (facility.facility_name ===
                        parsedStoredFacility?.facility_name ||
                        facility.facility_name ===
                          parsedStoredFacility?.site_name) &&
                      facility.city === parsedStoredFacility?.city &&
                      (parsedStoredFacility?.facility_name ||
                        parsedStoredFacility?.site_name) &&
                      parsedStoredFacility?.city,
                  );
                }

                if (matchingFacility) {
                  facilityToSelect = matchingFacility;
                }
              } catch {
                // Ignore parsing errors
              }
            }
          }
        } else if (!postPurchasePending) {
          setSelectedFacility(null);
          setComplianceData(null);
          try {
            sessionStorage.removeItem("filtered_selectedFacility");
          } catch {
            /* ignore */
          }
        }

        if (facilityToSelect) {
          setSelectedFacility(facilityToSelect);
          setPermissionCheckedFacilityId(null);
          sessionStorage.setItem(
            "filtered_selectedFacility",
            JSON.stringify(facilityToSelect),
          );
          if (
            postPurchasePending &&
            postPurchaseTarget &&
            postPurchaseFacilityMatchesTarget(
              facilityToSelect,
              postPurchaseTarget,
            )
          ) {
            postPurchaseTargetRef.current = null;
            postPurchaseRetryAttemptRef.current = 0;
            setPostPurchaseResolving(false);
            clearPostPurchaseFacilitySelection();
            initialNavFacilityIdRef.current = undefined;
            initialNavFacilityNameRef.current = undefined;
            initialNavFacilityCityRef.current = undefined;
            initialNavSelectedFacilityRef.current = undefined;
          }
        } else if (postPurchasePending) {
          setSelectedFacility(null);
          setComplianceData(null);
        }
      } catch {
        if (
          !cancelled &&
          hasPostPurchaseFacilityTarget(postPurchaseTargetRef.current) &&
          shouldRetryPostPurchaseFacilityFetch(
            false,
            postPurchaseRetryAttemptRef.current,
          )
        ) {
          postPurchaseRetryAttemptRef.current += 1;
          schedulePostPurchaseRetry();
          return;
        }
      } finally {
        if (!cancelled) {
          setFacilitiesLoading(false);
          if (!hasPostPurchaseFacilityTarget(postPurchaseTargetRef.current)) {
            setPostPurchaseResolving(false);
          }
        }
      }
    };

    void loadFacilities();

    return () => {
      cancelled = true;
      if (postPurchaseRetryTimerRef.current != null) {
        clearTimeout(postPurchaseRetryTimerRef.current);
        postPurchaseRetryTimerRef.current = null;
      }
    };
  // navigationState?.facilityId is intentionally omitted from deps: it is
  // captured once into initialNavFacilityIdRef on mount and consumed there.
  // Keeping it in deps would cause a redundant re-fetch when the clear-state
  // effect removes it from history, and would also re-apply stale nav state
  // on every country change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, activeVertical]);

  // Watch for country changes and update facilities dropdown
  useEffect(() => {
    const checkCountryChange = () => {
      const current = getSelectedCountryCode();
      if (current !== selectedCountry) {
        setSelectedCountry(current);
      }
    };
    const onAppCountryChanged = (e: Event) => {
      const code = (e as CustomEvent<{ countryCode?: string }>).detail
        ?.countryCode;
      if (code && typeof code === "string") {
        setSelectedCountry(code);
        setSelectedFacility(null);
        try {
          sessionStorage.removeItem("filtered_selectedFacility");
        } catch {
          /* ignore */
        }
        setComplianceData(null);
        setPermissionCheckedFacilityId(null);
      }
    };
    checkCountryChange();
    const intervalId = setInterval(checkCountryChange, 500);
    window.addEventListener("storage", checkCountryChange);
    window.addEventListener("appCountryChanged", onAppCountryChanged);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("storage", checkCountryChange);
      window.removeEventListener("appCountryChanged", onAppCountryChanged);
    };
  }, [selectedCountry]);

  // Leftover unpaid (Mackay DBA) reopens Select Plan. Hydrate admin promo
  // by leftover NPI before wall vs Select Plan. Promo Pay Now clears
  // the wall so leftover stays selected. True expiry → wall.
  useEffect(() => {
    if (!selectedFacility) return;
    let cancelled = false;
    const routeLeftoverUnpaid = async () => {
      let facility = selectedFacility;
      let unpaidAction = resolveUnpaidAccessAction(facility);
      if (unpaidAction === "active" || unpaidAction === "none") return;
      if (unpaidAction === "restricted" || unpaidAction === "select-plan") {
        facility = await fetchAndMergeAdminPromoForLeftover(facility);
        if (cancelled) return;
        unpaidAction = resolveUnpaidAccessAction(facility);
      }
      if (unpaidAction === "select-plan") {
        persistLeftoverUnpaidIdentity(facility);
        navigateToSelectPlanForUnpaidFacility(navigate, facility);
        return;
      }
      if (unpaidAction === "restricted") {
        navigateToRestrictedFacility(navigate, facility, facilities.length > 1);
      }
    };
    void routeLeftoverUnpaid();
    return () => {
      cancelled = true;
    };
  }, [selectedFacility, navigate, facilities.length]);
  // Pull the user's vertical context once on mount so we have a fallback when
  // no preview vertical is set. The vertical context endpoint is cheap and
  // tolerant of failures (preview users use the preview slug exclusively anyway),
  // so we silently swallow errors.
  useEffect(() => {
    let cancelled = false;
    getMyVerticalContext()
      .then((ctx) => {
        if (!cancelled) setVerticalContext(ctx);
      })
      .catch(() => {
        // Non-fatal — falls back to "healthcare" below.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // VerticalPreviewSwitcher / setLandingVerticalSession dispatch
  // `unicomply:preview-vertical-changed`; mirror into local state so the
  // visible-facilities filter and auto-select effect re-run immediately.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ slug: string | null }>).detail;
      // Prefer event slug when set; on clear, re-read session (aviation landing
      // may still pin the override for non-exec users).
      const next =
        detail?.slug ?? getRequestVerticalOverride() ?? null;
      setPreviewVerticalSlug(next);
    };
    window.addEventListener(
      PREVIEW_VERTICAL_CHANGED_EVENT,
      handler as EventListener,
    );
    return () => {
      window.removeEventListener(
        PREVIEW_VERTICAL_CHANGED_EVENT,
        handler as EventListener,
      );
    };
  }, []);

  /**
   * Facilities visible in the Selected-Facility dropdown — only those
   * matching the active vertical. We compare against the facility's own
   * `vertical` field, falling back to "healthcare" for any legacy row the
   * server hasn't yet stamped (the new server default makes this rare).
   */
  const visibleFacilities = useMemo<Facility[]>(() => {
    if (!facilities.length) return facilities;
    return facilities.filter(
      (f) => (f.vertical || "healthcare") === activeVertical && !f.is_network,
    );
  }, [facilities, activeVertical]);
  const groupedVisibleFacilityOptions = useMemo(
    () => buildGroupedFacilityOptions(visibleFacilities),
    [visibleFacilities],
  );
  const hasAffableConnection = useCallback((facility?: Facility | null) => {
    const value: unknown = (facility as { is_affable_connected?: unknown } | null | undefined)
      ?.is_affable_connected;
    if (value === true || value === 1 || value === "1") return true;
    if (typeof value === "string") {
      return value.trim().toLowerCase() === "true";
    }
    return false;
  }, []);

  /**
   * When the active vertical changes (or facilities load), make sure the
   * currently-selected facility still belongs to that vertical. If it does,
   * leave the user's pick alone. If it doesn't, auto-select the first
   * matching facility so the rest of the page (compliance fetch, profile
   * tab, etc.) renders against the right vertical without an extra click.
   */
  useEffect(() => {
    if (postPurchaseResolving) {
      return;
    }
    const wantCountry = getSelectedCountryCode().trim().toUpperCase();
    const current = selectedFacility;
    if (current && !userFacilityMatchesSelectedCountryCode(current, wantCountry)) {
      setSelectedFacility(null);
      sessionStorage.removeItem("filtered_selectedFacility");
      setComplianceData(null);
      setPermissionCheckedFacilityId(null);
      return;
    }
    if (!facilities.length) {
      if (shouldClearSelectionForEmptyFacilities(!!current, facilitiesLoading)) {
        setSelectedFacility(null);
        sessionStorage.removeItem("filtered_selectedFacility");
        setComplianceData(null);
        setPermissionCheckedFacilityId(null);
      }
      return;
    }
    const currentMatches =
      current && (current.vertical || "healthcare") === activeVertical;
    if (currentMatches) return;
    const next = visibleFacilities[0] || null;
    if (!next) {
      // No facility for this vertical — VerticalPreviewSwitcher handles the
      // navigation to FacilityValidationScreen; here we just clear so the
      // header doesn't show stale data from the previous vertical.
      setSelectedFacility(null);
      sessionStorage.removeItem("filtered_selectedFacility");
      setComplianceData(null);
      return;
    }
    setSelectedFacility(next);
    sessionStorage.setItem("filtered_selectedFacility", JSON.stringify(next));
    // Force compliance to refetch under the new facility.
    setComplianceData(null);
    setPermissionCheckedFacilityId(null);
  }, [
    activeVertical,
    facilities,
    visibleFacilities,
    selectedFacility,
    selectedCountry,
    postPurchaseResolving,
    facilitiesLoading,
  ]);

  const handleFacilityChange = (event: SelectChangeEvent<number | "">) => {
    const raw = event.target.value;
    if (raw === "") {
      navRequestedTabRef.current = undefined;
      setSelectedFacility(null);
      setPermissionCheckedFacilityId(null);
      sessionStorage.removeItem("filtered_selectedFacility");
      setComplianceData(null);
      setServerMockFiles([]);
      return;
    }
    const facilityId = typeof raw === "number" ? raw : Number(raw);
    const cached =
      groupedVisibleFacilityOptions.find(
        ({ facility }) => getFacilityHierarchyId(facility) === facilityId,
      )?.facility ||
      facilities.find((f) => getFacilityHierarchyId(f) === facilityId);

    const normalized =
      cached && getFacilityHierarchyId(cached) != null
        ? {
            ...cached,
            facility_id: getFacilityHierarchyId(cached)!,
          }
        : cached;

    // Manual dropdown change — clear any pending navigation tab intent so
    // auto-tab logic applies normally for this selection.
    navRequestedTabRef.current = undefined;

    // Apply cached data immediately for a responsive feel
    setSelectedFacility(normalized || null);
    setPermissionCheckedFacilityId(null);
    if (normalized) {
      sessionStorage.setItem("filtered_selectedFacility", JSON.stringify(normalized));
    } else {
      sessionStorage.removeItem("filtered_selectedFacility");
    }

    // Clear existing compliance data
    setComplianceData(null);
    complianceFetchGenerationRef.current += 1;
    complianceStreamAbortRef.current?.abort();
    penaltiesStreamAbortRef.current?.abort();
    complianceFetchInFlightRef.current = null;
    penaltiesFetchInFlightRef.current = null;
    penaltiesFetchGenerationRef.current += 1;
    emptyPenaltiesResponsesSeenRef.current = 0;
    lastFetchedFacilityIdRef.current = null;
    penaltyProgressSessionFacilityIdRef.current = null;
    setPenaltyExposureProgressSteps([]);
    // Clear server mock files so stale files from previous facility aren't shown
    setServerMockFiles([]);
    // Don't set loading to true here - wait for permissions to be checked first

    // Re-fetch the full facility list in the background so the profile-setup gate
    // always sees current DB values, not a potentially stale in-memory snapshot.
    // This matters when DB fields are changed externally after the initial page load.
    ensureMyFacilitiesLoaded({ bypassCache: true, vertical: activeVertical }).then(async (rawFresh) => {
      const freshList: Facility[] = rawFresh as Facility[];
      setFacilities(freshList);
      const fresh =
        buildGroupedFacilityOptions(freshList).find(
          ({ facility }) => getFacilityHierarchyId(facility) === facilityId,
        )?.facility ||
        freshList.find((f) => getFacilityHierarchyId(f) === facilityId);
      if (fresh && getFacilityHierarchyId(fresh) != null) {
        const freshNorm = {
          ...fresh,
          facility_id: getFacilityHierarchyId(fresh)!,
        };
        setSelectedFacility(freshNorm);
        sessionStorage.setItem("filtered_selectedFacility", JSON.stringify(freshNorm));
      }
    }).catch(() => {
      // Keep cached data if the refresh fails — non-critical
    });
  };

  // React to facility selection from the left-panel navigation when the
  // component is ALREADY MOUNTED on the same route (/dashboard/facilities).
  // In that case React Router updates location.state without remounting, so
  // the mount-time refs (initialNavFacilityIdRef etc.) never capture the new
  // facilityId. This effect watches the state and applies the selection live.
  useEffect(() => {
    const navFacilityId = navigationState?.facilityId;
    const navFacilityName = navigationState?.facilityName;
    const navFacilityCity = navigationState?.facilityCity;
    const navActiveTab = navigationState?.activeTab;
    const navSelectedFacility = normalizeNavigationFacility(
      navigationState?.selectedFacility,
    );

    if (!navFacilityId && !navFacilityName && !navSelectedFacility) return;

    // If the facility list hasn't loaded yet the mount-time fetchFacilities
    // handler will take care of selection via the navigation refs.
    if (facilities.length === 0) return;

    // Resolve the target facility — try ID first, then name + city fallback.
    let facilityToSelect: Facility | undefined = navFacilityId != null
      ? facilities.find((f) => f.facility_id === navFacilityId)
      : undefined;

    if (!facilityToSelect && navFacilityName) {
      facilityToSelect = facilities.find(
        (f) =>
          f.facility_name?.toLowerCase() === navFacilityName.toLowerCase() &&
          (!navFacilityCity ||
            f.city?.toLowerCase() === navFacilityCity.toLowerCase()),
      );
    }
    if (!facilityToSelect && navSelectedFacility) {
      facilityToSelect = navSelectedFacility;
      setFacilities((prev) =>
        prev.some((f) => f.facility_id === navSelectedFacility.facility_id)
          ? prev
          : [...prev, navSelectedFacility],
      );
    }

    if (facilityToSelect) {
      // Set the navigation tab intent BEFORE triggering setSelectedFacility so
      // the auto-tab useEffect reads it when selectedFacility changes.
      navRequestedTabRef.current = navActiveTab ?? undefined;
      setSelectedFacility(facilityToSelect);
      setPermissionCheckedFacilityId(null);
      setComplianceData(null);
      setServerMockFiles([]);
      sessionStorage.setItem(
        "filtered_selectedFacility",
        JSON.stringify(facilityToSelect),
      );
    }

    // Clear transient navigation fields from history so F5 doesn't reapply.
    navigate(location.pathname, {
      replace: true,
      state: {
        ...navigationState,
        facilityId: undefined,
        facilityName: undefined,
        facilityCity: undefined,
        selectedFacility: undefined,
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState?.facilityId, navigationState?.facilityName, navigationState?.selectedFacility]);

  // Helper to get base (global) permissions from the stored user object
  const getBasePermissions = React.useCallback((): Permission[] => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr) as any;
        const basePerms = user?.permissions;
        if (basePerms && Array.isArray(basePerms) && basePerms.length > 0) {
          return basePerms as Permission[];
        }
      }
    } catch (err) {
      console.warn("⚠️ [MyFacility] Failed to read base permissions", err);
    }
    return [];
  }, []);

  // Helper to restore base (global) permissions from the stored user object
  const restoreBasePermissions = React.useCallback(() => {
    try {
      const basePerms = getBasePermissions();
      if (basePerms.length > 0) {
        localStorage.setItem(
          "user_permissions",
          JSON.stringify(normalizePermissionList(basePerms as unknown[])),
        );
        window.dispatchEvent(new CustomEvent("userPermissionsUpdated"));
        console.log(
          "ℹ️ [MyFacility] Restored base permissions from user object",
        );
        return;
      }
    } catch (err) {
      console.warn("⚠️ [MyFacility] Failed to restore base permissions", err);
    }
  }, [getBasePermissions]);

  const isGlobalAdmin = isGlobalAdministrator();

  // Fetch facility-specific permissions when facility is selected
  useEffect(() => {
    if (
      !shouldFetchFacilityPermissions({
        selectedFacilityId: selectedFacility?.facility_id,
        checkedFacilityId: permissionCheckedFacilityId,
        permissionsLoading,
      })
    ) {
      return;
    }
    const fetchFacilityPermissions = async () => {
      if (!selectedFacility?.facility_id || selectedFacility.is_network) {
        setFacilityPermissions([]);
        setPermissionsLoading(false);
        setPermissionCheckedFacilityId(selectedFacility?.facility_id ?? null);
        setPermissionsResponseEmpty(false);
        restoreBasePermissions();
        return;
      }

      try {
        setPermissionsLoading(true);
        const userId = getCurrentUserId();
        if (!userId || userId === "0") {
          console.warn(
            "⚠️ [MyFacility] Cannot fetch permissions: user not authenticated",
          );
          setPermissionCheckedFacilityId(selectedFacility.facility_id);
          setPermissionsLoading(false);
          return;
        }

        const permissionsResponse = await fetchUserPermissions(
          userId,
          selectedFacility.facility_id,
        );

        const permissions =
          (permissionsResponse as any)?.result?.data ||
          permissionsResponse?.permissions ||
          (permissionsResponse as any)?.items ||
          (permissionsResponse as any)?.results ||
          [];

        // Check if response indicates successful retrieval with empty data
        const resultData = (permissionsResponse as any)?.result?.data;
        const resultTotal = (permissionsResponse as any)?.result?.total;
        const isSuccess = (permissionsResponse as any)?.success === true;
        const isEmptyResponse =
          isSuccess &&
          Array.isArray(resultData) &&
          resultData.length === 0 &&
          resultTotal === 0;

        setPermissionsResponseEmpty(isEmptyResponse);
        const basePerms = getBasePermissions();
        const merged = mergePermissionsByFeatureName(
          basePerms,
          permissions as unknown[],
        );
        setFacilityPermissions(merged);

        // Update localStorage with facility-specific permissions
        if (merged.length > 0) {
          localStorage.setItem("user_permissions", JSON.stringify(merged));
          window.dispatchEvent(new CustomEvent("userPermissionsUpdated"));
          console.log(
            "✅ [MyFacility] Facility permissions loaded and merged with base:",
            merged,
          );
        } else {
          // If facility-specific permissions are empty, fall back to base permissions
          console.log(
            "⚠️ [MyFacility] No permissions found for this facility. Restoring base permissions.",
          );
          restoreBasePermissions();
        }
        setPermissionCheckedFacilityId(selectedFacility.facility_id);
      } catch (error) {
        console.error(
          "❌ [MyFacility] Failed to fetch facility permissions:",
          error,
        );
        setPermissionCheckedFacilityId(selectedFacility.facility_id);
        setPermissionsResponseEmpty(false);
        // Don't clear permissions on error, keep existing ones
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchFacilityPermissions();
    // permissionCheckedFacilityId: same-facility reselects clear the check without
    // changing facility_id, so the fetch must rerun or compliance never loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFacility?.facility_id,
    getBasePermissions,
    restoreBasePermissions,
    permissionCheckedFacilityId,
    permissionsLoading,
  ]);

  // Helper function to check if a feature tab should be visible by feature name
  const canViewFeatureByName = useCallback(
    (featureName: string): boolean => {
      return canViewFacilityFeature(
        featureName,
        facilityPermissions,
        canViewByName(featureName),
        isGlobalAdmin,
      );
    },
    [facilityPermissions, isGlobalAdmin],
  );

  // Helper function to check if user can delete for Mock Survey feature
  const canDeleteMockSurvey = (): boolean => {
    if (facilityPermissions.length === 0) {
      // If no facility permissions loaded yet, fall back to general permissions check
      return canDeleteByName(FEATURE_NAMES.MOCK_SURVEY);
    }
    const permission = facilityPermissions.find(
      (p) =>
        p.feature_name?.toLowerCase() ===
        FEATURE_NAMES.MOCK_SURVEY.toLowerCase(),
    );
    return permission ? coercePermissionBool(permission.delete) : false;
  };

  // Helper function to check if user can add/upload for Mock Survey feature
  const canAddMockSurvey = (): boolean => {
    if (facilityPermissions.length === 0) {
      // If no facility permissions loaded yet, fall back to general permissions check
      return canAddByName(FEATURE_NAMES.MOCK_SURVEY);
    }
    const permission = facilityPermissions.find(
      (p) =>
        p.feature_name?.toLowerCase() ===
        FEATURE_NAMES.MOCK_SURVEY.toLowerCase(),
    );
    return permission ? coercePermissionBool(permission.add) : false;
  };

  const canUpdateMockSurvey = useMemo(
    () => canUpdateMockSurveyFromPermissions(facilityPermissions),
    [facilityPermissions],
  );

  // Facility Profile permission check
  const canViewFacilityProfileTab = useMemo(
    () => canViewFeatureByName(FACILITY_PROFILE_FEATURE_NAME),
    [canViewFeatureByName],
  );

  const canUpdateFacilityProfileTab = useMemo(
    () =>
      canUpdateFeatureFromPermissions(
        facilityPermissions,
        FACILITY_PROFILE_FEATURE_NAME,
      ),
    [facilityPermissions],
  );

  const canViewDashboardTab = useMemo(
    () => canViewFeatureByName(FEATURE_NAMES.DASHBOARD),
    [canViewFeatureByName],
  );

  type MainNavTabId =
    | "dashboard"
    | "facilityProfile"
    | "facilities"
    | "oig"
    | "penalty"
    | "compliance"
    | "riskanalysis"
    | "surveyorvisit";

  const mainNavTabs = useMemo(() => {
    const tabs: { id: MainNavTabId; label: string }[] = [];
    if (canViewDashboardTab) {
      tabs.push({ id: "dashboard", label: "Individual facility dashboard" });
    }
    if (canViewFacilityProfileTab) {
      tabs.push({ id: "facilityProfile", label: "Profile" });
    }
    if (canViewFeatureByName(FEATURE_NAMES.FACILITY_ANALYSIS)) {
      tabs.push({ id: "facilities", label: "Analysis" });
    }
    if (canViewFeatureByName(FEATURE_NAMES.POTENTIAL_PENALTIES)) {
      tabs.push({ id: "penalty", label: "Potential Penalties" });
    }
    if (canViewFeatureByName(FEATURE_NAMES.EXCLUSION_SANCTION_CHECK)) {
      tabs.push({ id: "oig", label: "Exclusion & Sanction Check" });
    }
    if (canViewFeatureByName(FEATURE_NAMES.CONTINUOUS_COMPLIANCE)) {
      tabs.push({ id: "compliance", label: "Continuous Compliance" });
    }
    if (canViewFeatureByName(FEATURE_NAMES.RISK_ANALYSIS)) {
      tabs.push({ id: "riskanalysis", label: "Risk Analysis" });
    }
    if (canViewFeatureByName(FEATURE_NAMES.MOCK_SURVEY)) {
      tabs.push({ id: "surveyorvisit", label: "Mock Survey" });
    }
    return tabs;
  }, [canViewDashboardTab, canViewFacilityProfileTab, canViewFeatureByName]);

  /** Payor Contract Analysis watch guide (tab merged into Risk Analysis). */
  useEffect(() => {
    if (activeTab !== "riskanalysis") return;
    if (!canViewFeatureByName(FEATURE_NAMES.PAYOR_CONTRACT_ANALYSIS)) return;
    if (watchGuideAvailability.payorcontract !== undefined) return;
    const preferredCategory = WATCH_GUIDE_CATEGORY_BY_TAB.payorcontract;
    if (!preferredCategory) return;
    let cancelled = false;
    const checkWatchGuideAvailability = async () => {
      try {
        const response = await getTrainingVideos({
          category: preferredCategory,
          is_active: true,
          page: 1,
          page_size: 100,
        });
        const hasPlayable = response.items.some(
          (item) => item.is_active && !item.is_delete && !!item.storage_link,
        );
        if (!cancelled) {
          setWatchGuideAvailability((prev) => ({
            ...prev,
            payorcontract: hasPlayable,
          }));
        }
      } catch {
        if (!cancelled) {
          setWatchGuideAvailability((prev) => ({
            ...prev,
            payorcontract: false,
          }));
        }
      }
    };
    void checkWatchGuideAvailability();
    return () => {
      cancelled = true;
    };
    // Intentionally omit `watchGuideAvailability.payorcontract`: listing it re-ran this
    // effect whenever any guide slot changed and could amplify network calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payor slot read only for guard
  }, [activeTab, canViewFeatureByName]);

  const mainTabIndex = useMemo(() => {
    const i = mainNavTabs.findIndex((t) => t.id === activeTab);
    return i >= 0 ? i : false;
  }, [mainNavTabs, activeTab]);

  // Switch to first available tab if current tab is not accessible
  useEffect(() => {
    if (isGlobalAdmin) return;
    if (facilityPermissions.length === 0) return; // Wait for permissions to load

    const checkPermission = (featureName: string): boolean => {
      const permission = facilityPermissions.find(
        (p) => p.feature_name?.toLowerCase() === featureName.toLowerCase(),
      );
      return permission ? coercePermissionBool(permission.view) : false;
    };

    if (activeTab === "dashboard" && !checkPermission(FEATURE_NAMES.DASHBOARD)) {
      const availableTabs = Object.keys(tabToFeatureName).filter((tab) =>
        checkPermission(tabToFeatureName[tab]),
      );
      if (availableTabs.length > 0) {
        setActiveTab(availableTabs[0] as typeof activeTab);
      } else if (canViewFacilityProfileTab) {
        setActiveTab("facilityProfile");
      }
      return;
    }

    if (activeTab === "dashboard") return;
    const currentFeatureName = tabToFeatureName[activeTab];
    if (currentFeatureName && !checkPermission(currentFeatureName)) {
      const availableTabs = Object.keys(tabToFeatureName).filter((tab) =>
        checkPermission(tabToFeatureName[tab]),
      );
      if (availableTabs.length > 0) {
        setActiveTab(availableTabs[0] as typeof activeTab);
      } else if (canViewDashboardTab) {
        setActiveTab("dashboard");
      } else if (canViewFacilityProfileTab) {
        setActiveTab("facilityProfile");
      }
    }
  }, [
    facilityPermissions,
    activeTab,
    isGlobalAdmin,
    canViewDashboardTab,
    canViewFacilityProfileTab,
  ]);

  // Ensure Facility Profile tab/content is inaccessible without view permission
  useEffect(() => {
    if (activeTab !== "facilityProfile") return;
    if (canViewFacilityProfileTab) return;

    const fallbackTabs: Array<
      "facilities" | "penalty" | "oig" | "compliance" | "riskanalysis" | "surveyorvisit"
    > = [
      "facilities",
      "penalty",
      "oig",
      "compliance",
      "riskanalysis",
      "surveyorvisit",
    ];

    const nextAllowed = fallbackTabs.find((tab) =>
      canViewFeatureByName(tabToFeatureName[tab]),
    );

    if (nextAllowed) {
      setActiveTab(nextAllowed);
    } else if (canViewDashboardTab) {
      setActiveTab("dashboard");
    }
  }, [activeTab, canViewDashboardTab, canViewFacilityProfileTab, canViewFeatureByName]);

  // Ensure Dashboard tab/content is inaccessible without view permission
  useEffect(() => {
    if (activeTab !== "dashboard") return;
    if (canViewDashboardTab) return;

    const nextAllowed = mainNavTabs[0]?.id;
    if (nextAllowed && nextAllowed !== "dashboard") {
      setActiveTab(nextAllowed);
    }
  }, [activeTab, canViewDashboardTab, mainNavTabs]);

  // Memoize facility data to prevent continuous re-parsing
  const [filtered_selectedFacility, setFilteredSelectedFacility] =
    useState<Facility | null>(() => {
      const stored = sessionStorage.getItem("filtered_selectedFacility");
      try {
        return stored ? (JSON.parse(stored) as Facility) : null;
      } catch (err) {
        console.error("Invalid JSON in sessionStorage:", stored, err);
        return null;
      }
    });

  // Watch for session storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = sessionStorage.getItem("filtered_selectedFacility");
      try {
        const newData = stored ? JSON.parse(stored) : null;
        setFilteredSelectedFacility(newData);
        console.log("📦 Session storage updated, new facility data:", newData);
      } catch (err) {
        console.error("Invalid JSON in sessionStorage:", stored, err);
        setFilteredSelectedFacility(null);
      }
    };

    // Listen for storage events (for cross-tab communication)
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [filtered_selectedFacility, navigate]);

  // Check if user has any view permissions for facility tabs
  const hasAnyViewPermission = useMemo(() => {
    if (canViewDashboardTab || canViewFacilityProfileTab) {
      return true;
    }
    const facilityFeatureNames = [
      FEATURE_NAMES.FACILITY_ANALYSIS,
      FEATURE_NAMES.POTENTIAL_PENALTIES,
      FEATURE_NAMES.EXCLUSION_SANCTION_CHECK,
      FEATURE_NAMES.CONTINUOUS_COMPLIANCE,
      FEATURE_NAMES.RISK_ANALYSIS,
      FEATURE_NAMES.PAYOR_CONTRACT_ANALYSIS,
      FEATURE_NAMES.MOCK_SURVEY,
    ];
    return facilityFeatureNames.some((featureName) =>
      canViewFeatureByName(featureName),
    );
  }, [canViewDashboardTab, canViewFacilityProfileTab, canViewFeatureByName]);

  // Derived booleans extracted here so they can be used as static dep-array entries.
  const hasAccreditingBody = !!selectedFacility?.accrediting_body;
  const hasMedicareCertificationDate = !!selectedFacility?.medicare_certification_date;

  // Route to the correct tab whenever the selected facility changes.
  // is_profile_approved is the authoritative flag — it is stamped directly on the
  // facility object (and persisted to sessionStorage) so it survives both facility
  // switches within this mount and full component remounts.
  useEffect(() => {
    if (!selectedFacility) return;

    // If user is returning from Survey Viewer, honor Mock Survey tab once and
    // skip default auto-routing to Facility Analysis for this selection cycle.
    if (returnToMockSurveyRef.current) {
      setActiveTab("surveyorvisit");
      setMockSurveyAccordionExpanded(true);
      returnToMockSurveyRef.current = false;
      sessionStorage.removeItem(MOCK_SURVEY_RETURN_FLAG_KEY);
      return;
    }

    // When the user clicked a facility from the left panel, honor the navigation
    // intent (e.g. "facilities" / Facility Analysis) regardless of profile state.
    // The ref is cleared after first use so subsequent in-page facility switches
    // resume the normal auto-tab logic.
    if (navRequestedTabRef.current) {
      let requestedTab = navRequestedTabRef.current as
        | "dashboard"
        | "facilityProfile"
        | "facilities"
        | "oig"
        | "penalty"
        | "compliance"
        | "riskanalysis"
        | "payorcontract"
        | "surveyorvisit";
      navRequestedTabRef.current = undefined;
      if (requestedTab === "payorcontract") {
        requestedTab = "riskanalysis";
      }
      setActiveTab(
        requestedTab === "facilityProfile" && !selectedFacility.is_profile_approved
          ? canViewDashboardTab
            ? "dashboard"
            : (mainNavTabs[0]?.id ?? "facilityProfile")
          : requestedTab,
      );
      return;
    }

    setActiveTab(mainNavTabs[0]?.id ?? "dashboard");
  // Deliberately use primitive field deps rather than the full selectedFacility object
  // to avoid re-running on every background refresh that doesn't change these values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFacility?.facility_id,
    selectedFacility?.is_profile_approved,
    hasAccreditingBody,
    hasMedicareCertificationDate,
    canViewDashboardTab,
    mainNavTabs,
  ]);

  // Fetch compliance data when facility data is available
  // Use a ref to track the current request and prevent stale responses from overwriting
  const currentRequestFacilityIdRef = useRef<number | null>(null);
  const complianceStreamInitFacilityRef = useRef<number | null>(null);

  const notifyComplianceStreamInit = useCallback((facilityId: number) => {
    if (complianceStreamInitFacilityRef.current === facilityId) {
      return;
    }
    complianceStreamInitFacilityRef.current = facilityId;
    setComplianceStreamInitSignal((prev) => prev + 1);
  }, []);

  useEffect(() => {
    complianceStreamInitFacilityRef.current = null;
    setComplianceStreamInitSignal(0);
  }, [selectedFacility?.facility_id]);

  const buildFacilityCompliancePayload = useCallback(
    (
      facility: Facility,
      facilityData: Facility | null | undefined,
      includePenalties: boolean,
    ): FacilityCompliancePayload => {
      const currentFacilityData = facilityData ?? facility;
      const state = currentFacilityData?.state
        ? String(currentFacilityData.state).trim()
        : "";
      const city = currentFacilityData?.city
        ? String(currentFacilityData.city).trim()
        : "";
      const userId = parseInt(getCurrentUserId(), 10) || 0;
      const profileTargeting =
        currentFacilityData?.profile_targeting_json ||
        currentFacilityData?.aviation_targeting ||
        null;
      return {
        user_id: userId,
        organization: currentFacilityData?.facility_name || "",
        facility_name: currentFacilityData?.facility_name || "",
        facility_type:
          currentFacilityData?.primary_profile_slug ||
          currentFacilityData?.facility_type_slug ||
          currentFacilityData?.facility_type ||
          "",
        country: getSelectedCountryCode(),
        state,
        county: "",
        city,
        site_url: "",
        documents: "",
        as_of_date: new Date().toISOString().split("T")[0],
        response_language: "English",
        instructions:
          "Search government regulatory sites for compliance requirements, permits, licenses, and medical facility documentation",
        output_format: "JSON",
        org_name: currentFacilityData.org_name || "test",
        last_medicare_revalidation_date:
          currentFacilityData?.last_medicare_revalidation_date ?? undefined,
        last_medicaid_revalidation_date:
          currentFacilityData?.last_medicaid_revalidation_date ?? undefined,
        facility_id: facility?.facility_id ?? undefined,
        facility_type_id:
          currentFacilityData?.vertical === "aviation"
            ? undefined
            : (currentFacilityData as { facility_type_id?: number })
                ?.facility_type_id ??
              (
                currentFacilityData as {
                  facility_details?: { facility_type_id?: number };
                }
              )?.facility_details?.facility_type_id ??
              undefined,
        vertical: currentFacilityData?.vertical ?? undefined,
        primary_profile_slug:
          currentFacilityData?.primary_profile_slug ?? undefined,
        profile_targeting_json: profileTargeting,
        activity_domains:
          currentFacilityData?.activity_domains ??
          (profileTargeting?.activity_domains as string[] | undefined),
        operation_contexts:
          currentFacilityData?.operation_contexts ??
          (profileTargeting?.operation_contexts as string[] | undefined),
        asset_types:
          currentFacilityData?.asset_types ??
          (profileTargeting?.asset_types as string[] | undefined),
        certificates_or_approvals:
          currentFacilityData?.certificates_or_approvals ??
          (profileTargeting?.certificates_or_approvals as string[] | undefined),
        regulation_packs:
          currentFacilityData?.regulation_packs ??
          (profileTargeting?.regulation_packs as string[] | undefined),
        overlay_packs:
          currentFacilityData?.overlay_packs ??
          (profileTargeting?.overlay_packs as string[] | undefined),
        penalty_packs:
          currentFacilityData?.penalty_packs ??
          (profileTargeting?.penalty_packs as string[] | undefined),
        include_penalties: includePenalties,
        ...(() => {
          const v = (
            currentFacilityData as { show_tax_compliance_calendar?: unknown }
          )?.show_tax_compliance_calendar;
          if (v === true || v === false) {
            return { show_tax_compliance_calendar: v };
          }
          return {};
        })(),
      };
    },
    [],
  );

  useEffect(() => {
    permissionGateRef.current = {
      loading: permissionsLoading,
      checkedFacilityId: permissionCheckedFacilityId,
      hasAnyView: hasAnyViewPermission,
    };
  }, [permissionsLoading, permissionCheckedFacilityId, hasAnyViewPermission]);

  // Show penalty exposure checklist immediately on dashboard (before BFF / compliance start).
  useEffect(() => {
    if (
      !shouldSeedPenaltyExposureProgressOnDashboard({
        activeTab,
        facilityId: selectedFacility?.facility_id,
        postPurchaseResolving,
        progressSessionFacilityId: penaltyProgressSessionFacilityIdRef.current,
      }) ||
      !selectedFacility
    ) {
      return;
    }
    setPenaltyExposureProgressSteps(
      buildInitialPenaltyExposureProgressSteps(
        buildPenaltyExposureProgressContext({
          countryCode:
            selectedFacility.country_code || getSelectedCountryCode() || "USA",
          vertical: selectedFacility.vertical || activeVertical,
          facilityTypeSlug: selectedFacility.facility_type_slug,
          facilityType: selectedFacility.facility_type,
        }),
      ),
    );
    penaltyProgressSessionFacilityIdRef.current = selectedFacility.facility_id;
  }, [
    activeTab,
    activeVertical,
    postPurchaseResolving,
    selectedFacility?.facility_id,
    selectedFacility?.country_code,
    selectedFacility?.vertical,
    selectedFacility?.facility_type_slug,
    selectedFacility?.facility_type,
  ]);

  // Global admin LLM prompts load via facility-compliance SSE (Redis on cache hit;
  // per-section prompt fields on LLM path). Do not call /parallel in the background.

  useEffect(() => {
    let cancelled = false;
    const effectFacilityId = selectedFacility?.facility_id ?? null;
    const effectFetchGeneration = complianceFetchGenerationRef.current;
    const useDashboardProgressStream = activeTab === "dashboard";

    const buildProgressContext = (facility: Facility) =>
      buildPenaltyExposureProgressContext({
        countryCode: facility.country_code || getSelectedCountryCode() || "USA",
        vertical: facility.vertical || activeVertical,
        facilityTypeSlug: facility.facility_type_slug,
        facilityType: facility.facility_type,
      });

    const applyProgressEvent = (raw: unknown) => {
      if (!raw || typeof raw !== "object") return;
      const event = raw as {
        stepId?: string;
        status?: string;
        label?: string;
      };
      if (!event.stepId || !event.status) return;
      if (
        event.status !== "started" &&
        event.status !== "complete" &&
        event.status !== "error"
      ) {
        return;
      }
      setPenaltyExposureProgressSteps((prev) =>
        applyComplianceProgressEvent(prev, {
          stepId: event.stepId as string,
          status: event.status as "started" | "complete" | "error",
          label: event.label,
        }),
      );
    };

    const fetchPenaltiesInBackground = async (
      facility: Facility,
      requestFacilityId: number,
    ) => {
      if (
        !shouldBeginPenaltiesFetch({
          inFlightFacilityId: penaltiesFetchInFlightRef.current,
          requestFacilityId,
        })
      ) {
        return;
      }

      penaltiesStreamAbortRef.current?.abort();
      const fetchGeneration = penaltiesFetchGenerationRef.current + 1;
      penaltiesFetchGenerationRef.current = fetchGeneration;
      penaltiesFetchInFlightRef.current = requestFacilityId;

      const currentFacilityData = facility || filtered_selectedFacility;
      const payload = buildFacilityCompliancePayload(
        facility,
        currentFacilityData,
        false,
      );

      const handleEmptyPenaltiesResponse = () => {
        emptyPenaltiesResponsesSeenRef.current += 1;
        if (
          shouldRetryEmptyPenaltiesResponse({
            emptyResponsesSeen: emptyPenaltiesResponsesSeenRef.current,
          })
        ) {
          window.setTimeout(() => {
            if (
              !cancelled &&
              currentRequestFacilityIdRef.current === requestFacilityId
            ) {
              void fetchPenaltiesInBackground(facility, requestFacilityId);
            }
          }, 250);
        }
      };

      setPenaltiesLoading(true);
      try {
        if (useDashboardProgressStream) {
          penaltiesStreamAbortRef.current = new AbortController();
          let penaltiesResponse: {
            sections: {
              penaltiesAndTotalExposure: FacilityComplianceData["sections"]["penaltiesAndTotalExposure"];
            };
            entityDetails?: FacilityComplianceData["entityDetails"];
          } = {
            sections: {
              penaltiesAndTotalExposure: emptyPenaltiesAndTotalExposure(),
            },
          };

          await streamFacilityCompliancePenaltiesOnly(
            payload,
            (eventType, data) => {
              if (cancelled) return;
              if (eventType === "progress") {
                applyProgressEvent(data);
              } else if (eventType === "init") {
                notifyComplianceStreamInit(requestFacilityId);
                const init = data as {
                  entityDetails?: FacilityComplianceData["entityDetails"];
                };
                penaltiesResponse = {
                  ...penaltiesResponse,
                  entityDetails: init.entityDetails,
                };
              } else if (eventType === "section") {
                const sectionEvent = data as {
                  section?: string;
                  data?: FacilityComplianceData["sections"]["penaltiesAndTotalExposure"];
                };
                if (
                  sectionEvent.section === "penaltiesAndTotalExposure" &&
                  sectionEvent.data
                ) {
                  penaltiesResponse = {
                    ...penaltiesResponse,
                    sections: {
                      penaltiesAndTotalExposure: sectionEvent.data,
                    },
                  };
                }
              } else if (eventType === "error") {
                console.error("Penalties stream error:", data);
              }
            },
            penaltiesStreamAbortRef.current.signal,
          );

          if (currentRequestFacilityIdRef.current !== requestFacilityId) {
            return;
          }
          const penaltiesLoaded = hasPenaltiesSectionLoaded(penaltiesResponse);
          if (!penaltiesLoaded) {
            handleEmptyPenaltiesResponse();
          }
          setComplianceData((prev) => {
            if (!penaltiesLoaded) {
              return prev;
            }
            if (!prev) {
              pendingPenaltiesMergeRef.current = {
                facilityId: requestFacilityId,
                response: penaltiesResponse,
              };
              return prev;
            }
            return mergePenaltiesIntoFacilityComplianceData(
              prev,
              penaltiesResponse,
            );
          });
          if (penaltiesLoaded) {
            emptyPenaltiesResponsesSeenRef.current = 0;
            setPenaltyExposureProgressSteps((prev) =>
              markPenaltyExposureProgressBlockComplete(prev, "penalties"),
            );
            if (currentRequestFacilityIdRef.current === requestFacilityId) {
              void refreshDashboardBffTiles(facility);
            }
          }
        } else {
          const penaltiesResponse =
            await facilityComplianceService.getFacilityCompliancePenaltiesOnly(
              payload,
            );
          if (currentRequestFacilityIdRef.current !== requestFacilityId) {
            return;
          }
          const penaltiesLoaded = hasPenaltiesSectionLoaded(penaltiesResponse);
          if (!penaltiesLoaded) {
            handleEmptyPenaltiesResponse();
          }
          setComplianceData((prev) => {
            if (!penaltiesLoaded) {
              return prev;
            }
            if (!prev) {
              pendingPenaltiesMergeRef.current = {
                facilityId: requestFacilityId,
                response: penaltiesResponse,
              };
              return prev;
            }
            return mergePenaltiesIntoFacilityComplianceData(
              prev,
              penaltiesResponse,
            );
          });
          if (penaltiesLoaded) {
            emptyPenaltiesResponsesSeenRef.current = 0;
            setPenaltyExposureProgressSteps((prev) =>
              markPenaltyExposureProgressBlockComplete(prev, "penalties"),
            );
            if (currentRequestFacilityIdRef.current === requestFacilityId) {
              void refreshDashboardBffTiles(facility);
            }
          }
        }
        console.log(
          "✅ Penalties section merged for facility:",
          facility.facility_name,
        );
      } catch (error) {
        if (
          currentRequestFacilityIdRef.current === requestFacilityId &&
          !cancelled
        ) {
          console.error("Error fetching penalties compliance data:", error);
          handleEmptyPenaltiesResponse();
        }
      } finally {
        if (
          shouldFinalizePenaltiesFetch({
            fetchGeneration,
            currentGeneration: penaltiesFetchGenerationRef.current,
            requestFacilityId,
            currentFacilityId: currentRequestFacilityIdRef.current,
          })
        ) {
          penaltiesFetchInFlightRef.current = null;
          setPenaltiesLoading(false);
        }
      }
    };

    const fetchComplianceData = async () => {
      if (!selectedFacility) {
        console.log("⏭️ Skipping API call - No facility data", {
          hasFacilityData: !!selectedFacility,
        });
        setComplianceLoading(false);
        return;
      }

      if (postPurchaseResolving) {
        console.log(
          "⏭️ Skipping compliance load — post-purchase facility still resolving",
          { facilityId: selectedFacility.facility_id },
        );
        setComplianceLoading(false);
        return;
      }

      if (activeTab === "facilityProfile") {
        setComplianceLoading(false);
        return;
      }

      // On dashboard, compliance SSE runs in parallel with profile auto-save.
      if (
        shouldWaitForProfileBeforeCompliance({
          activeTab,
          canViewFacilityProfileTab,
          isProfileApproved: selectedFacility.is_profile_approved === true,
        })
      ) {
        console.log(
          "⏭️ Waiting for facility profile bootstrap before compliance load",
          { facilityId: selectedFacility.facility_id },
        );
        setComplianceLoading(false);
        return;
      }

      // Only block compliance when the facility type requires a Medicare certification date
      // and that date is genuinely absent AND the user has not yet approved the profile.
      // If Facility Profile tab is hidden by permission, allow compliance APIs to proceed.
      if (
        activeTab !== "dashboard" &&
        canViewFacilityProfileTab &&
        !selectedFacility.is_profile_approved &&
        selectedFacility.requires_medicare_certification &&
        !selectedFacility.medicare_certification_date
      ) {
        console.log("⏭️ Skipping compliance load — Medicare certification date required but not yet provided");
        setComplianceLoading(false);
        return;
      }

      // Wait for permissions to load before checking
      const permissionGate = permissionGateRef.current;
      if (permissionGate.loading) {
        console.log("⏭️ Waiting for permissions to load...");
        return;
      }

      // Ensure permission check has completed for this facility on initial load and on changes
      if (permissionGate.checkedFacilityId !== selectedFacility.facility_id) {
        console.log("⏭️ Waiting for permission check to complete for selected facility...");
        return;
      }

      // Check if user has any view permissions before loading
      if (!permissionGate.hasAnyView) {
        console.log(
          "⏭️ Skipping API call - No view permissions for any facility tabs",
          {
            facilityName: selectedFacility.facility_name,
            facilityId: selectedFacility.facility_id,
            permissions: facilityPermissions,
          },
        );
        setComplianceLoading(false);
        setComplianceData(null);
        return;
      }

      const fetchAction = decideComplianceStreamFetch({
        selectedFacilityId: selectedFacility.facility_id,
        loadedFacilityId: lastFetchedFacilityIdRef.current,
        inFlightFacilityId: complianceFetchInFlightRef.current,
        hasComplianceData:
          lastFetchedFacilityIdRef.current === selectedFacility.facility_id,
        hasPenaltiesLoaded:
          lastFetchedFacilityIdRef.current === selectedFacility.facility_id &&
          !!complianceData &&
          hasPenaltiesSectionLoaded(complianceData),
      });

      if (fetchAction === "skip_in_flight") {
        console.log(
          "⏭️ Skipping API call - Parallel compliance fetch already in flight",
          { facilityId: selectedFacility.facility_id },
        );
        return;
      }

      if (fetchAction === "skip_has_data") {
        console.log(
          "⏭️ Skipping API call - Already have data for this facility",
          {
            facilityName: selectedFacility.facility_name,
            facilityId: selectedFacility.facility_id,
            hasComplianceData: !!complianceData,
          },
        );
        setComplianceLoading(false);
        return;
      }

      if (fetchAction === "penalties_only") {
        console.log(
          "⏭️ Skipping analysis stream - Already have analysis for this facility; loading penalties only",
          {
            facilityName: selectedFacility.facility_name,
            facilityId: selectedFacility.facility_id,
          },
        );
        setComplianceLoading(false);
        if (!cancelled) {
          void fetchPenaltiesInBackground(
            selectedFacility,
            selectedFacility.facility_id,
          );
        }
        return;
      }

      // Capture the facility ID for this request to detect stale responses
      const requestFacilityId = selectedFacility.facility_id;
      const fetchGeneration = complianceFetchGenerationRef.current;
      currentRequestFacilityIdRef.current = requestFacilityId;
      complianceFetchInFlightRef.current = requestFacilityId;

      console.log(
        "🔥 API Call - Fetching compliance data for:",
        selectedFacility.facility_name,
        "ID:",
        selectedFacility.facility_id,
        "OrgName:",
        selectedFacility.org_name,
      );

      setComplianceLoading(true);
      setComplianceError(null);
      setPenaltiesLoading(false);
      penaltiesFetchInFlightRef.current = null;
      penaltiesFetchGenerationRef.current += 1;
      emptyPenaltiesResponsesSeenRef.current = 0;

      try {
        const currentFacilityData =
          selectedFacility || filtered_selectedFacility;

        console.log(
          "🏥 Facility Compliance (MyFacilityDetails) - Facility Data:",
          {
            fromSelectedFacility: !!selectedFacility,
            fromSessionStorage:
              !selectedFacility && !!filtered_selectedFacility,
            facilityData: currentFacilityData,
            state: currentFacilityData?.state
              ? String(currentFacilityData.state).trim()
              : "(empty)",
            city: currentFacilityData?.city
              ? String(currentFacilityData.city).trim()
              : "(empty)",
          },
        );

        const payload = buildFacilityCompliancePayload(
          selectedFacility,
          currentFacilityData,
          false,
        );

        pendingPenaltiesMergeRef.current = null;
        void fetchPenaltiesInBackground(selectedFacility, requestFacilityId);

        let data: FacilityComplianceData;
        if (useDashboardProgressStream) {
          if (
            shouldResetPenaltyExposureProgress({
              fetchAction: "full_fetch",
              progressSessionFacilityId:
                penaltyProgressSessionFacilityIdRef.current,
              selectedFacilityId: requestFacilityId,
            })
          ) {
            setPenaltyExposureProgressSteps(
              buildInitialPenaltyExposureProgressSteps(
                buildProgressContext(selectedFacility),
              ),
            );
            penaltyProgressSessionFacilityIdRef.current = requestFacilityId;
          }
          complianceStreamAbortRef.current?.abort();
          complianceStreamAbortRef.current = new AbortController();
          let assembled = createEmptyFacilityComplianceData();

          await streamFacilityComplianceParallel(
            payload,
            (eventType, eventData) => {
              if (cancelled) return;
              if (eventType === "progress") {
                applyProgressEvent(eventData);
              } else if (eventType === "init") {
                notifyComplianceStreamInit(requestFacilityId);
                const init = eventData as {
                  entityDetails?: FacilityComplianceData["entityDetails"];
                  generatedAt?: string;
                  llmPromptsBySection?: FacilityComplianceData["llmPromptsBySection"];
                  llmInvokeWallSecondsBySection?: FacilityComplianceData["llmInvokeWallSecondsBySection"];
                };
                assembled = {
                  ...assembled,
                  entityDetails: init.entityDetails,
                  generatedAt: init.generatedAt,
                  ...(init.llmPromptsBySection
                    ? { llmPromptsBySection: init.llmPromptsBySection }
                    : {}),
                  ...(init.llmInvokeWallSecondsBySection
                    ? {
                        llmInvokeWallSecondsBySection:
                          init.llmInvokeWallSecondsBySection,
                      }
                    : {}),
                };
              } else if (eventType === "section") {
                const sectionEvent = eventData as {
                  section?: string;
                  data?: unknown;
                  prompt?: string;
                  llmInvokeWallSec?: number;
                };
                if (sectionEvent.section) {
                  assembled = mergeComplianceStreamSection(
                    assembled,
                    sectionEvent.section,
                    sectionEvent.data,
                  );
                  if (sectionEvent.prompt?.trim()) {
                    assembled = {
                      ...assembled,
                      llmPromptsBySection: {
                        ...assembled.llmPromptsBySection,
                        [sectionEvent.section]: sectionEvent.prompt,
                      },
                    };
                  }
                  if (sectionEvent.llmInvokeWallSec != null) {
                    assembled = {
                      ...assembled,
                      llmInvokeWallSecondsBySection: {
                        ...assembled.llmInvokeWallSecondsBySection,
                        [sectionEvent.section]: sectionEvent.llmInvokeWallSec,
                      },
                    };
                  }
                }
              } else if (eventType === "error") {
                console.error("Compliance stream error:", eventData);
              }
            },
            complianceStreamAbortRef.current.signal,
          );
          data = assembled;
        } else {
          data = await facilityComplianceService.getFacilityComplianceParallel(
            payload,
          );
        }
        // CRITICAL: Check if this response is still for the current facility
        // This prevents race conditions where a slower response from a previous
        // facility overwrites data from the currently selected facility
        if (currentRequestFacilityIdRef.current !== requestFacilityId) {
          console.log(
            "⏭️ Discarding stale response - facility changed during request",
            {
              requestedFacilityId: requestFacilityId,
              currentFacilityId: currentRequestFacilityIdRef.current,
            },
          );
          return;
        }

        if (fetchGeneration !== complianceFetchGenerationRef.current) {
          console.log(
            "⏭️ Discarding stale response - profile/compliance generation changed",
            {
              requestedGeneration: fetchGeneration,
              currentGeneration: complianceFetchGenerationRef.current,
            },
          );
          return;
        }

        const pendingPenaltiesResponse =
          takePendingPenaltiesMerge<FacilityCompliancePenaltiesResponse>(
            () => pendingPenaltiesMergeRef.current,
            () => {
              pendingPenaltiesMergeRef.current = null;
            },
            requestFacilityId,
          );
        if (pendingPenaltiesResponse) {
          data = mergePenaltiesIntoFacilityComplianceData(
            data,
            pendingPenaltiesResponse,
          );
        }

        lastFetchedFacilityIdRef.current = requestFacilityId;
        setComplianceError(null);
        setComplianceData((prev) =>
          preservePenaltiesOnAnalysisUpdate(data, prev),
        );
        setPenaltyExposureProgressSteps((prev) =>
          markPenaltyExposureProgressBlockComplete(prev, "analysis"),
        );
        if (hasPenaltiesSectionLoaded(data)) {
          void refreshDashboardBffTiles(selectedFacility);
        }
        console.log(
          "✅ Compliance data updated for facility:",
          selectedFacility.facility_name,
        );

        if (
          shouldFetchPenaltiesAfterAnalysis({
            hasPenaltiesLoaded: hasPenaltiesSectionLoaded(data),
            cancelled,
            penaltiesFetchInFlightFacilityId: penaltiesFetchInFlightRef.current,
            requestFacilityId,
          })
        ) {
          void fetchPenaltiesInBackground(selectedFacility, requestFacilityId);
        }
      } catch (error) {
        // Only handle error if this is still the current request
        if (
          currentRequestFacilityIdRef.current === requestFacilityId &&
          fetchGeneration === complianceFetchGenerationRef.current
        ) {
          console.error("Error fetching compliance data:", error);
          setComplianceError(
            error instanceof Error && error.message
              ? error.message
              : "Failed to load facility compliance analysis.",
          );
          setComplianceData(null);
          lastFetchedFacilityIdRef.current = null;
        }
      } finally {
        if (complianceFetchInFlightRef.current === requestFacilityId) {
          complianceFetchInFlightRef.current = null;
        }
        // Only update loading state if this is still the current request
        if (currentRequestFacilityIdRef.current === requestFacilityId) {
          setComplianceLoading(false);
        }
      }
    };

    // Start compliance immediately on dashboard; do not wait for the BFF response.
    if (activeTab === "dashboard") {
      void fetchComplianceData();
    } else if (activeTab !== "facilityProfile") {
      void fetchComplianceData();
    } else {
      setComplianceLoading(false);
    }

    return () => {
      if (
        shouldAbortComplianceStreamsOnEffectCleanup({
          effectFacilityId,
          currentFacilityId: selectedFacilityIdRef.current,
          effectFetchGeneration,
          currentFetchGeneration: complianceFetchGenerationRef.current,
        })
      ) {
        cancelled = true;
        complianceStreamAbortRef.current?.abort();
        penaltiesStreamAbortRef.current?.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    canViewFacilityProfileTab,
    selectedFacility?.facility_id,
    selectedFacility?.is_profile_approved,
    selectedFacility?.requires_medicare_certification,
    selectedFacility?.medicare_certification_date,
    filtered_selectedFacility?.facility_id,
    postPurchaseResolving,
    complianceRefetchNonce,
    permissionsLoading,
    permissionCheckedFacilityId,
  ]);
  // Show loading state
  if (loading) {
    return (
      <ContentContainer>
        <LoadingContainer>
          <CircularProgress size={40} sx={{ color: "#0a829d" }} />
          <Typography sx={{ ml: 2, color: "#737373" }}>
            Loading facility types...
          </Typography>
        </LoadingContainer>
      </ContentContainer>
    );
  }

  // Show facilities loading state only when we have no facility to render yet.
  if ((facilitiesLoading || postPurchaseResolving) && !selectedFacility) {
    return (
      <ContentContainer>
        <LoadingContainer>
          <CircularProgress size={40} sx={{ color: "#0a829d" }} />
          <Typography sx={{ ml: 2, color: "#737373" }}>
            {postPurchaseResolving
              ? "Setting up your facility..."
              : "Loading ..."}
          </Typography>
        </LoadingContainer>
      </ContentContainer>
    );
  }

  // Render AffableFLOW Document Manager panel
  const renderAffableSyncPanel = () => {
    if (!shouldShowAffablePanel) return null;

    return (
      <Box>
        {/* Back to upload files button */}
        {!isSyncingAffableRecords &&
          (affableRecords.length > 0 || affableUploadedFile) && (
            <Box
              onClick={() => {
                setIsAffablePanelClosed(true);
                setAffableDocuments([]);
                setAffableRecords([]);
                setSelectedAffableDocumentId(null);
                setSelectedAffableFolderId(null);
                setSelectedAffableFileKey(null);
                setHasAttemptedAffableSync(false);
                setAffableUploadedFile(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "12px 16px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                cursor: "pointer",
                mb: 2,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <AffableConnectedIcon />
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#262626",
                }}
              >
                Back to upload files
              </Typography>
            </Box>
          )}

        {affableSyncError && (
          <Alert
            severity="error"
            onClose={() => setAffableSyncError(null)}
            sx={{ mb: 1 }}
          >
            {affableSyncError}
          </Alert>
        )}

        {/* Show uploaded file card when available */}
        {affableUploadedFile && (
          <Box>
            <FileDisplayContainer
              sx={{
                flexDirection: "row",
                width: "calc(100% - 32px)",
              }}
            >
              <FileInfoContainer>
                <FileIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M20 12.5854C20.5826 12.7913 21 13.3469 21 14V14.5C21 15.6046 20.1046 16.5 19 16.5H11C9.89543 16.5 9 15.6046 9 14.5V9C9 8.34689 9.4174 7.79127 10 7.58535V6C10 5.17157 10.6716 4.5 11.5 4.5H16.5C17.3284 4.5 18 5.17157 18 6V12.5H19L18.9997 6.58525C19.5825 6.79109 20 7.34678 20 8V12.5854ZM17 11.7929V6C17 5.72386 16.7761 5.5 16.5 5.5H11.5C11.2239 5.5 11 5.72386 11 6V7.5H12.0858C12.4836 7.5 12.8651 7.65804 13.1464 7.93934L17 11.7929ZM10 9V14.5C10 15.0523 10.4477 15.5 11 15.5H19C19.5523 15.5 20 15.0523 20 14.5V14C20 13.7239 19.7761 13.5 19.5 13.5H17.9142C17.5164 13.5 17.1349 13.342 16.8536 13.0607L12.4393 8.64645C12.3456 8.55268 12.2184 8.5 12.0858 8.5H10.5C10.2239 8.5 10 8.72386 10 9Z"
                      fill="#065B70"
                    />
                  </svg>
                </FileIcon>
                <FileDetails sx={{ maxWidth: "250px", minWidth: 0 }}>
                  <FileName
                    sx={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={affableUploadedFile.name}
                  >
                    {affableUploadedFile.name}
                  </FileName>
                  <FileSize>
                    {(
                      affableUploadedFile.name.split(".").pop() || ""
                    ).toLowerCase()}{" "}
                    | {formatFileSize(affableUploadedFile.size)}
                  </FileSize>
                </FileDetails>
              </FileInfoContainer>
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <ReUploadButton
                  onClick={() => {
                    setAffableUploadedFile(null);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M13.8891 3.58537C13.2006 2.68731 12.1509 2.11111 10.9761 2.11111C9.19946 2.11111 7.65885 3.2022 7.15885 4.77778H6.66663C4.8257 4.77778 3.33329 6.27019 3.33329 8.11111C3.33329 9.95204 4.8257 11.4444 6.66663 11.4444H7.77774V10.2222H6.66663C5.50108 10.2222 4.55552 9.27667 4.55552 8.11111C4.55552 6.94556 5.50108 6 6.66663 6H7.79885L8.02885 5.24444C8.39329 4.02 9.58329 3.33333 10.9761 3.33333C11.7983 3.33333 12.5594 3.70556 13.0483 4.34L13.5206 4.94889L14.1039 4.44222C15.1428 3.61778 16.6 3.83111 17.3872 4.88889C18.1772 5.95111 17.9428 7.40667 16.8728 8.18L15.5555 9.15556V10.7772L17.4633 9.34C19.2372 8.02889 19.6328 5.52 18.3217 3.74667C17.0106 1.97333 14.5017 1.57556 12.7278 2.88667L12.5261 3.04L13.1094 3.54667L12.6372 4.15556L13.8891 3.58537Z"
                      fill="#065B70"
                    />
                    <path
                      d="M13.3333 10V17.7778L15.5 15.6111L16.3889 16.5L12.6667 20.2222L8.94446 16.5L9.83335 15.6111L12 17.7778V10H13.3333Z"
                      fill="#065B70"
                    />
                  </svg>
                  Re-upload
                </ReUploadButton>
                <DeleteButton
                  onClick={() => {
                    setAffableUploadedFile(null);
                  }}
                >
                  <DeleteFileIcon />
                </DeleteButton>
              </Box>
            </FileDisplayContainer>
          </Box>
        )}

        {/* Show 3 boxes only when no uploaded file */}
        {!affableUploadedFile && affableRecords.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "stretch",
              mt: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: "45%",
                minWidth: 0,
                border: "1px solid #0a829d",
                borderRadius: "4px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  backgroundColor: "#0a829d",
                  display: "flex",
                  alignItems: "center",
                  p: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                >
                  Survey Name
                </Typography>
              </Box>

              {/* Table Body - Dynamic */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  maxHeight: "280px",
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "2px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#0a829d",
                    borderRadius: "2px",
                    "&:hover": {
                      backgroundColor: "#086e86",
                    },
                  },
                }}
              >
                {affableRecords.map((record: any) => {
                  const isSelected =
                    selectedAffableDocumentId === record.DocumentID;
                  const recordName =
                    affableTableColumns.length > 0
                      ? record[affableTableColumns[0]] || "Untitled"
                      : "Untitled";
                  const matchingDoc = affableDocuments.find(
                    (doc: any) => doc.id === record.DocumentID,
                  );
                  const fileCount = matchingDoc
                    ? matchingDoc.folders.reduce(
                        (total: number, folder: any) =>
                          total + folder.images.length,
                        0,
                      )
                    : 0;
                  return (
                    <Box
                      key={record.DocumentID}
                      onClick={() => {
                        setSelectedAffableDocumentId(record.DocumentID);
                        // Auto-select first folder when document is selected
                        const matchingDoc = affableDocuments.find(
                          (doc: any) => doc.id === record.DocumentID,
                        );
                        if (matchingDoc?.folders?.length > 0) {
                          setSelectedAffableFolderId(matchingDoc.folders[0].id);
                        } else {
                          setSelectedAffableFolderId(null);
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 12px",
                        borderBottom: "1px solid #e5f5f8",
                        gap: "8px",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#e6fafc" : "#ffffff",
                        "&:hover": {
                          backgroundColor: isSelected ? "#d4edf3" : "#f5feff",
                        },
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4 2C3.44772 2 3 2.44772 3 3V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V5.41421C13 5.149 12.8946 4.89464 12.7071 4.70711L10.2929 2.29289C10.1054 2.10536 9.851 2 9.58579 2H4ZM5 5H8V6H5V5ZM5 8H11V9H5V8ZM5 11H11V12H5V11Z"
                          fill="#737373"
                        />
                      </svg>
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: "13px",
                          color: "#262626",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        {recordName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: "12px",
                          color: "#737373",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fileCount} {fileCount === 1 ? "file" : "files"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Pagination Row - Fixed at bottom */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 8px",
                  borderTop: "1px solid #e5f5f8",
                  gap: "4px",
                  flexShrink: 0,
                  backgroundColor: "#ffffff",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    py: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      ml: "auto",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: 12,
                        color: "#065b70",
                      }}
                    >
                      Rows per page
                    </Typography>
                    <Select
                      size="small"
                      value={affableRowsPerPage}
                      onChange={(e) => {
                        const newPageSize = Number(e.target.value);
                        setAffableRowsPerPage(newPageSize);
                        setAffableDocsPage(1);
                        handleAffableRecordsSync(0, newPageSize);
                      }}
                      sx={{
                        minWidth: 50,
                        width: 50,
                        height: 28,
                        "& .MuiSelect-select": {
                          padding: "4px 8px",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& .MuiMenuItem-root": {
                              fontSize: 12,
                              minHeight: "28px",
                              lineHeight: "28px",
                              fontFamily: '"Rethink Sans", sans-serif',
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                    </Select>
                    <Pagination
                      count={totalAffableDocPages}
                      page={affableDocsPage}
                      onChange={(_, value) => {
                        setAffableDocsPage(value);
                        handleAffableRecordsSync(value - 1);
                      }}
                      color="primary"
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          fontSize: 12,
                          height: 28,
                          minWidth: 28,
                          padding: "0 6px",
                        },
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "#0a829d",
                          color: "#ffffff",
                        },
                        "& .MuiPaginationItem-root.Mui-selected:hover": {
                          backgroundColor: "#0a829d",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {affableDocuments.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "55%",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0,
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      width: "35%",
                      borderRadius: "4px 0 0 4px",
                      border: "1px solid #0a829d",
                      backgroundColor: "#ffffff",
                      overflow: "hidden",
                      height: "240px",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#0a829d",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#ffffff",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(() => {
                          const selectedRecord = affableRecords.find(
                            (r: any) =>
                              r.DocumentID === selectedAffableDocumentId,
                          );
                          if (
                            selectedRecord &&
                            affableTableColumns.length > 0
                          ) {
                            return (
                              selectedRecord[affableTableColumns[0]] ||
                              "Documents"
                            );
                          }
                          return "Documents";
                        })()}
                      </Typography>
                    </Box>

                    <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                      {selectedAffableDocument?.folders.length ? (
                        selectedAffableDocument.folders.map((folder: any) => {
                          const isActive =
                            selectedAffableFolder &&
                            selectedAffableFolder.id === folder.id;
                          return (
                            <Box
                              key={folder.id}
                              sx={{
                                px: 1.5,
                                py: 1,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                backgroundColor: isActive
                                  ? "#e6fafc"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: "#f5feff",
                                },
                              }}
                              onClick={() =>
                                setSelectedAffableFolderId(folder.id)
                              }
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#043c4a",
                                  fontWeight: isActive ? 600 : 500,
                                }}
                              >
                                {folder.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#0a829d",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {folder.images.length}{" "}
                                {folder.images.length === 1 ? "file" : "files"}
                              </Typography>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography
                          sx={{ fontSize: "13px", color: "#4f6f78", p: 1.5 }}
                        >
                          No folders available for this document yet.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      width: "65%",
                      borderRadius: "0 4px 4px 0",
                      border: "1px solid #0a829d",
                      borderLeft: "none",
                      backgroundColor: "#ffffff",
                      overflow: "hidden",
                      height: "240px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#ffffff",
                        borderBottom: "1px solid #0a829d",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#043c4a",
                        }}
                      >
                        {selectedAffableFolder?.name || "Default"}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, flex: 1, overflow: "hidden" }}>
                      {!selectedAffableFolder ||
                      !selectedAffableFolder.images.length ? (
                        <Typography sx={{ fontSize: "14px", color: "#4f6f78" }}>
                          No files available in this folder.
                        </Typography>
                      ) : (
                        <>
                          <Box
                            sx={{
                              maxHeight: "100%",
                              height: "100%",
                              overflowY: "auto",
                              overflowX: "auto",
                              pr: 1,
                              "&::-webkit-scrollbar": {
                                width: "4px",
                                height: "4px",
                              },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor: "#f1f1f1",
                                borderRadius: "2px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#0a829d",
                                borderRadius: "2px",
                                "&:hover": {
                                  backgroundColor: "#086e86",
                                },
                              },
                            }}
                          >
                            {selectedAffableFolder.images.map((image: any) => {
                              const fileKey = `${selectedAffableFolder.id}-${image.id}`;
                              const isSelected =
                                selectedAffableFileKey === fileKey;
                              return (
                                <Box
                                  key={`${selectedAffableFolder.id}-${image.id}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    onChange={() =>
                                      setSelectedAffableFileKey(
                                        isSelected ? null : fileKey,
                                      )
                                    }
                                  />
                                  <AffableFileRow>
                                    <AffableFileName>
                                      {image.name}
                                      {image.type ? ` (${image.type})` : ""}
                                    </AffableFileName>
                                    <AffableFileMeta>
                                      {image.createdAt ||
                                        "No created date provided"}
                                    </AffableFileMeta>
                                  </AffableFileRow>
                                </Box>
                              );
                            })}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
                {/* Upload Selected File Button */}
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "5px 5px",
                    mt: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleUploadSelectedAffableFile}
                    disabled={loadingAffableFile || !selectedAffableFileKey}
                    sx={{
                      backgroundColor: "#0a829d",
                      color: "#ffffff",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      fontFamily: '"Rethink Sans", sans-serif',
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#086e86",
                      },
                      "&:disabled": {
                        backgroundColor: "#b0d4dc",
                        color: "#ffffff",
                      },
                    }}
                  >
                    {loadingAffableFile ? (
                      <>
                        <CircularProgress
                          size={16}
                          sx={{ color: "#ffffff", mr: 1 }}
                        />
                        Uploading...
                      </>
                    ) : (
                      "Upload Selected File"
                    )}
                  </Button>
                </Box>
              </Box>
            ) : (
              !isSyncingAffableRecords &&
              !affableSyncError && (
                <Typography sx={{ fontSize: "14px", color: "#4f6f78" }}>
                  No documents available for this facility yet.
                </Typography>
              )
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <ContentContainer>
        <ContentWrapper>
          <MainContent>
            {navigationState?.returnTo?.currentView === "facility-network" ? (
              <Button
                variant="text"
                onClick={() =>
                  navigate("/dashboard/facilities", {
                    state: {
                      currentView: "facility-network",
                      networkTab: navigationState.returnTo?.networkTab || "children",
                      selectedFacility,
                    },
                  })
                }
                sx={{
                  alignSelf: "flex-start",
                  textTransform: "none",
                  color: "#0a829d",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: 13,
                  fontWeight: 600,
                  mb: 1,
                  px: 0,
                  minWidth: 0,
                }}
              >
                ← Back to Linked facilities
              </Button>
            ) : null}
            <HeaderSection>
              <HeaderContainer>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    justifyContent: "space-between",
                    p: { xs: 1.5, sm: 2 },
                    border: "1px solid #CCF5FF",
                    borderRadius: "12px",
                    mb: 3,
                    gap: { xs: 2, md: 3 },
                  }}
                >
                  {/* Facility selector grows; facility type group on the right */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      flexWrap: "nowrap",
                      alignItems: { xs: "stretch", sm: "center" },
                      flex: 1,
                      gap: { xs: 1, sm: 2 },
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#065B70",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      Selected Facility
                    </Typography>

                    <Select<number | "">
                      size="small"
                      value={
                        selectedFacility == null
                          ? ""
                          : (getFacilityHierarchyId(selectedFacility) ?? "")
                      }
                      onChange={handleFacilityChange}
                      displayEmpty
                      renderValue={(selected) => {
                        if (
                          selected === "" ||
                          selected === null ||
                          selected === undefined
                        ) {
                          return (
                            <Typography
                              sx={{ color: "#737373", fontSize: "14px" }}
                            >
                              Select a facility
                            </Typography>
                          );
                        }
                        const facilityId = selected as number;
                        const opt = groupedVisibleFacilityOptions.find(
                          ({ facility: f }) =>
                            getFacilityHierarchyId(f) === facilityId,
                        );
                        if (!opt) {
                          return (
                            <Typography sx={{ fontSize: "14px" }}>
                              {formatFacilityDropdownLabel(selectedFacility!)}
                            </Typography>
                          );
                        }
                        return (
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <FacilityPickerOptionContent
                                facility={opt.facility}
                                isParent={opt.isParent}
                              />
                            </Box>
                            {hasAffableConnection(opt.facility) && (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  height: 18,
                                }}
                              >
                                <AffableConnectedIcon />
                              </Box>
                            )}
                          </Box>
                        );
                      }}
                      sx={{
                        flex: { xs: "unset", sm: 1 },
                        width: { xs: "100%" },
                        minWidth: { xs: "100%", sm: 150 },
                        fontSize: "14px",
                        background: "#FDFFFF",
                        border: "1px solid #99D4E1",
                        borderRadius: "8px",
                        "& .MuiSelect-select": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflow: "hidden",
                          textOverflow: "clip",
                          py: 1,
                          alignItems: "center",
                          display: "flex",
                          minHeight: FACILITY_HEADER_FIELD_MIN_HEIGHT_PX,
                          boxSizing: "border-box",
                        },
                      }}
                    >
                      {groupedVisibleFacilityOptions.map(({ facility, depth, isParent, key }) => {
                        const optionId = getFacilityHierarchyId(facility);
                        if (optionId == null) {
                          return null;
                        }
                        const facilityLabel = formatFacilityDropdownLabel(facility);
                        const groupBadge = facilityGroupBadgeLabel(facility, isParent);
                        return (
                        <MenuItem
                          key={key}
                          value={optionId}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: 1,
                              pl: depth === 0 ? 0 : 3,
                              minHeight: FACILITY_HEADER_FIELD_MIN_HEIGHT_PX,
                              boxSizing: "border-box",
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }} title={facilityLabel}>
                              <FacilityPickerOptionContent
                                facility={facility}
                                isParent={isParent}
                              />
                            </Box>

                            {(groupBadge ||
                              isRestrictedFacility(facility) ||
                              hasAffableConnection(facility)) && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexShrink: 0,
                                }}
                              >
                                {groupBadge ? (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "10px",
                                      color: "#0a829d",
                                      border: "1px solid #99d4e1",
                                      borderRadius: "999px",
                                      px: "5px",
                                      height: 18,
                                      lineHeight: 1,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {groupBadge}
                                  </Box>
                                ) : null}

                                {isRestrictedFacility(facility) && (
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: "#DC2626",
                                      flexShrink: 0,
                                      lineHeight: 1,
                                    }}
                                  >
                                    Restricted
                                  </Typography>
                                )}

                                {hasAffableConnection(facility) && (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      height: 18,
                                    }}
                                  >
                                    <AffableConnectedIcon />
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        </MenuItem>
                        );
                      })}
                    </Select>
                  </Box>

                  {selectedFacility && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: "nowrap",
                        alignItems: { xs: "stretch", sm: "center" },
                        gap: { xs: 1, sm: 2 },
                        flexShrink: 0,
                        ml: { xs: 0, md: "auto" },
                        alignSelf: { xs: "stretch", md: "center" },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#065B70",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {(selectedFacility.vertical || "").toLowerCase() ===
                        "aviation"
                          ? "Aviation Profile"
                          : "Facility Type"}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          border: "1px solid #99D4E1",
                          borderRadius: "8px",
                          background: "#FDFFFF",
                          px: 1.5,
                          py: 1,
                          minWidth: { xs: 0, sm: 200 },
                          maxWidth: { sm: 320 },
                          width: { xs: "100%", sm: "auto" },
                          minHeight: FACILITY_HEADER_FIELD_MIN_HEIGHT_PX,
                          boxSizing: "border-box",
                        }}
                      >
                        <FacilityTypeValueStack facility={selectedFacility} />
                      </Box>
                    </Box>
                  )}
                </Box>

                <HeaderTitle>
                  {/* <BookIcon>
                   
                </BookIcon> */}
                  <CreateSpaceIcon />
                  <Title>My Facility</Title>
                  {activeTab !== "facilityProfile" && activeTab !== "oig" && (
                    <DisclaimerText>
                      (All analysis below are AI-assisted. Human review and
                      verification are required.)
                    </DisclaimerText>
                  )}
                </HeaderTitle>
                {mainNavTabs.length > 0 && (
                  <Tabs
                    value={mainTabIndex}
                    onChange={(_, idx) => {
                      const next = mainNavTabs[idx as number];
                      if (next) void handleTabClick(next.id);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={(theme) => ({
                      width: "100%",
                      minHeight: 44,
                      mt: 1,
                      "& .MuiTabs-indicator": {
                        height: 2,
                        backgroundColor: "#011f26",
                      },
                      "& .MuiTabScrollButton-root.Mui-disabled": {
                        width: 0,
                        minWidth: 0,
                        maxWidth: 0,
                        overflow: "hidden",
                        opacity: 0,
                        pointerEvents: "none",
                      },
                      "& .MuiTabs-flexContainer": {
                        justifyContent: "flex-start",
                      },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "15px",
                        minHeight: 44,
                        py: 1,
                        px: 1.25,
                        minWidth: "auto",
                        color: "#737373",
                        "&.Mui-selected": { color: "#065b70" },
                      },
                      "& .MuiTab-root:first-of-type": { pl: 0 },
                      [theme.breakpoints.down("sm")]: {
                        "& .MuiTab-root": { fontSize: "13px", px: 1 },
                      },
                    })}
                  >
                    {mainNavTabs.map((t) => (
                      <Tab key={t.id} label={t.label} />
                    ))}
                  </Tabs>
                )}
              </HeaderContainer>
              {!hasAnyViewPermission &&
                (facilityPermissions.length > 0 ||
                  permissionsResponseEmpty) && (
                  <Box
                    sx={{
                      padding: "40px 20px",
                      textAlign: "center",
                      backgroundColor: "#f9f9f9",
                      borderRadius: "8px",
                      margin: "20px 0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: "16px",
                        fontWeight: 500,
                        color: "#737373",
                      }}
                    >
                      You don't have view permissions for any facility features.
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#737373",
                        marginTop: "8px",
                      }}
                    >
                      Please contact your administrator to request access.
                    </Typography>
                  </Box>
                )}
              {selectedFacility && canViewDashboardTab && (
                <Box
                  sx={{
                    display: activeTab === "dashboard" ? "block" : "none",
                    bgcolor: "#ffffff",
                    borderRadius: "8px",
                    p: { xs: 1, sm: 2 },
                  }}
                >
                  <FacilityDashboardTab
                    facility={selectedFacility}
                    isTabActive={activeTab === "dashboard"}
                    data={dashboardBffData}
                    loading={dashboardBffLoading}
                    error={dashboardBffError}
                    onRetry={() => void loadDashboardBff(selectedFacility)}
                    onNavigate={handleDashboardNavigate}
                    complianceCalendar={
                      complianceData?.sections?.penaltiesAndTotalExposure
                        ?.complianceCalendar
                    }
                    potentialPenaltyItems={
                      complianceData?.sections?.penaltiesAndTotalExposure?.items
                    }
                    asOfDate={complianceData?.entityDetails?.asOfDate}
                    complianceCalendarLoading={complianceLoading || penaltiesLoading}
                    penaltyExposureProgressSteps={penaltyExposureProgressSteps}
                  />
                </Box>
              )}
              {selectedFacility && canViewFacilityProfileTab && (
                <Box
                  sx={{
                    display: activeTab === "facilityProfile" ? "block" : "none",
                  }}
                >
                  <FacilityProfileTab
                    facilityId={selectedFacility.facility_id}
                    facilityTypeSlug={selectedFacility.facility_type_slug || ""}
                    countryCode={selectedFacility.country_code || "USA"}
                    readOnly={!canUpdateFacilityProfileTab}
                    deferProfileFetchUntilTabVisible={
                      selectedFacility.is_profile_approved === true
                    }
                    isProfileTabActive={
                      (activeTab === "facilityProfile" ||
                        selectedFacility.is_profile_approved !== true) &&
                      (selectedFacility.is_profile_approved === true ||
                        dashboardBffData != null ||
                        dashboardBffError != null)
                    }
                    profileSchemaWarmedFromBff={
                      dashboardBffData?.profile_schema_warmed === true
                    }
                    deferAutoSaveUntilComplianceInit={
                      activeTab === "dashboard" &&
                      selectedFacility.is_profile_approved !== true
                    }
                    complianceStreamInitSignal={complianceStreamInitSignal}
                    skipComplianceCacheInvalidationOnAutoSave={
                      activeTab === "dashboard" &&
                      selectedFacility.is_profile_approved !== true
                    }
                    existingAccreditingBody={selectedFacility.accrediting_body as { slug: string; name: string }[] | null | undefined}
                    existingMedicareDate={selectedFacility.medicare_certification_date as string | null | undefined}
                    autoSaveOnInitialLoad={selectedFacility.is_profile_approved !== true}
                    onPreferencePatch={(fid, patch) => {
                      setSelectedFacility((prev) => {
                        if (!prev || prev.facility_id !== fid) return prev;
                        const next = { ...prev, ...patch };
                        try {
                          sessionStorage.setItem(
                            "filtered_selectedFacility",
                            JSON.stringify(next),
                          );
                        } catch {
                          /* ignore */
                        }
                        notifySelectedFacilitySessionUpdated();
                        return next;
                      });
                      setFacilities((prev) =>
                        prev.map((f) =>
                          f.facility_id === fid ? { ...f, ...patch } : f,
                        ),
                      );
                    }}
                    onComplete={(updatedFields) => {
                      const acceptedFacilityId = selectedFacility.facility_id;
                      const rawAccrediting =
                        pickAccreditingBodyRawFromProfileSave(updatedFields);
                      // Persist to sessionStorage so the flag survives remounts.
                      persistProfileAcceptedId(acceptedFacilityId);
                      const taxCal =
                        updatedFields.show_tax_compliance_calendar !== undefined
                          ? Boolean(updatedFields.show_tax_compliance_calendar)
                          : undefined;
                      const normalisedAccrediting =
                        rawAccrediting !== undefined
                          ? normaliseAccreditingBodyForFacility(rawAccrediting)
                          : undefined;
                      const listPatch = {
                        is_profile_approved: true as const,
                        ...(normalisedAccrediting !== undefined
                          ? { accrediting_body: normalisedAccrediting }
                          : {}),
                        ...(updatedFields.medicare_certification_date !== undefined
                          ? {
                              medicare_certification_date:
                                updatedFields.medicare_certification_date as string | null,
                            }
                          : {}),
                        ...(taxCal !== undefined
                          ? { show_tax_compliance_calendar: taxCal }
                          : {}),
                      };
                      // Stamp is_profile_approved on the selected facility object.
                      setSelectedFacility((prev) => {
                        if (!prev) return prev;
                        const next = { ...prev, ...listPatch };
                        try {
                          sessionStorage.setItem(
                            "filtered_selectedFacility",
                            JSON.stringify(next),
                          );
                        } catch {
                          /* ignore */
                        }
                        notifySelectedFacilitySessionUpdated();
                        return next;
                      });
                      // Stamp the same flag on the facility in the dropdown list so
                      // switching away and back preserves the "already approved" state.
                      setFacilities((prev) =>
                        prev.map((f) =>
                          f.facility_id === acceptedFacilityId
                            ? { ...f, ...listPatch }
                            : f,
                        ),
                      );
                      patchMyFacilityRow(acceptedFacilityId, listPatch);
                      if (
                        shouldResetComplianceAfterProfileSave({
                          acceptedFacilityId,
                          complianceInFlightFacilityId:
                            complianceFetchInFlightRef.current,
                        })
                      ) {
                        // Profile data has changed — clear any previously cached compliance
                        // results so the analysis re-runs with the newly saved values.
                        complianceFetchGenerationRef.current += 1;
                        complianceFetchInFlightRef.current = null;
                        setComplianceData(null);
                        lastFetchedFacilityIdRef.current = null;
                        penaltiesFetchInFlightRef.current = null;
                        penaltiesFetchGenerationRef.current += 1;
                        emptyPenaltiesResponsesSeenRef.current = 0;
                        penaltyProgressSessionFacilityIdRef.current = null;
                        pendingPenaltiesMergeRef.current = null;
                        setDashboardRefreshKey((k) => k + 1);
                      }
                      setActiveTab("dashboard");
                    }}
                  />
                </Box>
              )}
              {hasAnyViewPermission && (
                <TabContentContainer>
                  <Box
                    sx={{
                      display: activeTab === "facilities" ? "block" : "none",
                    }}
                  >
                    <FacilityAnalysis
                      complianceData={complianceData}
                      loading={complianceLoading && hasAnyViewPermission}
                      error={complianceError}
                      onRetry={() => setComplianceRefetchNonce((nonce) => nonce + 1)}
                      selectedFacility={selectedFacility}
                      analysisTabActive={activeTab === "facilities"}
                      onWatchGuideClick={handleWatchGuideClick}
                      watchGuideLoading={watchGuideLoading}
                      watchGuideIconSrc={VideoRecordingIcon}
                      showWatchGuide={watchGuideAvailability.facilities === true}
                    />
                  </Box>
                  <Box
                    sx={{ display: activeTab === "penalty" ? "block" : "none" }}
                  >
                    <Penalty
                      penaltyTabActive={activeTab === "penalty"}
                      penaltyData={
                        complianceData?.sections?.penaltiesAndTotalExposure ||
                        null
                      }
                      loading={complianceLoading && hasAnyViewPermission}
                      penaltiesLoading={penaltiesLoading && hasAnyViewPermission}
                      selectedFacility={selectedFacility}
                      asOfDate={complianceData?.entityDetails?.asOfDate}
                      penaltiesLlmPrompt={
                        complianceData?.llmPromptsBySection?.penalties
                      }
                      penaltiesLlmWallSec={
                        complianceData?.llmInvokeWallSecondsBySection?.penalties
                      }
                      historicPenaltiesFacility={
                        dashboardBffData?.historic_penalties_facility ?? null
                      }
                      onWatchGuideClick={handleWatchGuideClick}
                      watchGuideLoading={watchGuideLoading}
                      watchGuideIconSrc={VideoRecordingIcon}
                      showWatchGuide={watchGuideAvailability.penalty === true}
                    />
                  </Box>
                  <Box sx={{ display: activeTab === "oig" ? "block" : "none" }}>
                    <OIGAdverseCheckForm
                      key={`oig-${selectedFacility?.facility_id}`}
                      oigTabActive={activeTab === "oig"}
                      facilityData={
                        selectedFacility || filtered_selectedFacility
                      }
                      onWatchGuideClick={handleWatchGuideClick}
                      watchGuideLoading={watchGuideLoading}
                      watchGuideIconSrc={VideoRecordingIcon}
                      showWatchGuide={watchGuideAvailability.oig === true}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: activeTab === "compliance" ? "block" : "none",
                    }}
                  >
                    <ContinuousCompliance
                      key={`compliance-${selectedFacility?.facility_id}`}
                      complianceTabActive={activeTab === "compliance"}
                      facilityData={
                        selectedFacility || filtered_selectedFacility
                      }
                      focusUploadSection={complianceFocusSection}
                      onFocusUploadSectionConsumed={() =>
                        setComplianceFocusSection(null)
                      }
                      onWatchGuideClick={handleWatchGuideClick}
                      watchGuideLoading={watchGuideLoading}
                      watchGuideIconSrc={VideoRecordingIcon}
                      showWatchGuide={watchGuideAvailability.compliance === true}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: activeTab === "riskanalysis" ? "block" : "none",
                    }}
                  >
                    <RiskAnalysis
                      riskTabActive={activeTab === "riskanalysis"}
                      facilityData={
                        selectedFacility || filtered_selectedFacility
                      }
                      focusUploadSection={riskFocusSection}
                      onFocusUploadSectionConsumed={() =>
                        setRiskFocusSection(null)
                      }
                      onWatchGuideClick={handleWatchGuideClick}
                      watchGuideLoading={watchGuideLoading}
                      watchGuideIconSrc={VideoRecordingIcon}
                      showWatchGuide={watchGuideAvailability.riskanalysis === true}
                      payorContractAnalysis={
                        canViewFeatureByName(
                          FEATURE_NAMES.PAYOR_CONTRACT_ANALYSIS,
                        ) ? (
                          <PayorContractAnalysis
                            facilityData={
                              selectedFacility || filtered_selectedFacility
                            }
                            onWatchGuideClick={handleWatchGuideClick}
                            watchGuideLoading={watchGuideLoading}
                            watchGuideIconSrc={VideoRecordingIcon}
                            showWatchGuide={
                              watchGuideAvailability.payorcontract === true
                            }
                          />
                        ) : undefined
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: activeTab === "surveyorvisit" ? "block" : "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        mb: "12px",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M6 10.5C6 10.2239 6.22386 10 6.5 10C6.77614 10 7 10.2239 7 10.5C7 10.7761 6.77614 11 6.5 11C6.22386 11 6 10.7761 6 10.5ZM6.5 12C6.22386 12 6 12.2239 6 12.5C6 12.7761 6.22386 13 6.5 13C6.77614 13 7 12.7761 7 12.5C7 12.2239 6.77614 12 6.5 12ZM6 14.5C6 14.2239 6.22386 14 6.5 14C6.77614 14 7 14.2239 7 14.5C7 14.7761 6.77614 15 6.5 15C6.22386 15 6 14.7761 6 14.5ZM8.5 10C8.22386 10 8 10.2239 8 10.5C8 10.7761 8.22386 11 8.5 11H13.5C13.7761 11 14 10.7761 14 10.5C14 10.2239 13.7761 10 13.5 10H8.5ZM8 12.5C8 12.2239 8.22386 12 8.5 12H13.5C13.7761 12 14 12.2239 14 12.5C14 12.7761 13.7761 13 13.5 13H8.5C8.22386 13 8 12.7761 8 12.5ZM8.5 14C8.22386 14 8 14.2239 8 14.5C8 14.7761 8.22386 15 8.5 15H13.5C13.7761 15 14 14.7761 14 14.5C14 14.2239 13.7761 14 13.5 14H8.5ZM6 2C4.89543 2 4 2.89543 4 4V16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V7.41421C16 7.01639 15.842 6.63486 15.5607 6.35355L11.6464 2.43934C11.3651 2.15804 10.9836 2 10.5858 2H6ZM5 4C5 3.44772 5.44772 3 6 3H10V6.5C10 7.32843 10.6716 8 11.5 8H15V16C15 16.5523 14.5523 17 14 17H6C5.44772 17 5 16.5523 5 16V4ZM14.7929 7H11.5C11.2239 7 11 6.77614 11 6.5V3.20711L14.7929 7Z"
                          fill="#065B70"
                        />
                      </svg>
                      <Typography
                        sx={{
                          color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
                          fontSize: "16px",
                          fontStyle: "normal",
                          fontWeight: 500,
                          lineHeight: "normal",
                        }}
                      >
                        Mock Survey
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {watchGuideAvailability.surveyorvisit === true && (
                        <Button
                        variant="outlined"
                        disabled={watchGuideLoading}
                        onClick={handleWatchGuideClick}
                        sx={{
                          display: "flex",
                          flexShrink: 0,
                          minHeight: "40px",
                          padding: "8px 16px",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "8px",
                          borderRadius: "8px",
                          border: "1px solid #5BBFD6",
                          background: "#FFFFFF",
                          boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#F8FDFF",
                            borderColor: "#3CA9C2",
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={VideoRecordingIcon}
                          alt="Watch guide"
                          sx={{ width: 16, height: 16, display: "block" }}
                        />
                        <Typography
                          sx={{
                            color: "#086E86",
                            fontFamily: '"Rethink Sans", sans-serif',
                            fontSize: "13px",
                            fontWeight: 600,
                            lineHeight: "normal",
                          }}
                        >
                          {watchGuideLoading ? "Loading Guide..." : "Watch Guide"}
                        </Typography>
                        </Button>
                      )}
                    </Box>
                    <Accordion
                      expanded={mockSurveyAccordionExpanded}
                      onChange={(_, expanded) =>
                        setMockSurveyAccordionExpanded(expanded)
                      }
                      sx={{
                        marginBottom: "8px",
                        borderRadius: "8px !important",
                        border: "1px solid #e0e0e0",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        overflow: "hidden",
                        "&:before": { display: "none" },
                        "&.Mui-expanded": { margin: "0 0 8px 0" },
                        "& .MuiAccordionSummary-root.Mui-expanded": {
                          minHeight: "48px",
                        },
                        "& .MuiAccordionSummary-content.Mui-expanded": {
                          margin: "12px 0",
                        },
                        "& .MuiAccordionDetails-root": { paddingTop: "0px" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M5 7.5L10 12.5L15 7.5"
                              stroke="#262626"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        }
                        sx={{
                          borderRadius: "8px",
                          border: "1px solid #9EE5F4",
                          backgroundColor: "#FFF",
                          boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                          "&.Mui-expanded": {
                            border: "1px solid #9EE5F4",
                            borderRadius: "8px 8px 0 0",
                            boxShadow: "none",
                          },
                          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded":
                            {
                              transform: "rotate(180deg)",
                            },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flex: 1,
                            mr: 2,
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M15 12.5854C15.5826 12.7913 16 13.3469 16 14V14.5C16 15.6046 15.1046 16.5 14 16.5H6C4.89543 16.5 4 15.6046 4 14.5V9C4 8.34689 4.4174 7.79127 5 7.58535V6C5 5.17157 5.67157 4.5 6.5 4.5H11.5C12.3284 4.5 13 5.17157 13 6V12.5H14L13.9997 6.58525C14.5825 6.79109 15 7.34678 15 8V12.5854ZM12 11.7929V6C12 5.72386 11.7761 5.5 11.5 5.5H6.5C6.22386 5.5 6 5.72386 6 6V7.5H7.08579C7.48361 7.5 7.86514 7.65804 8.14645 7.93934L12 11.7929ZM5 9V14.5C5 15.0523 5.44772 15.5 6 15.5H14C14.5523 15.5 15 15.0523 15 14.5V14C15 13.7239 14.7761 13.5 14.5 13.5H12.9142C12.5164 13.5 12.1349 13.342 11.8536 13.0607L7.43934 8.64645C7.34557 8.55268 7.21839 8.5 7.08579 8.5H5.5C5.22386 8.5 5 8.72386 5 9Z"
                              fill="#262626"
                            />
                          </svg>
                          <Typography
                            sx={{
                              fontSize: "16px",
                              fontWeight: 600,
                              color: "#262626",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Mock Survey Report
                          </Typography>
                          <Box sx={{ flex: 1 }} />
                          {/* {isAffableConnected && (
                            <Button
                              variant="outlined"
                              disabled={isConnectingAffable}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConnectAffableBPM();
                              }}
                              sx={{
                                display: "flex",
                                padding: "8px 16px",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "8px",
                                borderRadius: "8px",
                                minHeight: "36px",
                                height: "36px",
                                border: "1px solid #5BBFD6",
                                background: "#E6FAFC",
                                boxShadow:
                                  "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                                textTransform: "none",
                                "&:hover": {
                                  backgroundColor: "#f5fdff",
                                  borderColor: "#065b70",
                                  color: "#065b70",
                                  boxShadow:
                                    "0px 4px 8px rgba(6, 91, 112, 0.2)",
                                },
                                "&:active": {
                                  backgroundColor: "#e5f9fd",
                                  boxShadow:
                                    "0px 1px 2px rgba(6, 91, 112, 0.2)",
                                },
                              }}
                            >
                              <AffableConnectedIcon />
                              <Typography
                                sx={{
                                  color: "#086E86",
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  lineHeight: "normal",
                                }}
                              >
                                {isConnectingAffable
                                  ? "Connecting..."
                                  : "Connect with AffableFLOW"}
                              </Typography>
                            </Button>
                          )} */}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{ padding: "16px", backgroundColor: "#ffffff" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            mb: 2,
                            color: "#737373",
                            fontSize: "14px",
                            lineHeight: 1.6,
                            wordWrap: "break-word",
                          }}
                        >
                          Upload your survey documents to review, add comments,
                          and track compliance feedback. You can annotate
                          specific sections and download the documents with your
                          comments for reference.
                        </Typography>

                        {isSyncingAffableRecords ? (
                          <Box
                            sx={{
                              textAlign: "center",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 1,
                              padding: "32px",
                            }}
                          >
                            <CircularProgress
                              size={24}
                              sx={{ color: "#065b70" }}
                            />
                          </Box>
                        ) : !shouldShowAffablePanel ? (
                          <>
                            <Box
                              component="label"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px dashed #5bbfd6",
                                borderRadius: "8px",
                                padding: "16px 8px",
                                width: "100%",
                                boxSizing: "border-box",
                                cursor:
                                  mockUploadLoading || !canAddMockSurvey()
                                    ? "not-allowed"
                                    : "pointer",
                                opacity: !canAddMockSurvey() ? 0.6 : 1,
                                backgroundColor: "transparent",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  borderColor: "#5bbfd6",
                                },
                              }}
                            >
                              <input
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx"
                                style={{ display: "none" }}
                                ref={(el) =>
                                  (fileInputRef.current =
                                    el as HTMLInputElement | null)
                                }
                                onChange={(e) =>
                                  handleMockFilesAdd(e.target.files)
                                }
                                disabled={
                                  mockUploadLoading || !canAddMockSurvey()
                                }
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 1,
                                  width: "100%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: "12px",
                                    width: "100%",
                                    justifyContent: "center",
                                  }}
                                >
                                  {/* {isAffableConnected && (
                                    <>
                                      <IconButton
                                        aria-label={
                                          isSyncingAffableRecords
                                            ? "Uploading file with AffableFLOW..."
                                            : "Upload file with AffableFLOW"
                                        }
                                        title={
                                          isSyncingAffableRecords
                                            ? "Uploading file with AffableFLOW..."
                                            : "Upload file with AffableFLOW"
                                        }
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          const facilityId =
                                            selectedFacility?.facility_id
                                              ? String(
                                                  selectedFacility.facility_id,
                                                )
                                              : selectedFacility?.facility_name ||
                                                "";
                                          if (facilityId) {
                                            try {
                                              setServerFilesLoading(true);
                                              const list =
                                                await mockSurveyService.listFiles(
                                                  facilityId,
                                                );
                                              setServerMockFiles(
                                                (list?.items || []).map(
                                                  (i: any) => ({
                                                    id: i.id,
                                                    filename: i.filename,
                                                    filecontent: i.fileblob,
                                                    created_at: i.created_at,
                                                    DocumentId:
                                                      i.DocumentId ?? 0,
                                                  }),
                                                ),
                                              );
                                              setServerFilesLoading(false);
                                            } catch (err) {
                                              console.error(
                                                "Failed to load files immediately:",
                                                err,
                                              );
                                              setServerFilesLoading(false);
                                            }
                                          }
                                          handleAffableRecordsSync(
                                            affableDocsPage - 1,
                                          );
                                        }}
                                        disabled={
                                          isSyncingAffableRecords ||
                                          loadingAffableFile
                                        }
                                        sx={{
                                          borderRadius: "8px",
                                          border: "1px solid #5bbfd6",
                                          background: "#e6fafc",
                                          padding: "8px 16px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: "8px",
                                          "&:hover": {
                                            backgroundColor: "#d1f5f8",
                                          },
                                          "&:active": {
                                            backgroundColor: "#c1eff5",
                                          },
                                        }}
                                      >
                                        {isSyncingAffableRecords ||
                                        loadingAffableFile ? (
                                          <CircularProgress
                                            size={20}
                                            sx={{ color: "#065B70" }}
                                          />
                                        ) : (
                                          <>
                                            <AffableConnectedIcon />
                                            <Typography
                                              sx={{
                                                fontFamily:
                                                  '"Rethink Sans", sans-serif',
                                                fontSize: "13px",
                                                fontWeight: 500,
                                                color: "#065B70",
                                                textTransform: "none",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              Upload file from AffableFLOW
                                            </Typography>
                                          </>
                                        )}
                                      </IconButton>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0px",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: "20px",
                                            height: "1px",
                                            backgroundColor: "#0E9ABA",
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: "12px",
                                            color: "#0E9ABA",
                                            fontFamily:
                                              "Rethink Sans, sans-serif",
                                            padding: "0 2px",
                                          }}
                                        >
                                          OR
                                        </Typography>
                                        <Box
                                          sx={{
                                            width: "20px",
                                            height: "1px",
                                            backgroundColor: "#0E9ABA",
                                          }}
                                        />
                                      </Box>
                                    </>
                                  )} */}
                                  <Button
                                    variant="contained"
                                    component="span"
                                    disabled={
                                      mockUploadLoading || !canAddMockSurvey()
                                    }
                                    sx={{
                                      backgroundColor: "#E6FAFC",
                                      color: "#065B70",
                                      border: "1px solid #5BBFD6",
                                      borderRadius: "8px",
                                      padding: "8px 16px",
                                      textTransform: "none",
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      fontFamily: "Rethink Sans, sans-serif",
                                      boxShadow: "none",
                                      lineHeight: "normal",
                                      minHeight: "unset",
                                      "&:hover": {
                                        backgroundColor: "#D1F5F8",
                                        boxShadow: "none",
                                      },
                                      "&:disabled": {
                                        backgroundColor: "#E6FAFC",
                                        opacity: 0.6,
                                      },
                                    }}
                                  >
                                    {mockUploadLoading ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <CircularProgress
                                          size={16}
                                          sx={{ color: "#0a829d" }}
                                        />
                                        Uploading...
                                      </Box>
                                    ) : (
                                      <>
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          style={{ marginRight: "6px" }}
                                        >
                                          <path
                                            d="M3.5 2C3.22386 2 3 2.22386 3 2.5C3 2.77614 3.22386 3 3.5 3H12.5C12.7761 3 13 2.77614 13 2.5C13 2.22386 12.7761 2 12.5 2H3.5ZM8.35355 4.14645C8.15829 3.95118 7.84171 3.95118 7.64645 4.14645L4.14645 7.64645C3.95118 7.84171 3.95118 8.15829 4.14645 8.35355C4.34171 8.54882 4.65829 8.54882 4.85355 8.35355L7.5 5.70711V13.5C7.5 13.7761 7.72386 14 8 14C8.27614 14 8.5 13.7761 8.5 13.5V5.70711L11.1464 8.35355C11.3417 8.54882 11.6583 8.54882 11.8536 8.35355C12.0488 8.15829 12.0488 7.84171 11.8536 7.64645L8.35355 4.14645Z"
                                            fill="#086E86"
                                          />
                                        </svg>
                                        Upload Documents
                                      </>
                                    )}
                                  </Button>
                                  <Typography
                                    sx={{
                                      color: "#737373",
                                      fontSize: "12px",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Supported file type: DOC, DOCX
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {mockSurveyFiles.filter((f: any) => !f.inlineOnly)
                              .length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                {mockSurveyFiles
                                  .filter((f: any) => !f.inlineOnly)
                                  .map((file) => {
                                    const fileExtension = (
                                      file.name.split(".").pop() || ""
                                    ).toLowerCase();
                                    const fileSize = file.size
                                      ? formatFileSize(file.size)
                                      : "";

                                    return (
                                      <Box
                                        key={file.id}
                                        data-file-id={file.id}
                                        sx={{
                                          border: "1px solid #e5f5f8",
                                          borderRadius: "8px",
                                          background: "#fff",
                                          padding: "16px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "16px",
                                        }}
                                      >
                                        {/* File Icon */}
                                        <Box
                                          sx={{
                                            width: "48px",
                                            height: "48px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#E6FAFC",
                                            borderRadius: "8px",
                                            flexShrink: 0,
                                          }}
                                        >
                                          <Description
                                            sx={{
                                              color: "#065B70",
                                              fontSize: "32px",
                                            }}
                                          />
                                        </Box>

                                        {/* File Info */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              color: "#262626",
                                              fontFamily:
                                                "Rethink Sans, sans-serif",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {file.name}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              color: "#737373",
                                              fontFamily:
                                                "Rethink Sans, sans-serif",
                                              mt: "4px",
                                            }}
                                          >
                                            {fileExtension}{" "}
                                            {fileSize ? `| ${fileSize}` : ""}
                                          </Typography>
                                          {/* Show Pending status for local files (not yet saved to backend) */}
                                          <Box
                                            sx={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                              fontSize: 11,
                                              fontWeight: 600,
                                              color: "#f59e0b",
                                              backgroundColor: "#fef3c7",
                                              borderRadius: "9999px",
                                              px: 1,
                                              py: "2px",
                                              lineHeight: 1,
                                              mt: "4px",
                                            }}
                                          >
                                            <Box
                                              component="span"
                                              sx={{
                                                display: "inline-block",
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                backgroundColor: "#f59e0b",
                                                animation:
                                                  "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                                                "@keyframes pulse": {
                                                  "0%, 100%": {
                                                    opacity: 1,
                                                  },
                                                  "50%": {
                                                    opacity: 0.5,
                                                  },
                                                },
                                              }}
                                            />
                                            <Typography
                                              component="span"
                                              sx={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                fontFamily:
                                                  "Rethink Sans, sans-serif",
                                              }}
                                            >
                                              Pending
                                            </Typography>
                                          </Box>
                                        </Box>

                                        {/* Action Buttons */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: "8px",
                                            flexWrap: "wrap",
                                            alignItems: "center",
                                          }}
                                        >
                                          {/* Save Button */}
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={
                                              savingFileId === file.id ? (
                                                <CircularProgress
                                                  size={16}
                                                  sx={{ color: "#065B70" }}
                                                />
                                              ) : (
                                                <Save />
                                              )
                                            }
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              setSavingFileId(file.id);
                                              try {
                                                // Convert data URL to File
                                                const response = await fetch(
                                                  file.url,
                                                );
                                                const blob =
                                                  await response.blob();
                                                const fileObj = new File(
                                                  [blob],
                                                  file.name,
                                                  { type: file.type },
                                                );

                                                // Get facility ID
                                                const facilityId =
                                                  selectedFacility?.facility_id
                                                    ? String(
                                                        selectedFacility.facility_id,
                                                      )
                                                    : selectedFacility?.facility_name ||
                                                      "test";

                                                // Upload to backend
                                                const result =
                                                  await mockSurveyService.uploadFile(
                                                    fileObj,
                                                    facilityId,
                                                  );

                                                if (
                                                  result.success ||
                                                  result.file_id
                                                ) {
                                                  toast.success(
                                                    "File saved successfully!",
                                                  );
                                                  // Remove file from local list
                                                  setMockSurveyFiles((prev) =>
                                                    prev.filter(
                                                      (f) => f.id !== file.id,
                                                    ),
                                                  );
                                                  // Refresh server files list
                                                  try {
                                                    const list =
                                                      await mockSurveyService.listFiles(
                                                        facilityId,
                                                      );
                                                    setServerMockFiles(
                                                      (list?.items || []).map(
                                                        (i: any) => ({
                                                          id: i.id,
                                                          filename: i.filename,
                                                          filecontent:
                                                            i.fileblob,
                                                          created_at:
                                                            i.created_at,
                                                          DocumentId:
                                                            i.DocumentId ?? 0,
                                                        }),
                                                      ),
                                                    );
                                                  } catch (err) {
                                                    console.error(
                                                      "Failed to refresh file list",
                                                      err,
                                                    );
                                                  }
                                                } else {
                                                  toast.error(
                                                    result.message ||
                                                      "Failed to save file",
                                                  );
                                                }
                                              } catch (error: any) {
                                                console.error(
                                                  "Failed to save file:",
                                                  error,
                                                );
                                                toast.error(
                                                  error.message ||
                                                    "Failed to save file. Please try again.",
                                                );
                                              } finally {
                                                setSavingFileId(null);
                                              }
                                            }}
                                            disabled={savingFileId === file.id}
                                            sx={{
                                              textTransform: "none",
                                              borderColor: "#5BBFD6",
                                              color: "#065B70",
                                              minWidth: "110px",
                                              "&:hover": {
                                                backgroundColor: "#EAF8FB",
                                                borderColor: "#5BBFD6",
                                              },
                                              "&:disabled": {
                                                borderColor: "#5BBFD6",
                                                color: "#065B70",
                                                opacity: 0.7,
                                              },
                                            }}
                                          >
                                            Save
                                          </Button>

                                          {/* Re-upload Button - Only replaces locally, doesn't upload to backend */}
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CloudUpload />}
                                            disabled={!canAddMockSurvey()}
                                            onClick={async (e) => {
                                              e.stopPropagation();

                                              // Check if user has add permission for Mock Survey
                                              if (!canAddMockSurvey()) {
                                                toast.error(
                                                  "You do not have permission to upload documents",
                                                );
                                                return;
                                              }

                                              try {
                                                // Create a hidden file input for re-upload
                                                const input =
                                                  document.createElement(
                                                    "input",
                                                  );
                                                input.type = "file";
                                                input.accept =
                                                  ".pdf,.doc,.docx";
                                                input.style.display = "none";

                                                input.onchange = async (
                                                  event,
                                                ) => {
                                                  const target =
                                                    event.target as HTMLInputElement;
                                                  const files = target.files;
                                                  if (
                                                    !files ||
                                                    files.length === 0
                                                  ) {
                                                    document.body.removeChild(
                                                      input,
                                                    );
                                                    return;
                                                  }

                                                  const newFile = files[0];

                                                  try {
                                                    // Convert new file to data URL (same as handleMockFilesAdd does)
                                                    const toDataUrl = (
                                                      file: File,
                                                    ) =>
                                                      new Promise<string>(
                                                        (resolve, reject) => {
                                                          const reader =
                                                            new FileReader();
                                                          reader.onload = () =>
                                                            resolve(
                                                              String(
                                                                reader.result ||
                                                                  "",
                                                              ),
                                                            );
                                                          reader.onerror =
                                                            reject;
                                                          reader.readAsDataURL(
                                                            file,
                                                          );
                                                        },
                                                      );

                                                    const dataUrl =
                                                      await toDataUrl(newFile);

                                                    // Create new file object with same structure as existing local files
                                                    type MockSurveyFile = {
                                                      id: string;
                                                      name: string;
                                                      url: string;
                                                      type: string;
                                                      comment: string;
                                                      size: number;
                                                      justAdded?: boolean;
                                                    };

                                                    const newFileEntry: MockSurveyFile =
                                                      {
                                                        id: file.id, // Keep the same ID to replace
                                                        name: newFile.name,
                                                        url: dataUrl,
                                                        type:
                                                          newFile.type ||
                                                          "application/octet-stream",
                                                        comment:
                                                          file.comment || "", // Preserve existing comment
                                                        size: newFile.size,
                                                        justAdded: false, // Don't mark as just added since it's a replacement
                                                      };

                                                    // Replace the old file with the new one in local list
                                                    setMockSurveyFiles((prev) =>
                                                      prev.map((f) =>
                                                        f.id === file.id
                                                          ? newFileEntry
                                                          : f,
                                                      ),
                                                    );

                                                    toast.success(
                                                      "File replaced locally. Click Save to upload to backend.",
                                                    );
                                                  } catch (error: any) {
                                                    console.error(
                                                      "Failed to replace file:",
                                                      error,
                                                    );
                                                    toast.error(
                                                      error.message ||
                                                        "Failed to replace file. Please try again.",
                                                    );
                                                  }

                                                  // Clean up
                                                  document.body.removeChild(
                                                    input,
                                                  );
                                                };

                                                document.body.appendChild(
                                                  input,
                                                );
                                                input.click();
                                              } catch (error: any) {
                                                console.error(
                                                  "Failed to initiate re-upload:",
                                                  error,
                                                );
                                                toast.error(
                                                  "Failed to initiate re-upload. Please try again.",
                                                );
                                              }
                                            }}
                                            sx={{
                                              textTransform: "none",
                                              backgroundColor: "#E6FAFC",
                                              borderColor: "#5BBFD6",
                                              color: "#065B70",
                                              minWidth: "110px",
                                              "&:hover": {
                                                backgroundColor: "#D1F5F8",
                                                borderColor: "#5BBFD6",
                                              },
                                            }}
                                          >
                                            Re-upload
                                          </Button>

                                          {/* Survey Viewer Button */}
                                          {/* <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<Description />}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const response = await fetch(file.url);
                                      const blob = await response.blob();
                                      
                                      const reader = new FileReader();
                                      reader.onload = () => {
                                        const arrayBuffer = reader.result as ArrayBuffer;
                                        const uint8Array = new Uint8Array(arrayBuffer);
                                        const binaryString = Array.from(uint8Array, byte => String.fromCharCode(byte)).join('');
                                        const fileInfo = {
                                          fileName: file.name,
                                          fileType: file.type,
                                        };
                                        // Include fileId if available (for server files)
                                        if (file.id && typeof file.id === 'number') {
                                          (fileInfo as any).fileId = file.id;
                                        }
                                        sessionStorage.setItem('surveyViewerFile', JSON.stringify(fileInfo));
                                        sessionStorage.setItem('surveyViewerFileData', btoa(binaryString));
                                        navigate("/dashboard/survey-viewer", {
                                          state: file.id ? { fileId: file.id, fileName: file.name } : undefined,
                                        });
                                      };
                                      reader.readAsArrayBuffer(blob);
                                    } catch (error) {
                                      console.error('Failed to open survey viewer', error);
                                      navigate("/dashboard/survey-viewer", {
                                        state: {
                                          fileId: file.id,
                                          fileName: file.name,
                                        },
                                      });
                                    }
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    background: "linear-gradient(135deg, #0a829d 0%, #58c2d8 100%)",
                                    color: "#E6FAFC",
                                    boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.25)",
                                    "&:hover": {
                                      background: "linear-gradient(135deg, #096b82 0%, #4db0c5 100%)",
                                    },
                                  }}
                                >
                                  Survey Viewer
                                </Button> */}

                                          {/* Delete Button - Only show if delete permission is true */}
                                          {canDeleteMockSurvey() && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                requestMockSurveyFileDelete(
                                                  file as any,
                                                );
                                              }}
                                              disabled={
                                                deletingFileId ===
                                                (file.fileid || file.id)
                                              }
                                              sx={{
                                                border: "1px solid #737373",
                                                color: "#737373",
                                                "&:hover": {
                                                  backgroundColor: "#f5f5f5",
                                                },
                                                "&:disabled": {
                                                  color: "#737373",
                                                  opacity: 0.7,
                                                },
                                              }}
                                            >
                                              {deletingFileId ===
                                              (file.fileid || file.id) ? (
                                                <CircularProgress
                                                  size={16}
                                                  sx={{ color: "#737373" }}
                                                />
                                              ) : (
                                                <Delete />
                                              )}
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                              </Box>
                            )}
                          </>
                        ) : null}

                        {/* AffableFLOW Document Manager Panel */}
                        {shouldShowAffablePanel && !isSyncingAffableRecords && (
                          <Box sx={{ marginTop: "12px" }}>
                            {renderAffableSyncPanel()}
                            {errorAffableFile && (
                              <Alert
                                severity="error"
                                sx={{ fontSize: "12px", mt: 1 }}
                              >
                                {errorAffableFile}
                              </Alert>
                            )}
                          </Box>
                        )}

                        {/* Server-side uploaded files list - Always visible */}
                        {/* Horizontal separator above Uploaded files */}
                        <Box
                          sx={{
                            borderTop: "1px solid #e5e7eb",
                            marginTop: 2,
                            marginBottom: 1.5,
                          }}
                        />
                        <Box sx={{ mt: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "8px",
                              borderRadius: "4px 4px 0 0",
                              border: "1px solid #0A829D",
                              background: "#0A829D",
                              px: "12px",
                              py: "8px",
                              mb: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontFamily: "Rethink Sans, sans-serif",
                                fontWeight: 500,
                                color: "#FFF",
                                lineHeight: "1.5",
                              }}
                            >
                              Files Available for Mock Survey
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontFamily: "Rethink Sans, sans-serif",
                                fontWeight: 500,
                                color: "#E6FAFC",
                              }}
                            >
                              {serverMockFiles.length} files
                            </Typography>
                          </Box>
                          {serverFilesLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "120px",
                                mt: 1,
                              }}
                            >
                              <CircularProgress sx={{ color: "#5BBFD6" }} />
                            </Box>
                          ) : serverMockFiles.length > 0 ? (
                            <Box
                              sx={{
                                mt: 0,
                                border: "1px solid #99d4e1",
                                borderTop: "none",
                                borderRadius: "0 0 8px 8px",
                                backgroundColor: "#FFF",
                                p: "12px",
                                boxSizing: "border-box",
                                width: "100%",
                                overflowX: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                }}
                              >
                                {paginatedServerMockFiles.map(
                                  (serverFile: any, index: number) => (
                                    <FileDisplayContainer
                                      key={serverFile.id}
                                      sx={{
                                        width: "100%",
                                        border: "1px solid #CFEAF2",
                                        backgroundColor: "#F3FBFE",
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        gap: "10px",
                                        marginTop: 0,
                                      }}
                                    >
                                    <Box
                                      sx={{
                                        width: "24px",
                                        minWidth: "24px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        color: "#262626",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      {mockFilesStartIndex + index + 1}
                                    </Box>
                                    <FileInfoContainer>
                                      <FileIcon>
                                        <svg
                                          width="32"
                                          height="32"
                                          viewBox="0 0 32 32"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M6 5C6 3.89543 6.89543 3 8 3H16V10C16 11.6569 17.3431 13 19 13H26V27C26 28.1046 25.1046 29 24 29H8C6.89543 29 6 28.1046 6 27V5ZM25.927 12H19C17.8954 12 17 11.1046 17 10V3.07303C17.3293 3.16456 17.6327 3.33984 17.8787 3.58579L25.4142 11.1213C25.6602 11.3673 25.8354 11.6707 25.927 12ZM8 2C6.34315 2 5 3.34315 5 5V27C5 28.6569 6.34315 30 8 30H24C25.6569 30 27 28.6569 27 27V12.5355C27 11.7399 26.6839 10.9768 26.1213 10.4142L18.5858 2.87868C18.0232 2.31607 17.2601 2 16.4645 2H8Z"
                                            fill="#262626"
                                          />
                                        </svg>
                                      </FileIcon>
                                      <FileDetails>
                                        <FileName
                                          sx={{
                                            fontSize: "12px",
                                            fontWeight: 700,
                                            color: "#262626",
                                          }}
                                        >
                                          {serverFile.filename}
                                        </FileName>
                                        <FileSize>
                                          {(
                                            serverFile.filename
                                              .split(".")
                                              .pop() || ""
                                          ).toLowerCase()}
                                          {serverFile.created_at &&
                                            ` | ${formatDateTime(serverFile.created_at)}`}
                                        </FileSize>
                                      </FileDetails>
                                    </FileInfoContainer>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      {/* Download Button */}
                                      <Button
                                        variant="contained"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!canUpdateMockSurvey) {
                                            toast.error(
                                              "You do not have permission to download documents",
                                            );
                                            return;
                                          }
                                          setDownloadingFileId(serverFile.id);
                                          try {
                                            if (serverFile.filecontent) {
                                              const binaryString = atob(
                                                serverFile.filecontent,
                                              );
                                              const bytes = new Uint8Array(
                                                binaryString.length,
                                              );
                                              for (
                                                let i = 0;
                                                i < binaryString.length;
                                                i++
                                              ) {
                                                bytes[i] =
                                                  binaryString.charCodeAt(i);
                                              }
                                              const fileExtension = (
                                                serverFile.filename
                                                  .split(".")
                                                  .pop() || ""
                                              ).toLowerCase();
                                              let mimeType =
                                                "application/octet-stream";
                                              if (
                                                fileExtension === "docx" ||
                                                fileExtension === "doc"
                                              ) {
                                                mimeType =
                                                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                                              } else if (
                                                fileExtension === "pdf"
                                              ) {
                                                mimeType = "application/pdf";
                                              } else if (
                                                fileExtension === "xlsx" ||
                                                fileExtension === "xls"
                                              ) {
                                                mimeType =
                                                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
                                              }
                                              const blob = new Blob([bytes], {
                                                type: mimeType,
                                              });
                                              const url =
                                                URL.createObjectURL(blob);
                                              const link =
                                                document.createElement("a");
                                              link.href = url;
                                              link.download =
                                                formatDownloadFileName(
                                                  serverFile.filename,
                                                );
                                              document.body.appendChild(link);
                                              link.click();
                                              document.body.removeChild(link);
                                              URL.revokeObjectURL(url);
                                              void logMockSurveyAuditEvent(
                                                "DOWNLOAD",
                                                "/api/v1/mock-survey/mock-survey-file-get",
                                                "GET",
                                                {
                                                  action: "DOWNLOAD",
                                                  fileId: serverFile.id,
                                                  fileName:
                                                    serverFile.filename,
                                                },
                                              );
                                            } else {
                                              toast.error(
                                                "File content not available for download",
                                              );
                                              void logMockSurveyAuditEvent(
                                                "DOWNLOAD",
                                                "/api/v1/mock-survey/mock-survey-file-get",
                                                "GET",
                                                {
                                                  action: "DOWNLOAD",
                                                  fileId: serverFile.id,
                                                  fileName:
                                                    serverFile.filename,
                                                },
                                                400,
                                                "File content not available for download",
                                              );
                                            }
                                          } catch (error) {
                                            console.error(
                                              "Failed to download file:",
                                              error,
                                            );
                                            toast.error(
                                              "Failed to download file. Please try again.",
                                            );
                                            void logMockSurveyAuditEvent(
                                              "DOWNLOAD",
                                              "/api/v1/mock-survey/mock-survey-file-get",
                                              "GET",
                                              {
                                                action: "DOWNLOAD",
                                                fileId: serverFile.id,
                                                fileName: serverFile.filename,
                                              },
                                              500,
                                              "Failed to download file. Please try again.",
                                            );
                                          } finally {
                                            setDownloadingFileId(null);
                                          }
                                        }}
                                        disabled={
                                          !canUpdateMockSurvey ||
                                          downloadingFileId === serverFile.id
                                        }
                                        title={
                                          canUpdateMockSurvey
                                            ? "Download document"
                                            : "View-only access — download not permitted"
                                        }
                                        sx={{
                                          display: "flex",
                                          padding: "12px 24px",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          gap: "8px",
                                          borderRadius: "8px",
                                          textTransform: "none",
                                          minHeight: "36px",
                                          height: "36px",
                                          background:
                                            "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
                                          boxShadow:
                                            "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
                                          "&:hover": {
                                            backgroundColor: "#065b70",
                                            boxShadow:
                                              "0px 4px 8px rgba(6, 91, 112, 0.3)",
                                            border: "1px solid #065b70",
                                          },
                                          "&:active": {
                                            backgroundColor: "#065b70",
                                            boxShadow:
                                              "0px 1px 2px rgba(6, 91, 112, 0.2)",
                                          },
                                        }}
                                      >
                                        {downloadingFileId === serverFile.id ? (
                                          <CircularProgress
                                            size={16}
                                            sx={{ color: "#065B70" }}
                                          />
                                        ) : (
                                          "Download"
                                        )}
                                      </Button>
                                      {/* Survey Viewer Button */}
                                      <ViewReportButton
                                        onClick={async (
                                          e: React.MouseEvent<HTMLButtonElement>,
                                        ) => {
                                          e.stopPropagation();
                                          try {
                                            if (serverFile.filecontent) {
                                              const binaryString = atob(
                                                serverFile.filecontent,
                                              );
                                              const bytes = new Uint8Array(
                                                binaryString.length,
                                              );
                                              for (
                                                let i = 0;
                                                i < binaryString.length;
                                                i++
                                              ) {
                                                bytes[i] =
                                                  binaryString.charCodeAt(i);
                                              }
                                              const blob = new Blob([bytes], {
                                                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                              });
                                              saveAffablePanelState();
                                              const fileInfo = JSON.stringify({
                                                fileName: serverFile.filename,
                                                fileType:
                                                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                                fileId: serverFile.id,
                                              });
                                              sessionStorage.setItem(
                                                "surveyViewerFile",
                                                fileInfo,
                                              );
                                              sessionStorage.setItem(
                                                "surveyViewerFileData",
                                                serverFile.filecontent,
                                              );
                                              localStorage.setItem(
                                                "surveyViewerFile",
                                                fileInfo,
                                              );
                                              localStorage.setItem(
                                                "surveyViewerFileData",
                                                serverFile.filecontent,
                                              );
                                              navigate(
                                                "/dashboard/survey-viewer",
                                                {
                                                  state: {
                                                    fileBlob: blob,
                                                    fileName:
                                                      serverFile.filename,
                                                    fileId: serverFile.id,
                                                    readOnly:
                                                      !canUpdateMockSurvey,
                                                  },
                                                },
                                              );
                                              void logMockSurveyAuditEvent(
                                                "VIEW",
                                                "/dashboard/survey-viewer",
                                                "GET",
                                                {
                                                  action: "VIEW",
                                                  resource: "SURVEY_VIEWER",
                                                  fileId: serverFile.id,
                                                  fileName:
                                                    serverFile.filename,
                                                },
                                              );
                                            } else {
                                              saveAffablePanelState();
                                              navigate(
                                                "/dashboard/survey-viewer",
                                                {
                                                  state: {
                                                    fileId: serverFile.id,
                                                    fileName:
                                                      serverFile.filename,
                                                    readOnly:
                                                      !canUpdateMockSurvey,
                                                  },
                                                },
                                              );
                                              void logMockSurveyAuditEvent(
                                                "VIEW",
                                                "/dashboard/survey-viewer",
                                                "GET",
                                                {
                                                  action: "VIEW",
                                                  resource: "SURVEY_VIEWER",
                                                  fileId: serverFile.id,
                                                  fileName:
                                                    serverFile.filename,
                                                },
                                              );
                                            }
                                          } catch (error) {
                                            console.error(
                                              "Failed to open survey viewer",
                                              error,
                                            );
                                            toast.error(
                                              "Failed to open survey viewer. Please try again.",
                                            );
                                            saveAffablePanelState();
                                            navigate(
                                              "/dashboard/survey-viewer",
                                              {
                                                state: {
                                                  fileId: serverFile.id,
                                                  fileName: serverFile.filename,
                                                  readOnly: !canUpdateMockSurvey,
                                                },
                                              },
                                            );
                                            void logMockSurveyAuditEvent(
                                              "VIEW",
                                              "/dashboard/survey-viewer",
                                              "GET",
                                              {
                                                action: "VIEW",
                                                resource: "SURVEY_VIEWER",
                                                fileId: serverFile.id,
                                                fileName: serverFile.filename,
                                              },
                                              500,
                                              "Failed to open survey viewer. Please try again.",
                                            );
                                          }
                                        }}
                                        sx={{
                                          minHeight: "36px",
                                          height: "36px",
                                          padding: "8px 16px",
                                        }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                        >
                                          <path
                                            d="M3.5 3C3.5 1.89543 4.39543 1 5.5 1H9.08579C9.48361 1 9.86514 1.15804 10.1464 1.43934L13.0607 4.35355C13.342 4.63486 13.5 5.01639 13.5 5.41421V13C13.5 14.1046 12.6046 15 11.5 15H5.5C4.39543 15 3.5 14.1046 3.5 13V3ZM5.5 2C4.94772 2 4.5 2.44772 4.5 3V13C4.5 13.5523 4.94772 14 5.5 14H11.5C12.0523 14 12.5 13.5523 12.5 13V6H10C9.17157 6 8.5 5.32843 8.5 4.5V2H5.5ZM10 5H12.2929L9.5 2.20711V4.5C9.5 4.77614 9.72386 5 10 5Z"
                                            fill="white"
                                          />
                                        </svg>
                                        Survey Viewer
                                      </ViewReportButton>
                                      {/* Upload to AffableFlow Button - Only show when AffableFlow UI is visible (after clicking "Upload file from AffableFLOW") */}
                                      {shouldShowAffablePanel &&
                                        isAffableConnected && (
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={
                                              uploadingToAffableFileId ===
                                                serverFile.id ||
                                              !selectedAffableDocumentId ||
                                              selectedAffableFolderId ===
                                                null ||
                                              selectedAffableFolderId ===
                                                undefined
                                            }
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleUploadToAffableFLOW(
                                                serverFile,
                                              );
                                            }}
                                            sx={{
                                              textTransform: "none",
                                              minHeight: "36px",
                                              height: "36px",
                                              padding: "8px 16px",
                                              borderRadius: "8px",
                                              border: "1px solid #5BBFD6",
                                              backgroundColor: "#E6FAFC",
                                              color: "#065B70",
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              fontFamily:
                                                "Rethink Sans, sans-serif",
                                              boxShadow:
                                                "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                                              display: "flex",
                                              alignItems: "center",
                                              "&:hover": {
                                                backgroundColor: "#D1F5F8",
                                                borderColor: "#5BBFD6",
                                              },
                                            }}
                                          >
                                            {uploadingToAffableFileId ===
                                            serverFile.id ? (
                                              <CircularProgress
                                                size={16}
                                                sx={{ color: "#065B70", mr: 1 }}
                                              />
                                            ) : (
                                              <Box
                                                sx={{
                                                  mr: 1,
                                                  display: "flex",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <AffableConnectedIcon />
                                              </Box>
                                            )}
                                            Upload to AffableFlow
                                          </Button>
                                        )}
                                      {/* Delete Button */}
                                      {canDeleteMockSurvey() && (
                                        <DeleteButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            requestMockSurveyFileDelete({
                                              id: String(serverFile.id),
                                              name: serverFile.filename,
                                              fileid: serverFile.id,
                                            });
                                          }}
                                          disabled={
                                            deletingFileId === serverFile.id
                                          }
                                          sx={{
                                            minWidth: "auto",
                                            minHeight: "30px",
                                            height: "30px",
                                            padding: "3px 3px",
                                            // padding: 0,
                                          }}
                                        >
                                          {deletingFileId === serverFile.id ? (
                                            <CircularProgress
                                              size={16}
                                              sx={{ color: "#737373" }}
                                            />
                                          ) : (
                                            <DeleteFileIcon />
                                          )}
                                        </DeleteButton>
                                      )}
                                    </Box>
                                    </FileDisplayContainer>
                                  ),
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "center",
                                  mt: 1,
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                >
                                  <Typography
                                    sx={{
                                      color: "#525252",
                                      fontFamily: "Rethink Sans, sans-serif",
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    Rows per page
                                  </Typography>
                                  <Select
                                    size="small"
                                    value={mockFilesRowsPerPage}
                                    onChange={(e) => {
                                      const value = Number(e.target.value);
                                      setMockFilesRowsPerPage(value);
                                      setMockFilesPage(1);
                                    }}
                                    sx={{
                                      fontSize: 12,
                                      height: 28,
                                      "& .MuiSelect-select": {
                                        padding: "4px 8px",
                                      },
                                    }}
                                  >
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                  </Select>
                                </Box>

                                <Pagination
                                  count={totalMockFilesPages}
                                  page={mockFilesPage}
                                  onChange={(_, value) => setMockFilesPage(value)}
                                  size="small"
                                  sx={{
                                    "& .MuiPaginationItem-root": {
                                      fontSize: 12,
                                      height: 28,
                                      minWidth: 28,
                                      padding: "0 6px",
                                    },
                                    "& .MuiPaginationItem-root.Mui-selected": {
                                      backgroundColor: "#0a829d",
                                      color: "#ffffff",
                                    },
                                    "& .MuiPaginationItem-root.Mui-selected:hover":
                                      {
                                        backgroundColor: "#0a829d",
                                      },
                                  }}
                                />
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                mt: 0,
                                border: "1px solid #99d4e1",
                                borderTop: "none",
                                borderRadius: "0 0 8px 8px",
                                backgroundColor: "#FFF",
                                p: "12px",
                                boxSizing: "border-box",
                                width: "100%",
                                overflowX: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  minHeight: "120px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#737373",
                                    fontFamily: "Rethink Sans, sans-serif",
                                    textAlign: "center",
                                  }}
                                >
                                  No files uploaded yet
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </TabContentContainer>
              )}
            </HeaderSection>
          </MainContent>
        </ContentWrapper>
      </ContentContainer>

      <Dialog
        open={watchGuideOpen}
        onClose={closeWatchGuideDialog}
        fullWidth
        maxWidth="md"
        transitionDuration={0}
        TransitionProps={{
          onExited: resetWatchGuideDialogState,
        }}
      >
        <DialogTitle
          sx={{
            color: "#065b70",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            px: 3,
          }}
        >
          {`User Guide - ${watchGuideTitle}`}
          <IconButton
            onClick={closeWatchGuideDialog}
            size="small"
            sx={{ p: 0, width: 40, height: 40 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
            >
              <path
                d="M19.9974 6.66797C27.3612 6.66797 33.3307 12.6375 33.3307 20.0013C33.3307 27.3651 27.3612 33.3346 19.9974 33.3346C12.6336 33.3346 6.66406 27.3651 6.66406 20.0013C6.66406 12.6375 12.6336 6.66797 19.9974 6.66797ZM16.3464 15.1908C16.0217 14.9658 15.5726 14.998 15.2833 15.2873L15.1869 15.4027C14.9619 15.7275 14.9941 16.1765 15.2833 16.4658L18.8189 20.0013L15.2833 23.5368L15.1869 23.6522C14.9619 23.977 14.9941 24.4261 15.2833 24.7153L15.3988 24.8118C15.7235 25.0368 16.1726 25.0046 16.4619 24.7153L19.9974 21.1798L23.5329 24.7153L23.6483 24.8118C23.9731 25.0368 24.4222 25.0046 24.7114 24.7153L24.8079 24.5999C25.0329 24.2752 25.0007 23.8261 24.7114 23.5368L21.1759 20.0013L24.7114 16.4658L24.8079 16.3504C25.0329 16.0256 25.0007 15.5765 24.7114 15.2873L24.596 15.1908C24.2712 14.9658 23.8222 14.998 23.5329 15.2873L19.9974 18.8228L16.4619 15.2873L16.3464 15.1908Z"
                fill="#065B70"
              />
            </svg>
          </IconButton>
        </DialogTitle>
        <Box sx={{ borderBottom: "1px solid #B8DDE4", mx: 3 }} />
        <DialogContent sx={{ pt: 0.5, px: 3, pb: 2.5 }}>
          {!watchGuideError && (
            <Typography
              sx={{
                color: "#065b70",
                fontSize: "16px",
                lineHeight: 1.5,
                mb: 1,
                mt: 1,
                fontFamily: '"Rethink Sans", sans-serif',
              }}
            >
              {watchGuideVideo?.description?.trim() ||""}
            </Typography>
          )}
          {watchGuideError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {watchGuideError}
            </Alert>
          ) : watchGuideVideoUrl ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                aspectRatio: "16 / 9",
                borderRadius: "12px",
                backgroundColor: "#000",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <ReactPlayer
                src={watchGuideVideoUrl}
                controls
                playing={
                  watchGuideUserStarted &&
                  watchGuideOpen &&
                  Boolean(watchGuideVideoUrl)
                }
                width="100%"
                height="100%"
                onError={() =>
                  setWatchGuideError(
                    "Unable to play this video from the provided URL",
                  )
                }
              />
              {!watchGuideUserStarted && (
                <Box
                  onClick={() => setWatchGuideUserStarted(true)}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      width: 84,
                      height: 84,
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M8 6.75C8 6.15326 8.64867 5.78496 9.15991 6.09335L17.6428 11.3433C18.1305 11.6407 18.1305 12.3593 17.6428 12.6567L9.15991 17.9067C8.64867 18.215 8 17.8467 8 17.25V6.75Z"
                        fill="#0B5D70"
                      />
                    </svg>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Typography sx={{ color: "#737373", py: 1 }}>
              {watchGuideLoading
                ? "Loading guide video..."
                : watchGuideVideo
                  ? "Preparing video..."
                  : "No guide video available"}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Provider Type selector when NPI is ambiguous (USA) – required before Penalty/Risk */}
      <Dialog
        open={providerTypeModalOpen}
        onClose={() => setProviderTypeModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ color: "#065b70", fontWeight: 600 }}>
          Select Provider Type
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#262626", fontSize: 14, mb: 2 }}>
            This facility&apos;s type could not be determined from NPI alone.
            Please select the correct Provider Type for accurate penalty and
            compliance analysis.
          </Typography>
          <FormControl fullWidth size="medium">
            <InputLabel id="provider-type-label">Provider Type</InputLabel>
            <Select
              labelId="provider-type-label"
              label="Provider Type"
              value={providerTypeSelectedSlug}
              onChange={(e: SelectChangeEvent<string>) =>
                setProviderTypeSelectedSlug(e.target.value)
              }
              sx={{ backgroundColor: "#fff" }}
              displayEmpty
            >
              <MenuItem value="">
                {providerTypeOptionsLoading
                  ? "Loading options..."
                  : "Select..."}
              </MenuItem>
              {providerTypeOptions.map((o) => (
                <MenuItem key={o.slug} value={o.slug}>
                  {o.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProviderTypeModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!providerTypeSelectedSlug}
            onClick={() => {
              const opt = providerTypeOptions.find(
                (o) => o.slug === providerTypeSelectedSlug,
              );
              if (!opt) return;
              const facility =
                selectedFacility || getSelectedFacilityFromSession<Facility>();
              if (facility) {
                const updated = {
                  ...(typeof facility === "object" ? facility : {}),
                  facility_type: opt.display_name,
                  facility_type_slug: opt.slug,
                  facility_type_confirmed: true,
                };
                sessionStorage.setItem(
                  "filtered_selectedFacility",
                  JSON.stringify(updated),
                );
                setSelectedFacility(updated as Facility);
              }
              setProviderTypeModalOpen(false);
              setActiveTab(providerTypeModalPendingTab);
            }}
            sx={{
              background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
              "&:hover": { backgroundColor: "#065b70" },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mockSurveyFileToDelete !== null}
        onClose={() => setMockSurveyFileToDelete(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ color: "#065b70", fontWeight: 600 }}>
          Delete mock survey file?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#262626", fontSize: 14 }}>
            Are you sure you want to delete{" "}
            <strong>{mockSurveyFileToDelete?.name}</strong>? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setMockSurveyFileToDelete(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (!mockSurveyFileToDelete) return;
              const file = mockSurveyFileToDelete;
              setMockSurveyFileToDelete(null);
              void removeMockFileBackend(file);
            }}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
